import { CARE_PLAN_ACTIVITY_SYSTEM, CARE_PLAN_CATEGORY_SYSTEM, getCarePlanCategoryDisplay, getCoCMSequentialActivities, getActivitiesForCarePlanType, getActivitySlaDurationHours, getSequentialActivitiesForType, getAllCarePlanActivityOptions, getActivityMaxOccurrences, getActivityCadenceWeeks, getActivityAssignmentMode, getCarePlanActivityDescription, SERVICE_TYPE_OPTIONS, SERVICE_TYPE_SNOMED_MAP, HEALTHCARE_SERVICE_CODESYSTEMS, SERVICE_REQUEST_CATEGORY, SERVICE_REQUEST_NEED_IDENTIFIER_SYSTEM, OPERATIONAL_PROGRAM_OPTIONS, OPERATIONAL_PROGRAM_SYSTEM, PROGRAM_SELECTION_OBSERVATION_CODE, PROGRAM_SELECTION_OBSERVATION_SYSTEM, SELECTED_NEEDS_COMPONENT_CODE, SELECTED_NEEDS_COMPONENT_SYSTEM, PROGRAM_SELECTION_IDENTIFIER_SYSTEM, INTAKE_CHARGE_CPT_90791, CODE_SYSTEM as CODE_SYSTEM$1, INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM, AVAILABILITY_VERIFICATION_METHOD_OPTIONS, AVAILABILITY_VERIFICATION_METHOD_TO_CHANNEL, AVAILABILITY_VERIFICATION_METHOD_SYSTEM, COMMUNICATION_MEDIUM_SYSTEM, COMMUNICATION_MEDIUM_OPTIONS, AVAILABILITY_FINDING_SYSTEM, AVAILABILITY_UPDATE_CATEGORY_SYSTEM, FINDING_TO_CHARACTERISTIC_MAP, SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS, AVAILABILITY_LAST_VERIFIED_EXTENSION_URL, getCoverageAreaLocationName, SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS, SERVICE_PROVISION_OPTIONS, COCM_CONSENT_EXTENSION_URL, PATIENT_SUMMARY_EXTENSION_URL, GP_ROLE_TYPE_EXTENSION, GP_ROLE_TYPES, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM, PATIENT_ENGAGEMENT_STATUS_SYSTEM, PATIENT_ENGAGEMENT_STATUS_OPTIONS, isTerminatingEngagementStatus, COCM_CONSENT_DATE_EXTENSION_URL, FLAG_LOCAL_CODE_SYSTEM, PRACTITIONER_SPECIALTY_OPTIONS, getObservationDisplayByLoincCode, getObservationCoding as getObservationCoding$1, SURVEY_OBSERVATION_CATEGORY, CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL, PAUSE_FLAG_REASON_EXTENSION_URL, buildCommunicationTypeCategory, getFlagCategoryByCode, getFlagCategoryDisplay, getFlagCodingByCode, getTaskTypeConfig, SERVICE_CATEGORY_OPTIONS, getOrganizationTypeSystem, getOrganizationTypeDisplay, ORGANIZATION_TIER_SYSTEM, PROFILE_URLS, INDIVIDUAL_PRONOUNS_EXTENSION_URL, PATIENT_TAG_MAPPINGS, CONDITION_CLINICAL_STATUS_SYSTEM, ICD10_CM_SYSTEM, PROBLEM_LIST_ITEM_CATEGORY, CONDITION_CATEGORY_SYSTEM, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS, LANGUAGES, VALUE_SET, getEncounterTypeDisplay, ENCOUNTER_SCRATCH_EXTENSION_URL, ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL, isValidGASScore, GAS_ASSESSMENT_CODE, GAS_CODE_SYSTEM, getGASScoreDescription, getGASScoreDisplay, getQuestionnaireItems, STANDARD_QUESTIONNAIRES, isValidQuestionnaireScoring, WSAS_WORK_NOT_APPLICABLE_VALUE, LIKERT_FREQUENCY_OPTIONS, AI_GENERATED_TAG_SYSTEM, AI_GENERATED_TAG_CODES, QUESTIONNAIRE_URLS as QUESTIONNAIRE_URLS$1, ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL, CLINICAL_IMPRESSION_CODE_MAPPINGS, RETIRED_CLINICAL_IMPRESSION_FIELD_KEYS, getQuestionnaireDefinition } from './constants.mjs';
import { isNewLocation, toPatientId, toPractitionerId, toOrganizationId, isNewProviderOrganization, COVERAGE_APPLICABILITY_SECONDARY, COVERAGE_APPLICABILITY_PRIMARY } from './types.mjs';
import { formatHumanName, deepEquals } from '@medplum/core';
import { buildScoreInterpretation } from './scoring.mjs';
import { getPrePauseCarePlanStatus } from './resolvers.mjs';
import { localCalendarDateTodayISO, toE164US as toE164US$1 } from './utils.mjs';
import { normalizePhoneNumber } from '@benchmark/phone';

/**
 * FHIR Helper Functions
 *
 * Pure functions for constructing common FHIR data structures.
 * These are shared utilities used by builders across apps.
 */ /**
 * Create a FHIR Reference with required display value
 *
 * @param resourceType - The FHIR resource type
 * @param id - The resource ID (can be a real ID or urn:uuid for bundles)
 * @param display - Human-readable display name (required)
 * @returns Reference with display
 */ const buildReference = (resourceType, id, display)=>{
    if (!display || display.trim().length === 0) {
        throw new Error(`${resourceType} reference requires display name. ID: ${id}`);
    }
    // Support both real IDs and urn:uuid references for transaction bundles
    const reference = id.startsWith("urn:uuid:") ? id : `${resourceType}/${id}`;
    return {
        reference,
        display: display.trim()
    };
};
/**
 * Create a FHIR CodeableConcept
 *
 * @param system - The code system URL
 * @param code - The code value
 * @param display - The display name for the code
 * @param text - Optional human-readable text (defaults to display)
 * @returns CodeableConcept
 */ const createCodeableConcept$1 = (system, code, display, text)=>({
        coding: [
            {
                system,
                code,
                display
            }
        ],
        text: text ?? display
    });

/**
 * Get title for care plan type
 */ const getCarePlanTitle = (type)=>{
    switch(type){
        case "intake":
            return "Intake Care Plan";
        case "triage":
            return "Triage Care Plan";
        case "cocm":
            return "CoCM Care Plan";
        default:
            return "Care Plan";
    }
};
/**
 * Get description for care plan type
 */ const getCarePlanDescription = (type)=>{
    switch(type){
        case "intake":
            return "Initial intake care plan for new member onboarding.";
        case "triage":
            return "Triage care plan for care navigation and resource connection.";
        case "cocm":
            return "Collaborative Care Model (CoCM) care plan for behavioral health integration.";
        default:
            return "Care plan for patient management.";
    }
};
/**
 * Get activity definitions for the legacy (non-Task-backed) path.
 * CoCM uses only the sequential pair; other types use all definitions.
 */ const getLegacyActivities = (type)=>type === "cocm" ? getCoCMSequentialActivities() : getActivitiesForCarePlanType(type);
/**
 * Build a CarePlan with standardized structure
 *
 * Creates a CarePlan resource with standard activities based on type.
 * Uses shared constants for category and activity codes to ensure consistency
 * between different entry points (UI, fax, API).
 *
 * @param input - CarePlan building options
 * @returns CarePlan resource ready for creation
 */ function buildCarePlan(input) {
    const { type, patientRef, patientDisplay, serviceRequestRef, serviceRequestDisplay, authorRef, authorDisplay, careTeamId, notes, startFirstActivity = false, initialStatus = "active", title, description, activityReferences, supportingInfo, replaces, periodStart, extension } = input;
    const now = new Date().toISOString();
    // Build the CarePlan
    const carePlan = {
        resourceType: "CarePlan",
        status: initialStatus,
        intent: "plan",
        title: title || getCarePlanTitle(type),
        description: description || getCarePlanDescription(type),
        category: [
            {
                coding: [
                    {
                        system: CARE_PLAN_CATEGORY_SYSTEM,
                        code: type,
                        display: getCarePlanCategoryDisplay(type)
                    }
                ]
            }
        ],
        subject: buildReference("Patient", patientRef, patientDisplay),
        created: now,
        period: {
            start: periodStart ?? now
        },
        // FHIR R4 constraint cpl-3: activity must have detail OR reference, not both.
        // When Task-backed (activityReferences provided), use reference only —
        // iterate over activityReferences directly so the CarePlan references
        // every Task in the bundle (including extras like triage activities on
        // a CoCM plan).
        // When legacy (no references), use detail derived from the activity
        // definition constants for the plan type.
        activity: activityReferences ? activityReferences.map((ref)=>({
                reference: {
                    reference: ref.reference,
                    display: ref.display
                }
            })) : getLegacyActivities(type).map((activity, index)=>({
                detail: {
                    kind: "Task",
                    code: createCodeableConcept$1(CARE_PLAN_ACTIVITY_SYSTEM, activity.code, activity.display, activity.description),
                    status: startFirstActivity && index === 0 ? "in-progress" : "not-started"
                }
            }))
    };
    // Add ServiceRequest reference (basedOn) if provided
    if (serviceRequestRef) {
        carePlan.basedOn = [
            buildReference("ServiceRequest", serviceRequestRef, serviceRequestDisplay || "Initial Referral")
        ];
    }
    // Add author if provided
    if (authorRef && authorDisplay) {
        carePlan.author = buildReference("PractitionerRole", authorRef, authorDisplay);
    }
    // Add CareTeam reference if provided
    if (careTeamId) {
        carePlan.careTeam = [
            {
                reference: `CareTeam/${careTeamId}`,
                display: "Benchmark Care Team"
            }
        ];
    }
    // Add notes if provided
    if (notes) {
        carePlan.note = [
            {
                text: notes,
                time: now
            }
        ];
    }
    // Add supporting info if provided
    if (supportingInfo && supportingInfo.length > 0) {
        carePlan.supportingInfo = supportingInfo.map((ref)=>({
                reference: ref.reference,
                display: ref.display
            }));
    }
    // Add replacement links if provided
    if (replaces && replaces.length > 0) {
        carePlan.replaces = replaces.map((ref)=>({
                reference: ref.reference,
                display: ref.display
            }));
    }
    // Add extension if provided
    if (extension && extension.length > 0) {
        carePlan.extension = [
            ...extension
        ];
    }
    return carePlan;
}
/**
 * Build an intake CarePlan (convenience function)
 *
 * @deprecated Use buildCarePlan({ type: "intake", ... }) instead
 */ function buildIntakeCarePlan(input) {
    return buildCarePlan({
        ...input,
        type: "intake"
    });
}

/**
 * Build a FHIR Task for a CarePlan activity
 *
 * Creates a Task resource with proper basedOn → CarePlan linkage,
 * v2 activity code, and optional owner/requester.
 *
 * @param input - Activity and context for the Task
 * @returns Task resource ready for creation
 */ const buildCarePlanActivityTask = (input)=>{
    const task = {
        resourceType: "Task",
        // COMMENTED OUT: ActivityDefinition instantiation deferred; constants cover all activity metadata.
        // instantiatesCanonical: buildActivityDefinitionUrl(input.activityCode),
        status: input.initialStatus,
        intent: "order",
        priority: "routine",
        code: {
            coding: [
                {
                    system: CARE_PLAN_ACTIVITY_SYSTEM,
                    code: input.activityCode,
                    display: input.activityDisplay
                }
            ],
            text: input.activityDisplay
        },
        description: input.activityDescription,
        basedOn: [
            buildReference("CarePlan", input.carePlanRef, input.carePlanDisplay)
        ],
        for: buildReference("Patient", input.patientRef, input.patientDisplay),
        authoredOn: new Date().toISOString()
    };
    // Use explicit target date when provided, otherwise fall back to SLA-driven defaults.
    if (input.targetDate) {
        task.restriction = {
            period: {
                end: input.targetDate
            }
        };
    } else if (input.initialStatus === "ready" || input.initialStatus === "in-progress") {
        const slaHours = getActivitySlaDurationHours(input.activityCode);
        if (slaHours) {
            const dueDate = new Date();
            dueDate.setTime(dueDate.getTime() + slaHours * 60 * 60 * 1000);
            const dueDateIso = dueDate.toISOString();
            task.restriction = {
                period: {
                    end: dueDateIso
                }
            };
        } else {
            const dueDate = new Date();
            const year = dueDate.getFullYear();
            const month = String(dueDate.getMonth() + 1).padStart(2, "0");
            const day = String(dueDate.getDate()).padStart(2, "0");
            task.restriction = {
                period: {
                    end: `${year}-${month}-${day}`
                }
            };
        }
    }
    if (input.ownerRef && input.ownerDisplay) {
        task.owner = buildReference("PractitionerRole", input.ownerRef, input.ownerDisplay);
    }
    if (input.requesterRef && input.requesterDisplay) {
        task.requester = buildReference("PractitionerRole", input.requesterRef, input.requesterDisplay);
    }
    return task;
};

const CARE_PLAN_UUID_PREFIX = "urn:uuid:";
const TASK_UUID_PREFIX = "urn:uuid:";
/**
 * Build a transaction Bundle with CarePlan + Tasks + optional ServiceRequests.
 *
 * Creates a Bundle suitable for Medplum executeBatch/transaction:
 * 1. CarePlan with activity.reference → Task urn:uuids
 * 2. Task resources with basedOn → CarePlan urn:uuid
 * 3. When provided: ServiceRequest resources with basedOn → CarePlan urn:uuid
 *
 * All resources are created atomically - entire bundle succeeds or fails together.
 *
 * @param carePlanInput - Input for CarePlan construction
 * @param taskOverrides - Optional overrides applied to each Task (e.g. owner, requester)
 * @param serviceRequests - Optional ServiceRequests to include
 * @returns FHIR transaction Bundle
 */ const buildCarePlanBundle = (carePlanInput, taskOverrides, serviceRequests, taskOwnerOverrides, options)=>{
    const carePlanUuid = crypto.randomUUID();
    const carePlanFullUrl = `${CARE_PLAN_UUID_PREFIX}${carePlanUuid}`;
    const type = carePlanInput.type;
    const baseActivities = getSequentialActivitiesForType(type);
    const allActivities = getAllCarePlanActivityOptions();
    const extraActivities = (options?.extraActivityCodes ?? []).map((code)=>allActivities.find((activity)=>activity.code === code)).filter((activity)=>activity !== undefined);
    const excludeSet = new Set(options?.excludeActivityCodes ?? []);
    let activities = [
        ...baseActivities,
        ...extraActivities
    ].filter((activity, index, list)=>{
        return list.findIndex((candidate)=>candidate.code === activity.code) === index;
    }).filter((activity)=>!excludeSet.has(activity.code));
    if (carePlanInput.includeApproval && (type === "triage" || extraActivities.length > 0)) {
        const triageActivities = getActivitiesForCarePlanType("triage");
        const approvalActivity = triageActivities.find((activity)=>activity.code === "approve-selected-services");
        if (approvalActivity && !activities.some((activity)=>activity.code === approvalActivity.code)) {
            const selectServicesIndex = activities.findIndex((activity)=>activity.code === "select-services");
            if (selectServicesIndex >= 0) {
                activities = [
                    ...activities.slice(0, selectServicesIndex + 1),
                    approvalActivity,
                    ...activities.slice(selectServicesIndex + 1)
                ];
            } else {
                activities = [
                    approvalActivity,
                    ...activities
                ];
            }
        }
    }
    const taskRefs = [];
    const tasks = [];
    const carePlanDisplay = carePlanInput.title || `${type.charAt(0).toUpperCase() + type.slice(1)} Care Plan`;
    let isFirstTask = true;
    for(let i = 0; i < activities.length; i++){
        const activity = activities[i];
        const occurrences = getActivityMaxOccurrences(activity.code);
        const cadenceWeeks = getActivityCadenceWeeks(activity.code);
        const isCadenced = cadenceWeeks !== undefined && occurrences > 1;
        for(let occ = 0; occ < occurrences; occ++){
            const taskUuid = crypto.randomUUID();
            const taskFullUrl = `${TASK_UUID_PREFIX}${taskUuid}`;
            // First task of the entire plan starts as `ready` (actionable);
            // all others start as `draft` (blocked until prior completes).
            const initialStatus = carePlanInput.startFirstActivity && isFirstTask ? "ready" : "draft";
            isFirstTask = false;
            // Cadenced tasks get distinct display names (e.g. "Complete 6 Week Followup")
            const occWeeks = isCadenced ? cadenceWeeks * (occ + 1) : undefined;
            const displayName = isCadenced ? `Complete ${occWeeks} Week Followup` : activity.display;
            const ownerOverride = taskOwnerOverrides?.[activity.code];
            const assignmentMode = getActivityAssignmentMode(activity.code);
            const isUnassigned = assignmentMode === "unassigned-queue";
            const task = buildCarePlanActivityTask({
                activityCode: activity.code,
                activityDisplay: displayName,
                activityDescription: getCarePlanActivityDescription(activity.code),
                carePlanRef: carePlanFullUrl,
                carePlanDisplay,
                patientRef: carePlanInput.patientRef,
                patientDisplay: carePlanInput.patientDisplay,
                ownerRef: ownerOverride?.ref ?? (isUnassigned ? undefined : carePlanInput.authorRef),
                ownerDisplay: ownerOverride?.display ?? (isUnassigned ? undefined : carePlanInput.authorDisplay),
                requesterRef: carePlanInput.authorRef,
                requesterDisplay: carePlanInput.authorDisplay,
                initialStatus,
                ...taskOverrides
            });
            // Cadenced tasks: set due date at creation time based on calendar offset.
            // Unlike regular tasks whose due date is set when they become `ready`,
            // cadenced tasks have a fixed schedule relative to plan creation
            // (e.g. 6w, 12w, 18w). Set regardless of initialStatus so the worklist
            // can show when the task *will* be due.
            if (isCadenced && occWeeks) {
                const dueDate = new Date();
                dueDate.setTime(dueDate.getTime() + occWeeks * 7 * 24 * 60 * 60 * 1000);
                const dueDateIso = dueDate.toISOString();
                task.restriction = {
                    period: {
                        end: dueDateIso
                    }
                };
            }
            taskRefs.push({
                reference: taskFullUrl,
                display: displayName
            });
            tasks.push({
                fullUrl: taskFullUrl,
                resource: task
            });
        }
    }
    const carePlan = buildCarePlan({
        ...carePlanInput,
        activityReferences: taskRefs
    });
    const carePlanEntry = {
        fullUrl: carePlanFullUrl,
        resource: carePlan,
        request: {
            method: "POST",
            url: "CarePlan"
        }
    };
    const taskEntries = tasks.map((t)=>({
            fullUrl: t.fullUrl,
            resource: t.resource,
            request: {
                method: "POST",
                url: "Task"
            }
        }));
    // Build ServiceRequest entries when referral needs are provided
    const serviceRequestEntries = [];
    if (serviceRequests && serviceRequests.length > 0) {
        for (const sr of serviceRequests){
            const srOption = SERVICE_TYPE_OPTIONS.find((o)=>o.value === sr.code);
            const srDisplay = srOption?.label || sr.display || sr.code;
            const snomedMapping = SERVICE_TYPE_SNOMED_MAP[sr.code];
            const modalityCodes = sr.modalities && sr.modalities.length > 0 ? sr.modalities : sr.modality ? [
                sr.modality
            ] : [];
            const modalityDisplays = sr.modalityDisplays ?? [];
            const serviceRequest = {
                resourceType: "ServiceRequest",
                identifier: [
                    {
                        system: SERVICE_REQUEST_NEED_IDENTIFIER_SYSTEM,
                        value: `${carePlanInput.patientRef.replace(/^Patient\//, "")}|${carePlanUuid}|${sr.code}`
                    }
                ],
                status: "active",
                intent: "order",
                category: [
                    {
                        coding: [
                            SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL
                        ],
                        text: SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL.display
                    }
                ],
                code: {
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE,
                            code: sr.code,
                            display: srDisplay
                        },
                        ...snomedMapping ? [
                            snomedMapping
                        ] : []
                    ],
                    text: srDisplay
                },
                subject: {
                    reference: carePlanInput.patientRef.startsWith("urn:uuid:") ? carePlanInput.patientRef : `Patient/${carePlanInput.patientRef}`,
                    display: carePlanInput.patientDisplay
                },
                basedOn: [
                    {
                        reference: carePlanFullUrl,
                        display: carePlanDisplay
                    }
                ],
                authoredOn: new Date().toISOString(),
                ...modalityCodes.length > 0 && {
                    locationCode: [
                        {
                            coding: modalityCodes.map((modeCode, index)=>({
                                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                                    code: modeCode,
                                    display: modalityDisplays[index] || (modalityCodes.length === 1 ? sr.modalityDisplay || modeCode : modeCode)
                                }))
                        }
                    ]
                },
                ...sr.conditions && sr.conditions.length > 0 && {
                    reasonCode: sr.conditions.map((c)=>({
                            coding: [
                                {
                                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_CONDITION,
                                    code: c.code,
                                    display: c.display
                                }
                            ]
                        }))
                },
                ...sr.preferences && {
                    note: [
                        {
                            text: sr.preferences
                        }
                    ]
                }
            };
            if (carePlanInput.authorRef && carePlanInput.authorDisplay) {
                serviceRequest.requester = {
                    reference: carePlanInput.authorRef.startsWith("urn:uuid:") ? carePlanInput.authorRef : `PractitionerRole/${carePlanInput.authorRef}`,
                    display: carePlanInput.authorDisplay
                };
            }
            serviceRequestEntries.push({
                resource: serviceRequest,
                request: {
                    method: "POST",
                    url: "ServiceRequest"
                }
            });
        }
    }
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: [
            carePlanEntry,
            ...taskEntries,
            ...serviceRequestEntries
        ]
    };
};

/**
 * Clinical Notification Task constants and predicates.
 *
 * Clinical notifications are a distinct domain backed by FHIR `Task`, kept
 * strictly separate from care-plan and operational Tasks. These constants
 * define the discriminating `code.system` and the per-recipient read/unread
 * `businessStatus`, plus predicates used to slice notification Tasks from
 * operational Tasks in both directions.
 *
 * @agentic @benchmark/core
 */ /**
 * Canonical system URL for clinical notification Task types. A Task carrying a
 * `code.coding` in this system is a clinical notification, never an operational
 * or care-plan Task.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-task-type";
/**
 * The centralized clinical notification dictionary. Keyed by a stable SCREAMING
 * key; every entry is validated against {@link NotificationTypeDefinition}. This
 * is the one place to register, inspect, or reason about notification types.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TYPE_DEFINITIONS = {
    ACUTE_RISK_ESCALATION: {
        code: "acute-risk-escalation",
        display: "Acute Risk Escalation",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member survey response crossed an acute clinical-risk threshold and needs prompt clinician review.",
        category: "clinical-risk",
        defaultPriority: "urgent",
        prominenceTier: "urgent",
        triggerSummary: "PHQ-9 / PHQ-9A item 9 >= 1, C-SSRS >= 3, or a high-acuity questionnaire risk band.",
        userConfigurable: false,
        alwaysOnFloor: true
    },
    SERVICE_APPROVAL: {
        code: "service-approval",
        display: "Service approval",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member's selected services are ready for the advocate to approve or deny.",
        category: "service-approval",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "The member's approve-selected-services task transitioned to ready (actionable).",
        userConfigurable: false,
        alwaysOnFloor: false
    },
    APPOINTMENT_SCHEDULED: {
        code: "appointment-scheduled",
        display: "Appointment scheduled",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A booked appointment with a future start was newly recorded for the advocate's member.",
        category: "appointment",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "An Appointment was created with status booked and a start in the future.",
        userConfigurable: false,
        alwaysOnFloor: false
    }
};
/**
 * All registered notification type definitions.
 * @agentic @benchmark/core
 */ function listNotificationTypeDefinitions() {
    return Object.values(NOTIFICATION_TYPE_DEFINITIONS);
}
/**
 * Look up a notification type definition by its code. Returns undefined for an
 * unknown code.
 * @agentic @benchmark/core
 */ function getNotificationTypeDefinition(code) {
    return listNotificationTypeDefinitions().find((def)=>def.code === code);
}
/**
 * Canonical system URL for the per-recipient read/unread state carried on
 * `Task.businessStatus`. Kept distinct from `Task.status` so "read" is a
 * business fact, not a workflow-lifecycle transition (read !== work done).
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-read-status";
/**
 * Read/unread codes stored on `Task.businessStatus.coding`.
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS = {
    UNREAD: {
        code: "unread",
        display: "Unread"}};

/**
 * Build an unread clinical notification Task. Display and default priority come
 * from the centralized {@link NOTIFICATION_TYPE_DEFINITIONS} registry, so a new
 * type needs no builder change.
 * @agentic @benchmark/core
 */ const buildNotificationTask = (input)=>{
    const code = input.code ?? NOTIFICATION_TYPE_DEFINITIONS.ACUTE_RISK_ESCALATION.code;
    const definition = getNotificationTypeDefinition(code);
    const display = definition?.display ?? code;
    const task = {
        resourceType: "Task",
        status: "ready",
        intent: "order",
        priority: input.priority ?? definition?.defaultPriority ?? "routine",
        code: {
            coding: [
                {
                    system: NOTIFICATION_TASK_TYPE_SYSTEM,
                    code,
                    display
                }
            ],
            text: display
        },
        businessStatus: {
            coding: [
                {
                    system: NOTIFICATION_READ_STATUS_SYSTEM,
                    code: NOTIFICATION_READ_STATUS.UNREAD.code,
                    display: NOTIFICATION_READ_STATUS.UNREAD.display
                }
            ],
            text: NOTIFICATION_READ_STATUS.UNREAD.display
        },
        description: input.description,
        for: buildReference("Patient", input.patientId, input.patientDisplay),
        authoredOn: input.authoredOn ?? new Date().toISOString()
    };
    if (input.focus) {
        task.focus = input.focus;
    }
    if (input.reason) {
        task.reasonReference = input.reason;
    }
    if (input.owner) {
        task.owner = input.owner;
    }
    return task;
};

/**
 * Build a FHIR Observation for a program selection decision.
 * @agentic @benchmark/core
 */ function buildProgramSelectionObservation(input) {
    const { patientRef, patientDisplay, performerRef, performerDisplay, selectedProgram, reasonCodes, reasonText, selectedNeeds, intakeCarePlanId, existingId, derivedFromCarePlanRef, derivedFromCarePlanDisplay } = input;
    const patientId = patientRef.startsWith("Patient/") ? patientRef.replace("Patient/", "") : patientRef;
    const programOption = OPERATIONAL_PROGRAM_OPTIONS.find((o)=>o.code === selectedProgram);
    if (!programOption) {
        throw new Error(`Invalid program code: ${selectedProgram}`);
    }
    const observation = {
        resourceType: "Observation",
        status: "final",
        identifier: [
            {
                system: PROGRAM_SELECTION_IDENTIFIER_SYSTEM,
                value: `${patientId}|${intakeCarePlanId}`
            }
        ],
        code: {
            coding: [
                {
                    system: PROGRAM_SELECTION_OBSERVATION_CODE.system,
                    code: PROGRAM_SELECTION_OBSERVATION_CODE.code,
                    display: PROGRAM_SELECTION_OBSERVATION_CODE.display
                }
            ],
            text: "Program Selection"
        },
        subject: buildReference("Patient", patientRef, patientDisplay),
        performer: [
            buildReference("PractitionerRole", performerRef, performerDisplay)
        ],
        issued: new Date().toISOString(),
        effectiveDateTime: new Date().toISOString(),
        valueCodeableConcept: {
            coding: [
                {
                    system: OPERATIONAL_PROGRAM_SYSTEM,
                    code: programOption.code,
                    display: programOption.display
                }
            ],
            text: programOption.display
        }
    };
    if (existingId) {
        observation.id = existingId;
    }
    const notes = [];
    if (reasonText) {
        notes.push(reasonText);
    }
    if (notes.length > 0) {
        observation.note = notes.map((text)=>({
                text
            }));
    }
    const components = [];
    // Store reason codes as a component (FHIR R4 Observation has no reasonCode field)
    if (reasonCodes.length > 0) {
        components.push({
            code: {
                coding: [
                    {
                        system: PROGRAM_SELECTION_OBSERVATION_SYSTEM,
                        code: "selection-reason",
                        display: "Selection Reason"
                    }
                ]
            },
            valueString: reasonCodes.join(",")
        });
    }
    if (selectedNeeds && selectedNeeds.length > 0) {
        components.push({
            code: {
                coding: [
                    {
                        system: SELECTED_NEEDS_COMPONENT_SYSTEM,
                        code: SELECTED_NEEDS_COMPONENT_CODE.code,
                        display: SELECTED_NEEDS_COMPONENT_CODE.display
                    }
                ]
            },
            valueString: selectedNeeds.join(",")
        });
    }
    if (components.length > 0) {
        observation.component = components;
    }
    if (derivedFromCarePlanRef && derivedFromCarePlanDisplay) {
        observation.derivedFrom = [
            buildReference("CarePlan", derivedFromCarePlanRef, derivedFromCarePlanDisplay)
        ];
    }
    return observation;
}

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
const HL7_TERMINOLOGY_BASE = "http://terminology.hl7.org";
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;
// CodeSystem URLs
const CODE_SYSTEM = {
    V2_0131: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0131`,
    V3_ROLE_CODE: `${HL7_TERMINOLOGY_BASE}/CodeSystem/v3-RoleCode`,
    // External
    CPT: "http://www.ama-assn.org/go/cpt"};

/** HCPCS Level II system (G-codes such as G2214). @agentic @benchmark/core */ const HCPCS_SYSTEM = "https://bluebutton.cms.gov/resources/codesystem/hcpcs";
/**
 * The code system for a billed CoCM code. Numeric CPT codes (99492/99493/99494)
 * use the AMA CPT system; G-codes (G2214) are HCPCS Level II.
 * @agentic @benchmark/core
 */ const billingCodeSystem = (code)=>code.startsWith("G") ? HCPCS_SYSTEM : CODE_SYSTEM.CPT;
/** `Composition.type` value for a CoCM monthly billing packet. @agentic @benchmark/core */ const COCM_BILLING_PACKET_CODE_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/cocm-billing-packet`;
/** `Composition.type` value token. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE_CODE = "cocm-monthly-billing-packet";
/** `Composition.type` payload. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: COCM_BILLING_PACKET_TYPE_CODE,
            display: "CoCM Monthly Billing Packet"
        }
    ],
    text: "CoCM Monthly Billing Packet"
};
/** `Composition.category` payload (billing). @agentic @benchmark/core */ const COCM_BILLING_PACKET_CATEGORY = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: "billing",
            display: "Billing"
        }
    ]
};
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_PACKET_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-packet`;
/** Identifier system for the ChargeItem `{patientId}|{YYYY-MM}|{code}|{n}` key. @agentic @benchmark/core */ const COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-charge-item`;
/**
 * Extension URL carrying the packet + rules version on each ChargeItem, so a
 * line-item is self-describing (which rules produced it) for audit + dbt.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_VERSION_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-version`;
/**
 * Extension URL carrying the FULL serialized `BillingCodePacket` (JSON) on the
 * attested Composition, so every criterion that produced the codes — assessments
 * used, enrollment window, attribution, episode flags, versions, timestamp — is
 * reconstructible from the persisted resource (D19). Human-readable sections +
 * ChargeItems remain for querying; this is the complete audit snapshot.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-snapshot`;
/**
 * `Composition.status` values that model the packet lock (mirrors the summary
 * Observation semantics): open→closed→reopened.
 *  - `preliminary` = open (draft, not yet locked)
 *  - `final`       = closed / locked (submitted)
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_STATUS = {
    closed: "final"};
/** Build the composite identifier value for a patient-month packet. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/**
 * Build the identifier value for one billed-code ChargeItem occurrence
 * (`{patientId}|{YYYY-MM}|{code}|{n}`). Shared by the packet builder and the
 * submit/reopen reconciliation so both agree on line-item identity.
 * @agentic @benchmark/core
 */ const buildCoCMChargeItemIdentifierValue = (patientId, billingMonth, code, index)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|${code}|${index}`;

/**
 * Care units per billed code — the single TypeScript source of truth, aligned
 * with the dbt `billing_codes` seed (D24): 99492 / 99493 = 1.0 (base month),
 * 99494 = 0.5 (each 30-min add-on), G2214 = 0.25 (GBHI). Summing these over a
 * code package yields the packet's `careUnits`.
 *
 * NOTE (D24): the value for G2214 is 0.25, matching `billing_codes.csv` and both
 * `fct_*_cocm_billing` models. Two documentation strings historically said 0.5;
 * those are the stale ones, not this.
 * @agentic @benchmark/core
 */ const CARE_UNITS_BY_CODE = {
    "99492": 1.0,
    "99493": 1.0,
    "99494": 0.5,
    G2214: 0.25
};

const roleReference = (roleId, display)=>roleId ? {
        reference: `PractitionerRole/${roleId}`,
        ...display ? {
            display
        } : {}
    } : undefined;
/**
 * Build the atomic transaction entries for an attested billing packet.
 * @agentic @benchmark/core
 */ const buildCoCMBillingPacketBundle = (input)=>{
    const { packet, summaryObservation } = input;
    const newUuid = input.uuid ?? (()=>`urn:uuid:${crypto.randomUUID()}`);
    const attestedAt = input.attestedAt ?? new Date().toISOString();
    const patientRef = {
        reference: `Patient/${packet.patientId}`,
        ...input.patientDisplay ? {
            display: input.patientDisplay
        } : {}
    };
    const performer = roleReference(input.performerRoleId, input.performerDisplay);
    const [year, month] = packet.billingMonth.split("-").map(Number);
    const occurrencePeriod = {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
    const summaryFullUrl = newUuid();
    const entries = [
        {
            resource: summaryObservation,
            method: "PUT",
            url: input.summaryConditionalUrl,
            fullUrl: summaryFullUrl
        }
    ];
    // One ChargeItem per billed code occurrence (99494×2 → two line-items).
    const chargeItemRefs = [];
    packet.codes.forEach((code, index)=>{
        const identifierValue = buildCoCMChargeItemIdentifierValue(packet.patientId, packet.billingMonth, code, index);
        const chargeItem = {
            resourceType: "ChargeItem",
            identifier: [
                {
                    system: COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM,
                    value: identifierValue
                }
            ],
            status: "billable",
            code: {
                coding: [
                    {
                        system: billingCodeSystem(code),
                        code
                    }
                ],
                text: code
            },
            subject: patientRef,
            occurrencePeriod,
            quantity: {
                value: 1
            },
            ...performer ? {
                performer: [
                    {
                        actor: performer
                    }
                ]
            } : {},
            supportingInformation: [
                {
                    reference: summaryFullUrl
                }
            ],
            extension: [
                {
                    url: COCM_BILLING_PACKET_VERSION_EXTENSION_URL,
                    extension: [
                        {
                            url: "packetVersion",
                            valueInteger: packet.packetVersion
                        },
                        {
                            url: "rulesVersion",
                            valueString: packet.rulesVersion
                        },
                        {
                            url: "careUnits",
                            valueDecimal: CARE_UNITS_BY_CODE[code] ?? 0
                        }
                    ]
                }
            ]
        };
        const fullUrl = newUuid();
        chargeItemRefs.push({
            reference: fullUrl
        });
        entries.push({
            resource: chargeItem,
            method: "PUT",
            url: `ChargeItem?identifier=${COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM}|${identifierValue}`,
            fullUrl
        });
    });
    const sections = [
        {
            title: "Billed Codes",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${packet.packageDisplay || "No bill"} (${packet.careUnits} care units, rules ${packet.rulesVersion})</div>`
            },
            ...chargeItemRefs.length > 0 ? {
                entry: chargeItemRefs
            } : {}
        },
        {
            title: "Attested Minutes",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${packet.totalMinutes} attested minutes (${packet.billableMinutes} billable)</div>`
            },
            entry: [
                {
                    reference: summaryFullUrl
                }
            ]
        }
    ];
    if (summaryObservation.note?.[0]?.text) {
        sections.push({
            title: "Clinical Summary",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${summaryObservation.note[0].text}</div>`
            }
        });
    }
    const composition = {
        resourceType: "Composition",
        identifier: {
            system: COCM_BILLING_PACKET_IDENTIFIER_SYSTEM,
            value: buildCoCMBillingPacketIdentifierValue(packet.patientId, packet.billingMonth)
        },
        status: COCM_BILLING_PACKET_STATUS.closed,
        type: {
            ...COCM_BILLING_PACKET_TYPE
        },
        category: [
            {
                ...COCM_BILLING_PACKET_CATEGORY
            }
        ],
        subject: patientRef,
        date: attestedAt,
        title: `CoCM Billing Packet ${packet.billingMonth}`,
        // `author` is required (1..*); the submitting care manager is the author.
        // Empty only in the defensive no-role path (the modal always supplies one).
        author: performer ? [
            performer
        ] : [],
        ...performer ? {
            attester: [
                {
                    mode: "legal",
                    time: attestedAt,
                    party: performer
                }
            ]
        } : {},
        // Full packet snapshot (D19): the complete criteria — assessments used,
        // enrollment window, attribution, episode flags, versions, timestamp — so
        // the packet is reconstructible from this one resource for audit.
        extension: [
            {
                url: COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL,
                valueString: JSON.stringify(packet)
            }
        ],
        section: sections
    };
    entries.push({
        resource: composition,
        method: "PUT",
        url: `Composition?identifier=${COCM_BILLING_PACKET_IDENTIFIER_SYSTEM}|${buildCoCMBillingPacketIdentifierValue(packet.patientId, packet.billingMonth)}`,
        fullUrl: newUuid()
    });
    return entries;
};

/**
 * Build the Wellness-intake 90791 `ChargeItem`.
 * @agentic @benchmark/core
 */ function buildIntakeChargeItem(input) {
    const { encounterId, patientId, patientDisplay, performerRoleId, performerDisplay, occurrenceDateTime, existingId } = input;
    const chargeItem = {
        resourceType: "ChargeItem",
        status: "billable",
        identifier: [
            {
                system: INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM,
                value: encounterId
            }
        ],
        code: {
            coding: [
                {
                    system: CODE_SYSTEM$1.CPT,
                    code: INTAKE_CHARGE_CPT_90791
                }
            ],
            text: INTAKE_CHARGE_CPT_90791
        },
        subject: {
            reference: `Patient/${patientId}`,
            ...patientDisplay ? {
                display: patientDisplay
            } : {}
        },
        context: {
            reference: `Encounter/${encounterId}`
        },
        quantity: {
            value: 1
        },
        occurrenceDateTime: occurrenceDateTime ?? new Date().toISOString(),
        ...performerRoleId ? {
            performer: [
                {
                    actor: {
                        reference: `PractitionerRole/${performerRoleId}`,
                        ...performerDisplay ? {
                            display: performerDisplay
                        } : {}
                    }
                }
            ]
        } : {}
    };
    if (existingId) {
        chargeItem.id = existingId;
    }
    return chargeItem;
}

/**
 * Builds a FHIR transaction Bundle for logging an availability update.
 * @agentic @benchmark/core
 */ const buildAvailabilityUpdateBundle = ({ formData, healthcareService, sender, senderDisplay })=>{
    const entries = [];
    const communication = {
        resourceType: "Communication",
        status: "completed",
        category: [
            {
                coding: [
                    {
                        system: AVAILABILITY_UPDATE_CATEGORY_SYSTEM,
                        code: "availability-update",
                        display: "Availability Update"
                    }
                ]
            }
        ],
        recipient: [
            {
                reference: `HealthcareService/${formData.healthcareServiceId}`,
                display: formData.healthcareServiceDisplay
            }
        ],
        reasonCode: [
            {
                coding: [
                    {
                        system: AVAILABILITY_FINDING_SYSTEM,
                        code: formData.finding,
                        display: formData.findingDisplay
                    }
                ]
            }
        ],
        ...formData.verificationMethods.length > 0 && {
            medium: formData.verificationMethods.map((method)=>{
                const methodCoding = {
                    system: AVAILABILITY_VERIFICATION_METHOD_SYSTEM,
                    code: method,
                    display: AVAILABILITY_VERIFICATION_METHOD_OPTIONS.find((o)=>o.value === method)?.label ?? method
                };
                const channelCode = AVAILABILITY_VERIFICATION_METHOD_TO_CHANNEL[method] ?? null;
                // Lead with the canonical v3-ParticipationMode channel so `medium` is
                // queryable by channel; retain the verification-method as a second
                // coding for detail. Website/directory have no channel → method only.
                if (!channelCode) {
                    return {
                        coding: [
                            methodCoding
                        ]
                    };
                }
                return {
                    coding: [
                        {
                            system: COMMUNICATION_MEDIUM_SYSTEM,
                            code: channelCode,
                            display: COMMUNICATION_MEDIUM_OPTIONS.find((o)=>o.code === channelCode)?.display ?? channelCode
                        },
                        methodCoding
                    ]
                };
            })
        },
        ...formData.notes && {
            payload: [
                {
                    contentString: formData.notes
                }
            ]
        },
        sender: {
            reference: `PractitionerRole/${sender.id}`,
            display: senderDisplay
        },
        sent: new Date().toISOString()
    };
    entries.push({
        request: {
            method: "POST",
            url: "Communication"
        },
        resource: communication
    });
    const characteristicValue = FINDING_TO_CHARACTERISTIC_MAP[formData.finding];
    if (characteristicValue !== null) {
        const updatedHs = buildUpdatedHealthcareService(healthcareService, characteristicValue, communication.sent, senderDisplay);
        entries.push({
            request: {
                method: "PUT",
                url: `HealthcareService/${healthcareService.id}`
            },
            resource: updatedHs
        });
    }
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries
    };
};
const buildUpdatedHealthcareService = (existing, availabilityCode, verifiedDate, verifiedBy)=>{
    const displayLabel = SERVICE_CHARACTERISTIC_AVAILABILITY_OPTIONS.find((o)=>o.value === availabilityCode)?.label ?? availabilityCode;
    const newAvailabilityChar = {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                code: "availability",
                display: "Availability"
            },
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY,
                code: availabilityCode,
                display: displayLabel
            }
        ],
        text: displayLabel
    };
    const existingChars = existing.characteristic ?? [];
    const nonAvailabilityChars = existingChars.filter((c)=>!c.coding?.some((coding)=>coding.system === HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION && coding.code === "availability"));
    const otherExtensions = (existing.extension ?? []).filter((e)=>e.url !== AVAILABILITY_LAST_VERIFIED_EXTENSION_URL);
    return {
        ...existing,
        characteristic: [
            ...nonAvailabilityChars,
            newAvailabilityChar
        ],
        extension: [
            ...otherExtensions,
            {
                url: AVAILABILITY_LAST_VERIFIED_EXTENSION_URL,
                extension: [
                    {
                        url: "dateTime",
                        valueDateTime: verifiedDate
                    },
                    {
                        url: "verifiedBy",
                        valueString: verifiedBy
                    }
                ]
            }
        ]
    };
};

/**
 * Maps LocationFormData to Location FHIR resource. Pure transformation, no IO. Used for provider/clinic locations.
 * @agentic @benchmark/core
 */ /**
 * Converts form data to FHIR Location resource.
 * @agentic @benchmark/core
 */ function locationFromForm(formData, managingOrganizationId, managingOrganizationDisplay) {
    const location = {
        resourceType: "Location",
        status: "active",
        mode: "instance"
    };
    // Name
    if (formData.name) {
        location.name = formData.name;
    }
    // Address
    if (formData.street || formData.city || formData.state || formData.zip) {
        const lines = [];
        if (formData.street) {
            lines.push(formData.street);
        }
        if (formData.line2) {
            lines.push(formData.line2);
        }
        location.address = {
            use: "work",
            type: "physical",
            line: lines.length > 0 ? lines : undefined,
            city: formData.city,
            state: formData.state,
            postalCode: formData.zip
        };
    }
    // Position (geographic coordinates)
    if (typeof formData.latitude === "number" && typeof formData.longitude === "number") {
        location.position = {
            latitude: formData.latitude,
            longitude: formData.longitude
        };
    }
    // Phone
    if (formData.phone) {
        location.telecom = [
            {
                system: "phone",
                value: formData.phone,
                use: "work"
            }
        ];
    }
    // Managing Organization (from HealthcareService.providedBy)
    if (managingOrganizationId) {
        location.managingOrganization = {
            reference: `Organization/${managingOrganizationId}`,
            display: managingOrganizationDisplay || `Organization/${managingOrganizationId}`
        };
    }
    return location;
}
/**
 * Generates display name for Location from form data.
 * @agentic @benchmark/core
 */ function getLocationDisplayFromForm(formData) {
    if (formData.existingDisplay) {
        return formData.existingDisplay;
    }
    const parts = [];
    if (formData.name) parts.push(formData.name);
    if (formData.city && formData.state) {
        parts.push(`${formData.city}, ${formData.state}`);
    } else if (formData.city) {
        parts.push(formData.city);
    } else if (formData.state) {
        parts.push(formData.state);
    }
    return parts.join(" - ") || "New Location";
}

/**
 * Field-level FHIR CodeableConcept, Identifier, and Period builders. Pure, no IO.
 * @agentic @benchmark/core
 */ /**
 * Creates FHIR CodeableConcept element.
 * @param system - Code system URL
 * @param code - Code value
 * @param display - Display name for the code
 * @param text - Optional text (defaults to display)
 * @agentic @benchmark/core
 */ const createCodeableConcept = (system, code, display, text)=>({
        coding: [
            {
                system,
                code,
                display
            }
        ],
        text: text ?? display
    });

/**
 * Dimension code to display name mapping for HealthcareService eligibility/specialty/program dimensions.
 * @agentic @benchmark/core
 */ const DIMENSION_DISPLAYS$1 = {
    // Specialty dimensions
    treatmentModality: "Treatment Modality",
    treatmentCondition: "Treatment Condition",
    // Eligibility dimensions
    age: "Age Group",
    gender: "Gender",
    veteranStatus: "Veteran Status",
    studentStatus: "Student Status",
    pregnancyStatus: "Pregnancy Status",
    legalStatus: "Legal Status",
    incomeBand: "Income Band",
    // Program dimensions
    payerNetwork: "Payer Network",
    stateProgram: "State Program",
    insurancePlan: "Insurance Plan",
    // Characteristic dimensions
    access: "Access",
    availability: "Availability",
    scheduling: "Scheduling",
    waitTime: "Wait Time",
    visitMode: "Visit Mode",
    partnership: "Partnership"
};
/**
 * Returns display name for a dimension code.
 * @agentic @benchmark/core
 */ function getDimensionDisplay$1(dimension) {
    return DIMENSION_DISPLAYS$1[dimension] || dimension;
}
/**
 * Returns display name for a code from an options array.
 * @agentic @benchmark/core
 */ function getDisplayForCode$1(options, code) {
    if (!code) {
        return "";
    }
    return options.find((opt)=>opt.value === code)?.label || code;
}
/**
 * Creates dimension + value coding pattern for eligibility.
 * @agentic @benchmark/core
 */ function createEligibilityDimensionValue$1(dimension, value, valueDisplay) {
    if (!dimension || !value) {
        return null;
    }
    const dimensionSystemMap = {
        age: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_AGE_GROUP,
        gender: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_GENDER,
        veteranStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_VETERAN_STATUS,
        studentStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_STUDENT_STATUS,
        pregnancyStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_PREGNANCY_STATUS,
        legalStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_LEGAL_STATUS,
        incomeBand: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_INCOME_BAND
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay$1(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/**
 * Creates dimension + value coding pattern for specialty.
 * @agentic @benchmark/core
 */ function createSpecialtyDimensionValue$1(dimension, value, valueDisplay) {
    if (!dimension || !value) {
        return null;
    }
    const dimensionSystemMap = {
        treatmentModality: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_MODALITY,
        treatmentCondition: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_CONDITION
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay$1(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/**
 * Creates dimension + value coding pattern for program.
 * @agentic @benchmark/core
 */ function createProgramDimensionValue$1(dimension, value, valueDisplay) {
    if (!dimension || !value) {
        return null;
    }
    const dimensionSystemMap = {
        payerNetwork: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_PAYER_NETWORK,
        stateProgram: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_STATE,
        insurancePlan: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_INSURANCE_PLAN
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_DIMENSION,
                code: dimension,
                display: getDimensionDisplay$1(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/**
 * Maps careModality value to characteristic visitMode coding.
 * @agentic @benchmark/core
 */ function mapCareModalityToVisitMode$1(careModality) {
    const option = SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((opt)=>opt.value === careModality);
    if (option) {
        return {
            code: careModality,
            display: option.label
        };
    }
    const legacyMapping = {
        "in-person-only": {
            code: "in-person",
            display: "In-Person"
        },
        "virtual-only": {
            code: "telehealth-video",
            display: "Telehealth (Video)"
        },
        hybrid: {
            code: "hybrid",
            display: "Hybrid"
        }
    };
    return legacyMapping[careModality] || null;
}
/**
 * Applies shared form fields to an existing HealthcareService.
 * @agentic @benchmark/core
 */ function applySharedFieldsToService(service, formData, locationReferences, coverageAreaReferences) {
    // Start with existing service
    const updated = {
        ...service
    };
    // Apply location references
    if (locationReferences.length > 0) {
        updated.location = locationReferences;
    } else {
        delete updated.location;
    }
    // Apply coverage-area references (canonical state-coverage Locations
    // pre-resolved by the action layer). Always set or clear so removing
    // every selection on a primary service propagates to siblings as a
    // delete.
    if (coverageAreaReferences.length > 0) {
        updated.coverageArea = coverageAreaReferences;
    } else {
        delete updated.coverageArea;
    }
    // Apply eligibility
    if (formData.eligibility && formData.eligibility.length > 0) {
        const eligibilityValues = formData.eligibility.map((e)=>{
            const code = createEligibilityDimensionValue$1(e.dimension, e.value, e.valueDisplay);
            return code ? {
                code
            } : null;
        }).filter((e)=>e !== null);
        if (eligibilityValues.length > 0) {
            updated.eligibility = eligibilityValues;
        }
    } else if (formData.eligibilityCriteria) {
        // Backward compatibility
        updated.eligibility = [
            {
                comment: formData.eligibilityCriteria
            }
        ];
    } else {
        delete updated.eligibility;
    }
    // Apply characteristics (including careModality)
    const characteristics = [];
    // Visit mode from careModality
    if (formData.careModality && formData.careModality.length > 0) {
        formData.careModality.forEach((modality)=>{
            const visitMode = mapCareModalityToVisitMode$1(modality);
            if (visitMode) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: "visitMode",
                            display: "Visit Mode"
                        },
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                            code: visitMode.code,
                            display: visitMode.display
                        }
                    ],
                    text: visitMode.display
                });
            }
        });
    }
    // Partnership from partnershipType
    if (formData.partnershipType) {
        const partnershipDisplay = getDisplayForCode$1(SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, formData.partnershipType);
        characteristics.push({
            coding: [
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                    code: "partnership",
                    display: "Partnership"
                },
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP,
                    code: formData.partnershipType,
                    display: partnershipDisplay
                }
            ],
            text: partnershipDisplay
        });
    }
    // Structured characteristic data
    if (formData.characteristic && formData.characteristic.length > 0) {
        formData.characteristic.forEach((char)=>{
            const dimensionSystemMap = {
                access: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_ACCESS,
                availability: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY,
                scheduling: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_SCHEDULING,
                waitTime: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_WAIT_TIME
            };
            const valueSystem = dimensionSystemMap[char.dimension];
            if (valueSystem) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: char.dimension,
                            display: getDimensionDisplay$1(char.dimension)
                        },
                        {
                            system: valueSystem,
                            code: char.value,
                            display: char.valueDisplay || char.value
                        }
                    ],
                    text: char.valueDisplay || char.value
                });
            }
        });
    }
    if (characteristics.length > 0) {
        updated.characteristic = characteristics;
    } else {
        delete updated.characteristic;
    }
    // Apply serviceProvisionCode
    if (formData.serviceProvisionCode && formData.serviceProvisionCode.length > 0) {
        updated.serviceProvisionCode = formData.serviceProvisionCode.map((code)=>createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROVISION, code, getDisplayForCode$1(SERVICE_PROVISION_OPTIONS, code)));
    } else {
        delete updated.serviceProvisionCode;
    }
    // Apply specialty
    const specialtyList = [];
    if (formData.specialty && formData.specialty.length > 0) {
        formData.specialty.forEach((spec)=>{
            const specialtyValue = createSpecialtyDimensionValue$1(spec.dimension, spec.value, spec.valueDisplay);
            if (specialtyValue) {
                specialtyList.push(specialtyValue);
            }
        });
    }
    if (specialtyList.length > 0) {
        updated.specialty = specialtyList;
    } else {
        delete updated.specialty;
    }
    // Apply telecom
    if (formData.telecom && formData.telecom.length > 0) {
        updated.telecom = formData.telecom.map((t)=>({
                system: t.system,
                value: t.value,
                use: t.use
            }));
    } else {
        delete updated.telecom;
    }
    // Apply extraDetails
    if (formData.extraDetails) {
        updated.extraDetails = formData.extraDetails;
    } else {
        delete updated.extraDetails;
    }
    // Apply comment
    if (formData.comment) {
        updated.comment = formData.comment;
    } else {
        delete updated.comment;
    }
    // Apply program (including insurances converted to program)
    const programValues = [];
    // Programs from program field
    if (formData.program && formData.program.length > 0) {
        formData.program.forEach((prog)=>{
            const programValue = createProgramDimensionValue$1(prog.dimension, prog.value, prog.valueDisplay);
            if (programValue) {
                programValues.push(programValue);
            }
        });
    }
    // Insurances converted to program (takes precedence)
    if (formData.insurances && formData.insurances.length > 0) {
        formData.insurances.forEach((insuranceId, index)=>{
            const programValue = createProgramDimensionValue$1("insurancePlan", insuranceId, formData["insurances-displays"]?.[index] || `Insurance/${insuranceId}`);
            if (programValue) {
                programValues.push(programValue);
            }
        });
    }
    if (programValues.length > 0) {
        updated.program = programValues;
    } else {
        delete updated.program;
    }
    return updated;
}
/**
 * Builds a FHIR Bundle for batch updating multiple HealthcareServices.
 * @agentic @benchmark/core
 */ function buildBatchUpdateHealthcareServicesBundle(options) {
    const { formData, primaryService, siblingServices } = options;
    const entries = [];
    const newLocationTempUrls = new Map();
    // Track Location references to set on all services
    const locationReferences = [];
    // Get organization info for new locations
    const organizationId = formData.providerOrganization?.existingId || formData.providedBy;
    const organizationDisplay = formData.providerOrganization?.existingDisplay || formData["providedBy-display"];
    // Process locations - create new ones, reference existing ones
    const locations = formData.locations;
    if (locations && locations.length > 0) {
        locations.forEach((locationData, index)=>{
            if (isNewLocation(locationData)) {
                // Generate temp URL for new Location
                const tempUrl = `urn:uuid:${crypto.randomUUID()}`;
                newLocationTempUrls.set(index, tempUrl);
                // Build Location resource
                const location = locationFromForm(locationData, organizationId, organizationDisplay);
                entries.push({
                    fullUrl: tempUrl,
                    resource: location,
                    method: "POST",
                    url: "Location"
                });
                // Reference using temp URL
                locationReferences.push({
                    reference: tempUrl,
                    display: getLocationDisplayFromForm(locationData)
                });
            } else if (locationData.existingId) {
                // Reference existing Location
                locationReferences.push({
                    reference: `Location/${locationData.existingId}`,
                    display: locationData.existingDisplay || `Location/${locationData.existingId}`
                });
            }
        });
    } else if (formData.location && formData.location.length > 0) {
        // Legacy format
        formData.location.forEach((locId, index)=>{
            locationReferences.push({
                reference: `Location/${locId}`,
                display: formData["location-displays"]?.[index] || `Location/${locId}`
            });
        });
    }
    // Build coverage-area references from action-resolved canonical state
    // Locations on the form data.
    const coverageAreaReferences = (formData.coverageAreas ?? []).flatMap((area)=>{
        const locationId = area.locationId;
        if (!locationId) {
            return [];
        }
        return [
            {
                reference: `Location/${locationId}`,
                display: area.locationDisplay || getCoverageAreaLocationName(area.state)
            }
        ];
    });
    // Update primary service
    const updatedPrimary = applySharedFieldsToService(primaryService, formData, locationReferences, coverageAreaReferences);
    entries.push({
        resource: updatedPrimary,
        method: "PUT",
        url: `HealthcareService/${primaryService.id}`
    });
    // Update all sibling services with the same shared fields
    siblingServices.forEach((sibling)=>{
        const updatedSibling = applySharedFieldsToService(sibling, formData, locationReferences, coverageAreaReferences);
        entries.push({
            resource: updatedSibling,
            method: "PUT",
            url: `HealthcareService/${sibling.id}`
        });
    });
    return {
        bundle: {
            resourceType: "Bundle",
            type: "transaction",
            entry: entries.map((entry)=>({
                    ...entry.fullUrl && {
                        fullUrl: entry.fullUrl
                    },
                    request: {
                        method: entry.method || "POST",
                        url: entry.url || entry.resource.resourceType
                    },
                    resource: entry.resource
                }))
        },
        newLocationTempUrls,
        updatedServiceCount: 1 + siblingServices.length
    };
}

/**
 * Build Cancelled Task Entries. Creates transaction entries for cancelling tasks within FHIR Bundle transactions.
 * @agentic @benchmark/core
 */ /**
 * Builds transaction entries for cancelling tasks atomically within a FHIR Bundle transaction.
 * @agentic @benchmark/core
 */ function buildCancelledTaskEntries(tasks, reason) {
    return tasks.map((task)=>({
            resource: {
                ...task,
                status: "cancelled",
                lastModified: new Date().toISOString(),
                statusReason: {
                    text: reason
                }
            },
            method: "PUT",
            url: `Task/${task.id}`
        }));
}

/**
 * CarePlan Activity Status Mutation Helpers
 *
 * Pure functions that mutate activity status on existing CarePlan resources.
 * Used for legacy CarePlans (activity.detail only). Does not create new
 * activities or CarePlans. Task-backed CarePlans use Task.status as source
 * of truth; these helpers operate on detail.status for backwards compatibility.
 */ /**
 * Updates activity status in a CarePlan. Enforces only one active activity at a time.
 * @agentic @benchmark/core
 */ function updateCarePlanActivityStatus(carePlan, activityCode, newStatus, statusReason) {
    // Map unified statuses to legacy detail.status values
    const detailStatus = newStatus === "ready" ? "in-progress" : newStatus === "draft" ? "not-started" : newStatus === "on-hold" ? "on-hold" : newStatus;
    const updatedActivities = (carePlan.activity || []).map((activity)=>{
        const activityCodeValue = activity.detail?.code?.coding?.[0]?.code;
        if (activityCodeValue === activityCode) {
            return {
                ...activity,
                detail: {
                    ...activity.detail,
                    status: detailStatus,
                    ...statusReason && {
                        statusReason: {
                            text: statusReason
                        }
                    }
                }
            };
        }
        // When marking an activity "ready" (FHIR "in-progress"), demote other active activities
        if (newStatus === "ready" && activity.detail?.status === "in-progress") {
            return {
                ...activity,
                detail: {
                    ...activity.detail,
                    status: "not-started"
                }
            };
        }
        return activity;
    });
    return {
        ...carePlan,
        activity: updatedActivities
    };
}
/**
 * Updates all non-completed activities to cancelled for care plan cancellation.
 * @agentic @benchmark/core
 */ function updateActivitiesForCancellation(carePlan) {
    if (!carePlan.activity || carePlan.activity.length === 0) {
        return carePlan;
    }
    const updatedActivities = carePlan.activity.map((activity)=>{
        const currentStatus = activity.detail?.status;
        if (currentStatus && currentStatus !== "completed" && currentStatus !== "cancelled" && activity.detail) {
            return {
                ...activity,
                detail: {
                    ...activity.detail,
                    status: "cancelled"
                }
            };
        }
        return activity;
    });
    return {
        ...carePlan,
        activity: updatedActivities
    };
}

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * Care-time observation code system (Benchmark-minted).
 * Holds the single `care-management-minutes` code (D3).
 * @agentic @benchmark/core
 */ const CARE_TIME_CODE_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-type";
/**
 * Benchmark observation category system used as a sub-classification
 * alongside the HL7 `activity` category.
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/observation-category";
/**
 * Care-time method system encoding the source surface that produced
 * the entry (`task-completion`, `progress-update`, `call`, ...).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-method";
/**
 * UCUM system URL — used on `valueQuantity.system` for the `min` unit.
 * @agentic @benchmark/core
 */ const UCUM_SYSTEM = "http://unitsofmeasure.org";
/**
 * HL7 observation-category code used as the primary care-time category.
 * Pairs with the Benchmark `care-time` sub-category coding.
 * @agentic @benchmark/core
 */ const ACTIVITY_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "activity",
            display: "Activity"
        }
    ]
};
/**
 * Benchmark `care-time` sub-category coding paired with the HL7
 * `activity` category on every care-time Observation.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "care-time",
            display: "Care Time"
        }
    ]
};
/**
 * Single observation code for all care-time entries.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE_VALUE = "care-management-minutes";
/**
 * `Observation.code` payload for every care-time entry (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: CARE_TIME_OBSERVATION_CODE_VALUE,
            display: "Care management minutes"
        }
    ],
    text: "Care management minutes"
};
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Build the FHIR CodeableConcept payload for an `Observation.method` value.
 * @agentic @benchmark/core
 */ const getCareTimeMethodCoding = (method)=>{
    const option = CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === method);
    const display = option?.display ?? method;
    return {
        coding: [
            {
                system: CARE_TIME_METHOD_SYSTEM,
                code: method,
                display
            }
        ],
        text: display
    };
};
/**
 * UCUM `min` quantity coding used on every care-time Observation's
 * `valueQuantity`. Helper centralizes the unit shape.
 * @agentic @benchmark/core
 */ const buildCareTimeMinutesQuantity = (value)=>({
        value,
        unit: "min",
        system: UCUM_SYSTEM,
        code: "min"
    });
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");

/**
 * Strip a "ResourceType/" prefix from a stored reference and return the
 * raw id. Returns `undefined` for absent / unparseable refs.
 * @agentic @benchmark/core
 */ const extractIdFromReference = (reference, resourceType)=>{
    if (!reference) return undefined;
    const prefix = `${resourceType}/`;
    return reference.startsWith(prefix) ? reference.slice(prefix.length) : undefined;
};
/**
 * Build the FHIR Observation resource for a care-time entry. Pure mapping.
 *
 * If `existingObservation` is provided, the function preserves its `id`
 * and `meta` and overlays the new fields — used by the edit flow in the
 * activity-log drawer and by the intake-modal "update auto-created
 * Communication entry" path (D4 no-double-write).
 * @agentic @benchmark/core
 */ const buildCareTimeObservation = (form, existingObservation)=>{
    if (!Number.isFinite(form.minutes) || form.minutes <= 0) {
        throw new Error("Care-time minutes must be a positive number");
    }
    if (!form.patientId) {
        throw new Error("Care-time entry requires a patientId");
    }
    if (!form.performerRoleId) {
        throw new Error("Care-time entry requires a performerRoleId (performer)");
    }
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation",
        status: "final",
        code: {
            ...CARE_TIME_OBSERVATION_CODE
        }
    };
    // Preserve existing id for updates; status stays as-is unless we're
    // creating, in which case ensure status=final.
    if (!existingObservation) {
        observation.status = "final";
    }
    observation.category = [
        {
            ...ACTIVITY_OBSERVATION_CATEGORY
        },
        {
            ...CARE_TIME_OBSERVATION_CATEGORY
        }
    ];
    observation.code = {
        ...CARE_TIME_OBSERVATION_CODE
    };
    observation.method = getCareTimeMethodCoding(form.method);
    observation.subject = buildReference("Patient", form.patientId, form.patientDisplay);
    observation.performer = [
        buildReference("PractitionerRole", form.performerRoleId, form.performerDisplay)
    ];
    observation.valueQuantity = buildCareTimeMinutesQuantity(form.minutes);
    if (form.effectivePeriodStart && form.effectivePeriodEnd) {
        observation.effectivePeriod = {
            start: form.effectivePeriodStart,
            end: form.effectivePeriodEnd
        };
        delete observation.effectiveDateTime;
    } else {
        observation.effectiveDateTime = form.effectiveDate;
        delete observation.effectivePeriod;
    }
    observation.issued = new Date().toISOString();
    if (form.taskId) {
        observation.focus = [
            buildReference("Task", form.taskId, form.taskDisplay ?? "Task")
        ];
    } else if (!existingObservation) {
        delete observation.focus;
    }
    if (form.carePlanId) {
        observation.basedOn = [
            buildReference("CarePlan", form.carePlanId, form.carePlanDisplay ?? "Care Plan")
        ];
    } else if (!existingObservation) {
        delete observation.basedOn;
    }
    if (form.encounterId) {
        observation.encounter = buildReference("Encounter", form.encounterId, form.encounterDisplay ?? `Encounter ${form.encounterId}`);
    } else if (!existingObservation) {
        delete observation.encounter;
    }
    if (form.communicationId) {
        observation.derivedFrom = [
            buildReference("Communication", form.communicationId, `Communication ${form.communicationId}`)
        ];
    } else if (!existingObservation) {
        delete observation.derivedFrom;
    }
    if (form.notes && form.notes.trim().length > 0) {
        observation.note = [
            {
                text: form.notes.trim()
            }
        ];
    } else if (!existingObservation) {
        delete observation.note;
    } else {
        observation.note = undefined;
    }
    // Upsert the non-billable flag, tri-state on `form.nonBillable` and never
    // clobbering other extensions the Observation may already carry:
    //   true      → stamp the marker (excluded from CoCM billing)
    //   false     → remove the marker (explicitly billable)
    //   undefined → leave the entry's existing flag untouched, so update paths
    //               that don't manage this flag (e.g. encounter re-saves that
    //               rebuild the intake entries) can't silently strip it. An
    //               omitted flag must not be read as "make it billable".
    if (form.nonBillable !== undefined) {
        const otherExtensions = (observation.extension ?? []).filter((ext)=>ext.url !== CARE_TIME_NON_BILLABLE_EXTENSION_URL);
        if (form.nonBillable) {
            observation.extension = [
                ...otherExtensions,
                {
                    url: CARE_TIME_NON_BILLABLE_EXTENSION_URL,
                    valueBoolean: true
                }
            ];
        } else if (otherExtensions.length > 0) {
            observation.extension = otherExtensions;
        } else {
            delete observation.extension;
        }
    }
    return observation;
};
/**
 * Build a care-time Observation from a Communication.
 *
 * Returns `undefined` if any of these preconditions fail:
 *  - Communication is missing `sent` or `received` timestamps
 *  - The computed duration in minutes is < 1
 *  - There is no PractitionerRole sender to attribute the performer to
 *  - The Communication is missing a subject (patient)
 *
 * Skipping is preferred over writing a degraded entry — the brief
 * explicitly requires `performer` for every care-time observation (D8).
 * @agentic @benchmark/core
 */ const buildCareTimeFromCommunication = (input)=>{
    const { communication, method, patientDisplay, performerDisplay } = input;
    if (!communication.sent || !communication.received) return undefined;
    const sentMs = Date.parse(communication.sent);
    const receivedMs = Date.parse(communication.received);
    if (!Number.isFinite(sentMs) || !Number.isFinite(receivedMs)) return undefined;
    const durationMs = receivedMs - sentMs;
    if (durationMs <= 0) return undefined;
    // Round up to the nearest minute so a 30-second call still bills 1 min.
    const minutes = Math.max(1, Math.ceil(durationMs / 60_000));
    const senderRef = communication.sender;
    const performerId = extractIdFromReference(senderRef?.reference, "PractitionerRole");
    if (!performerId) return undefined;
    const subjectRef = communication.subject;
    const patientId = extractIdFromReference(subjectRef?.reference, "Patient");
    if (!patientId) return undefined;
    const communicationId = communication.id;
    if (!communicationId) return undefined;
    const performerDisplayResolved = performerDisplay ?? senderRef?.display ?? "Practitioner";
    return buildCareTimeObservation({
        minutes,
        method,
        effectiveDate: communication.sent,
        effectivePeriodStart: communication.sent,
        effectivePeriodEnd: communication.received,
        patientId,
        patientDisplay,
        performerRoleId: performerId,
        performerDisplay: performerDisplayResolved,
        communicationId
    });
};

/** Benchmark `billing-summary` category coding (distinct from `care-time`). @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "billing-summary",
            display: "Billing Summary"
        }
    ]
};
/** `Observation.code` value for a CoCM monthly billing summary. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_VALUE = "cocm-monthly-billing-summary";
/** `Observation.code` payload. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: COCM_BILLING_SUMMARY_CODE_VALUE,
            display: "CoCM Monthly Billing Summary"
        }
    ],
    text: "CoCM Monthly Billing Summary"
};
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-summary`;
/** Root complex-extension URL carrying submission + reopen audit. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-summary`;
/**
 * Relative sub-extension keys nested under
 * {@link COCM_BILLING_SUMMARY_EXTENSION_URL}.
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_EXT = {
    billingMonth: "billingMonth",
    submitted: "submitted",
    submittedAt: "submittedAt",
    submittedBy: "submittedBy",
    reopen: "reopen",
    reopenAt: "at",
    reopenReason: "reason",
    reopenBy: "by"
};
/**
 * `Observation.status` values that model the month lock:
 *  - `preliminary` = open (live, editable)
 *  - `final`       = closed / locked
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_STATUS = {
    open: "preliminary",
    closed: "final",
    reopened: "amended"
};
/** Build the composite identifier value for a patient-month. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;

const STATUS_MAP = {
    open: COCM_BILLING_SUMMARY_STATUS.open,
    closed: COCM_BILLING_SUMMARY_STATUS.closed,
    reopened: COCM_BILLING_SUMMARY_STATUS.reopened
};
/**
 * Attested minutes become a persisted billing fact (`valueQuantity.value`),
 * so a corrupted upstream sum must never be frozen into the record. Clamp
 * negatives to 0 and fail non-finite input (NaN / ±Infinity) CLOSED to 0 at
 * this single write chokepoint. Mirrors the resolver's `normalizeMinutes`
 * guard (#1318), which covered the on-read estimate path only — the
 * attestation/build write path was previously unguarded, so a negative or
 * NaN sum could be persisted as attested minutes.
 */ const normalizeAttestedMinutes = (minutes)=>Number.isFinite(minutes) ? Math.max(0, minutes) : 0;
const monthBounds = (billingMonth)=>{
    const [year, month] = billingMonth.split("-").map(Number);
    return {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
};
const roleRef = (roleId, display)=>roleId ? {
        reference: `PractitionerRole/${roleId}`,
        ...display ? {
            display
        } : {}
    } : undefined;
/**
 * Build (or rebuild) a CoCM monthly billing summary Observation.
 * @agentic @benchmark/core
 */ const buildCoCMBillingSummary = (input)=>{
    const { snapshot, submission, lockStatus = "open" } = input;
    const bounds = monthBounds(snapshot.billingMonth);
    const sub = [
        {
            url: COCM_BILLING_SUMMARY_EXT.billingMonth,
            valueString: snapshot.billingMonth
        },
        {
            url: COCM_BILLING_SUMMARY_EXT.submitted,
            valueBoolean: submission?.submitted ?? false
        }
    ];
    if (submission?.submitted) {
        if (submission.submittedAt) {
            sub.push({
                url: COCM_BILLING_SUMMARY_EXT.submittedAt,
                valueString: submission.submittedAt
            });
        }
        const by = roleRef(submission.submittedBy, submission.submittedByDisplay);
        if (by) {
            sub.push({
                url: COCM_BILLING_SUMMARY_EXT.submittedBy,
                valueReference: by
            });
        }
    }
    for (const entry of input.reopenHistory ?? []){
        const nested = [
            {
                url: COCM_BILLING_SUMMARY_EXT.reopenAt,
                valueString: entry.at
            }
        ];
        if (entry.reason) {
            nested.push({
                url: COCM_BILLING_SUMMARY_EXT.reopenReason,
                valueString: entry.reason
            });
        }
        const by = roleRef(entry.by, entry.byDisplay);
        if (by) nested.push({
            url: COCM_BILLING_SUMMARY_EXT.reopenBy,
            valueReference: by
        });
        sub.push({
            url: COCM_BILLING_SUMMARY_EXT.reopen,
            extension: nested
        });
    }
    const observation = {
        resourceType: "Observation",
        ...input.id ? {
            id: input.id
        } : {},
        status: STATUS_MAP[lockStatus],
        identifier: [
            {
                system: COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM,
                value: buildCoCMBillingSummaryIdentifierValue(snapshot.patientId, snapshot.billingMonth)
            }
        ],
        category: [
            {
                ...COCM_BILLING_SUMMARY_CATEGORY
            }
        ],
        code: {
            ...COCM_BILLING_SUMMARY_CODE
        },
        subject: {
            reference: `Patient/${snapshot.patientId}`,
            ...snapshot.patientDisplay ? {
                display: snapshot.patientDisplay
            } : {}
        },
        effectivePeriod: {
            start: bounds.start,
            end: bounds.end
        },
        valueQuantity: {
            value: normalizeAttestedMinutes(snapshot.attestedMinutes),
            unit: "min",
            system: UCUM_SYSTEM,
            code: "min"
        },
        issued: input.issued ?? new Date().toISOString(),
        extension: [
            {
                url: COCM_BILLING_SUMMARY_EXTENSION_URL,
                extension: sub
            }
        ]
    };
    const derivedFrom = (snapshot.contributingObservationIds ?? []).map((id)=>({
            reference: `Observation/${id}`
        }));
    if (derivedFrom.length > 0) {
        observation.derivedFrom = derivedFrom;
    }
    if (snapshot.clinicalSummary?.trim()) {
        observation.note = [
            {
                text: snapshot.clinicalSummary
            }
        ];
    }
    // `performer` is the care manager who recorded the month — known from the
    // first preliminary save, well before submission. Fall back to the submitter
    // so a submitted record built without an explicit performer stays attributed.
    const performerRef = roleRef(snapshot.performerRoleId, snapshot.performerDisplay) ?? (submission?.submitted ? roleRef(submission.submittedBy, submission.submittedByDisplay) : undefined);
    if (performerRef) {
        observation.performer = [
            performerRef
        ];
    }
    return observation;
};

/**
 * Generic reference creator that requires display value
 * Re-exported from @benchmark/core/builders for backward compatibility.
 * Supports both real IDs and urn:uuid references for transaction bundles.
 */ const buildReferenceWithDisplay = (resourceType, id, display)=>buildReference(resourceType, id, display);
/**
 * Build Patient reference with display (typed helper using branded PatientId)
 */ const buildPatientReference = (patientId, display)=>buildReference("Patient", patientId, display);
/**
 * Builds Practitioner reference with display.
 * @agentic @benchmark/core
 */ const buildPractitionerReference = (practitionerId, display)=>buildReference("Practitioner", practitionerId, display);
/**
 * Builds Organization reference with display.
 * @agentic @benchmark/core
 */ const buildOrganizationReference = (organizationId, display)=>buildReference("Organization", organizationId, display);
/**
 * Builds PractitionerRole reference with display.
 * @agentic @benchmark/core
 */ const buildPractitionerRoleReference = (practitionerRoleId, display)=>buildReference("PractitionerRole", practitionerRoleId, display);
/**
 * Builds Location reference with display.
 * @agentic @benchmark/core
 */ const buildLocationReference = (locationId, display)=>buildReference("Location", locationId, display);

/**
 * Check if address has meaningful data
 * Internal helper function
 */ const hasAddressData = (address)=>Boolean(address.text || address.line && address.line.length > 0 && address.line[0] || address.city || address.state || address.postalCode || address.country);
/**
 * Get display name for a patient.
 * When a preferred name (FHIR nickname) exists, formats as "Legal Name (Preferred Name)".
 */ const getPatientDisplayName = (patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (!officialName) {
        return `Patient ${patient.id}`;
    }
    const parts = [];
    if (officialName.given?.length) {
        parts.push(...officialName.given);
    }
    if (officialName.family) {
        parts.push(officialName.family);
    }
    if (parts.length === 0) {
        return `Patient ${patient.id}`;
    }
    const legalName = parts.join(" ");
    const nickname = patient.name?.find((n)=>n.use === "nickname" && n !== officialName);
    const preferredName = nickname?.given?.[0];
    if (preferredName) {
        return `${legalName} (${preferredName})`;
    }
    return legalName;
};
/**
 * Returns true when a gp-role-type code represents the Benchmark primary advocate.
 * Supports both current and legacy value codes.
 */ const isBenchmarkPrimaryAdvocateRoleCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Get Benchmark primary advocate reference from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getPatientBenchmarkLCSW = (patient)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateRoleCode(ext.valueCode))) ?? null;
};
/**
 * Get MRN (Medical Record Number) from patient identifiers
 * Returns null if no MRN identifier exists
 */ const getPatientMRN = (patient)=>{
    if (!patient.identifier || patient.identifier.length === 0) {
        return null;
    }
    const mrnIdentifier = patient.identifier.find((identifier)=>identifier.type?.coding?.some((coding)=>coding.code === "MR"));
    if (mrnIdentifier?.value) {
        return {
            system: mrnIdentifier.system || "",
            value: mrnIdentifier.value
        };
    }
    return null;
};
/**
 * Get active phone number from patient telecom array
 * Returns undefined if no active phone exists
 */ const getActivePatientPhone = (patient)=>patient.telecom?.find((telecom)=>telecom.system === "phone" && !telecom.period?.end)?.value;
/**
 * Get active address from patient address array
 * Filters out addresses with 'old' use or ended periods
 * Returns undefined if no active address exists or address has no data
 */ const getActivePatientAddress = (patient)=>{
    const address = patient.address?.find((candidate)=>candidate.use !== "old" && (!candidate.period || !candidate.period.end));
    return address && hasAddressData(address) ? address : undefined;
};
/**
 * Get active telecom entries from patient telecom array
 * Filters out telecom entries with ended periods
 */ const getActivePatientTelecom = (patient)=>patient.telecom?.filter((telecom)=>!telecom.period?.end) || [];
/**
 * Extracts CoCM consent (verified flag + date) from the Patient resource's
 * `cocm-consent` complex extension — populated via the patient edit form's
 * "CoCM Consent Verified" checkbox and "CoCM Consent Date" field
 * (`ReferralInformationSection`). This is distinct from the CoCM CarePlan's
 * `cocm-consent-date` extension (captured automatically at enrollment); see
 * `resolveCoCMConsent` in `cocm-eligibility.service` for how the two sources
 * are reconciled for billing/eligibility display.
 * @agentic @benchmark/core
 */ const getCoCMConsentFromPatient = (patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return undefined;
    const verified = Boolean(cocmExtension.extension.find((sub)=>sub.url === "verified")?.valueBoolean);
    const date = cocmExtension.extension.find((sub)=>sub.url === "date")?.valueDate;
    return {
        verified,
        date
    };
};
/**
 * Extracts patient summary from Patient resource extension.
 * @agentic @benchmark/core
 */ const getPatientSummary = (patient)=>{
    const summaryExtension = patient.extension?.find((ext)=>ext.url === PATIENT_SUMMARY_EXTENSION_URL);
    if (!summaryExtension?.extension) {
        return undefined;
    }
    const textExt = summaryExtension.extension.find((ext)=>ext.url === "text");
    const lastUpdatedExt = summaryExtension.extension.find((ext)=>ext.url === "lastUpdated");
    const authorExt = summaryExtension.extension.find((ext)=>ext.url === "author");
    const generatedByExt = summaryExtension.extension.find((ext)=>ext.url === "generatedBy");
    const modelUsedExt = summaryExtension.extension.find((ext)=>ext.url === "modelUsed");
    const promptVersionExt = summaryExtension.extension.find((ext)=>ext.url === "promptVersion");
    const text = textExt?.valueString;
    const lastUpdated = lastUpdatedExt?.valueDateTime;
    const authorReference = authorExt?.valueReference?.reference;
    const authorDisplay = authorExt?.valueReference?.display;
    const generatedBy = generatedByExt?.valueCode ?? "manual";
    const modelUsed = modelUsedExt?.valueString;
    const promptVersion = promptVersionExt?.valueString;
    if (!text || !lastUpdated || !authorReference || !authorDisplay) {
        return undefined;
    }
    return {
        text,
        lastUpdated,
        authorReference,
        authorDisplay,
        generatedBy,
        modelUsed,
        promptVersion
    };
};
/**
 * Builds patient summary extension for FHIR Patient resource.
 * @agentic @benchmark/core
 */ const buildPatientSummaryExtension = (options)=>{
    const { text, authorReference, generatedBy, modelUsed, promptVersion } = options;
    if (!authorReference.display) {
        throw new Error("Author reference must include display name");
    }
    const extensionItems = [
        {
            url: "text",
            valueString: text
        },
        {
            url: "lastUpdated",
            valueDateTime: new Date().toISOString()
        },
        {
            url: "author",
            valueReference: authorReference
        },
        {
            url: "generatedBy",
            valueCode: generatedBy
        }
    ];
    if (modelUsed) {
        extensionItems.push({
            url: "modelUsed",
            valueString: modelUsed
        });
    }
    if (promptVersion) {
        extensionItems.push({
            url: "promptVersion",
            valueString: promptVersion
        });
    }
    return {
        url: PATIENT_SUMMARY_EXTENSION_URL,
        extension: extensionItems
    };
};
/**
 * Updates or adds patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const updatePatientSummaryExtension = (existingExtensions, options)=>{
    const newSummaryExtension = buildPatientSummaryExtension(options);
    if (!existingExtensions) {
        return [
            newSummaryExtension
        ];
    }
    const otherExtensions = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return [
        ...otherExtensions,
        newSummaryExtension
    ];
};
/**
 * Removes patient summary extension; returns new extensions array (immutable).
 * @agentic @benchmark/core
 */ const removePatientSummaryExtension = (existingExtensions)=>{
    if (!existingExtensions) {
        return undefined;
    }
    const filtered = existingExtensions.filter((ext)=>ext.url !== PATIENT_SUMMARY_EXTENSION_URL);
    return filtered.length > 0 ? filtered : undefined;
};

/**
 * Transforms domain model to ClinicalImpression FHIR resource. Handles valueType switch for appropriate fields.
 * @agentic @benchmark/core
 */ function buildClinicalImpression(input) {
    const { patient, encounter, code, valueType, value, profileUrl, existingClinicalImpressionId, tempEncounterFullUrl, effectiveDate, status: inputStatus } = input;
    // Code is required - return undefined if missing (caller handles this)
    if (!code) {
        return undefined;
    }
    // Value is required for creating/updating - return undefined if missing (caller handles deletion)
    if (!value || !value.valueString && !value.valueCoding && !value.valueUri) {
        return undefined;
    }
    const clinicalImpression = {
        resourceType: "ClinicalImpression",
        status: inputStatus ?? "completed",
        subject: buildReferenceWithDisplay("Patient", patient.id, getPatientDisplayName(patient)),
        date: effectiveDate || encounter?.period?.start || new Date().toISOString(),
        code: code
    };
    // If we have an existing clinical impression ID, this is an update
    if (existingClinicalImpressionId) {
        clinicalImpression.id = existingClinicalImpressionId;
    }
    // Only add encounter reference if provided
    if (encounter) {
        // Use temporary full URL for new encounters in bundle transactions
        // Otherwise use the actual encounter ID
        if (tempEncounterFullUrl) {
            // New encounter: placeholder reference rewritten by Medplum on submit.
            // Omit the display — deriving it from the placeholder uuid leaves a
            // display whose id mismatches the resolved reference (Medplum rewrites
            // the reference but not the display).
            clinicalImpression.encounter = {
                reference: tempEncounterFullUrl
            };
        } else {
            clinicalImpression.encounter = buildReferenceWithDisplay("Encounter", encounter.id, `Encounter ${encounter.id}`);
        }
    }
    if (profileUrl) {
        clinicalImpression.meta = {
            profile: [
                profileUrl
            ]
        };
    }
    // Handle different types of clinical impression values (mechanical transformation)
    switch(valueType){
        case "finding":
            if (value.valueCoding) {
                clinicalImpression.finding = [
                    {
                        itemCodeableConcept: {
                            coding: [
                                value.valueCoding
                            ]
                        }
                    }
                ];
            } else if (value.valueString) {
                // If it's a text finding, store it in both finding and summary
                clinicalImpression.finding = [
                    {
                        itemCodeableConcept: {
                            text: value.valueString
                        }
                    }
                ];
                clinicalImpression.summary = value.valueString;
            }
            break;
        case "investigation":
            if (value.valueCoding) {
                clinicalImpression.investigation = [
                    {
                        code: {
                            coding: [
                                value.valueCoding
                            ]
                        }
                    }
                ];
            }
            break;
        case "protocol":
            if (value.valueUri) {
                clinicalImpression.protocol = [
                    value.valueUri
                ];
            }
            break;
        case "summary":
            if (value.valueString) {
                clinicalImpression.summary = value.valueString;
            }
            break;
        case "problem":
            // For problem type, we populate summary but don't create Condition here
            // Condition creation is business logic handled by action/builder
            if (value.valueString) {
                clinicalImpression.summary = value.valueString;
            }
            break;
    }
    return clinicalImpression;
}

/**
 * Builds a FHIR Bundle for atomic CoCM cancellation (CarePlan, Flag, Patient updates).
 * @agentic @benchmark/core
 */ function buildCoCMCancellationBundle(options) {
    const { cancelledCarePlan, flagToDeactivate, patient, newEngagementStatus, tasksToCancel = [] } = options;
    if (!cancelledCarePlan.id || !patient.id) {
        throw new Error("Care plan and patient must have IDs for cancellation bundle");
    }
    const entries = [];
    // Step 1: Update care plan status to revoked
    entries.push({
        resource: cancelledCarePlan,
        method: "PUT",
        url: `CarePlan/${cancelledCarePlan.id}`
    });
    // Step 2: Deactivate CoCM flag if it exists
    if (flagToDeactivate?.id) {
        const deactivatedFlag = {
            ...flagToDeactivate,
            id: flagToDeactivate.id,
            status: "inactive"
        };
        entries.push({
            resource: deactivatedFlag,
            method: "PUT",
            url: `Flag/${flagToDeactivate.id}`
        });
    }
    // Step 3: Update patient engagement status if needed
    if (newEngagementStatus) {
        const currentTags = patient.meta?.tag || [];
        // Filter out both V1 and V2 engagement status tags
        const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
        // Find the new status option
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === newEngagementStatus);
        if (!statusOption) {
            throw new Error(`Invalid engagement status code: ${newEngagementStatus}`);
        }
        // Add new status tag
        const updatedPatient = {
            ...patient,
            id: patient.id,
            meta: {
                ...patient.meta || {},
                tag: [
                    ...filteredTags,
                    {
                        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                        code: statusOption.code,
                        display: statusOption.display
                    }
                ]
            }
        };
        entries.push({
            resource: updatedPatient,
            method: "PUT",
            url: `Patient/${patient.id}`
        });
    }
    // Step 4: Cancel tasks if setting terminating engagement status
    if (newEngagementStatus && isTerminatingEngagementStatus(newEngagementStatus) && tasksToCancel.length > 0) {
        const taskEntries = buildCancelledTaskEntries(tasksToCancel, `CoCM cancelled: Patient status changed to ${newEngagementStatus}`);
        entries.push(...taskEntries);
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Builds a FHIR Bundle for atomic CoCM completion (CarePlan, Flag, Patient updates).
 * @agentic @benchmark/core
 */ function buildCoCMCompletionBundle(options) {
    const { completedCarePlan, flagToDeactivate, patient, newEngagementStatus, tasksToCancel = [] } = options;
    if (!completedCarePlan.id || !patient.id) {
        throw new Error("Care plan and patient must have IDs for completion bundle");
    }
    const entries = [];
    // Step 1: Update care plan status to completed
    entries.push({
        resource: completedCarePlan,
        method: "PUT",
        url: `CarePlan/${completedCarePlan.id}`
    });
    // Step 2: Deactivate CoCM flag if it exists
    if (flagToDeactivate?.id) {
        const deactivatedFlag = {
            ...flagToDeactivate,
            id: flagToDeactivate.id,
            status: "inactive"
        };
        entries.push({
            resource: deactivatedFlag,
            method: "PUT",
            url: `Flag/${flagToDeactivate.id}`
        });
    }
    // Step 3: Update patient engagement status if needed
    if (newEngagementStatus) {
        const currentTags = patient.meta?.tag || [];
        // Filter out both V1 and V2 engagement status tags
        const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
        // Find the new status option
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === newEngagementStatus);
        if (!statusOption) {
            throw new Error(`Invalid engagement status code: ${newEngagementStatus}`);
        }
        // Add new status tag
        const updatedPatient = {
            ...patient,
            id: patient.id,
            meta: {
                ...patient.meta || {},
                tag: [
                    ...filteredTags,
                    {
                        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                        code: statusOption.code,
                        display: statusOption.display
                    }
                ]
            }
        };
        entries.push({
            resource: updatedPatient,
            method: "PUT",
            url: `Patient/${patient.id}`
        });
    }
    // Step 4: Cancel tasks if setting terminating engagement status
    if (newEngagementStatus && isTerminatingEngagementStatus(newEngagementStatus) && tasksToCancel.length > 0) {
        const taskEntries = buildCancelledTaskEntries(tasksToCancel, `CoCM completed: Patient status changed to ${newEngagementStatus}`);
        entries.push(...taskEntries);
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Build the `cocm-consent` complex extension from consent values, or `null`
 * when neither an explicit answer nor a date is present. False is a recorded No. The sub-extension order
 * (`verified` then `date`) matches the form adapters' historical output.
 * @agentic @benchmark/core
 */ function buildCoCMConsentExtension(consent) {
    const subExtensions = [];
    if (consent.verified !== undefined) {
        subExtensions.push({
            url: "verified",
            valueBoolean: consent.verified
        });
    }
    if (consent.date) {
        subExtensions.push({
            url: "date",
            valueDate: consent.date
        });
    }
    if (subExtensions.length === 0) {
        return null;
    }
    return {
        url: COCM_CONSENT_EXTENSION_URL,
        extension: subExtensions
    };
}
/**
 * Return a copy of `patient` with its `cocm-consent` extension upserted from
 * the given consent values. When consent has neither an explicit answer nor a
 * date, any existing `cocm-consent` extension is removed. All other extensions
 * are preserved. Pure — the input patient is not mutated.
 * @agentic @benchmark/core
 */ function withCoCMConsentExtension(patient, consent) {
    const others = (patient.extension ?? []).filter((ext)=>ext.url !== COCM_CONSENT_EXTENSION_URL);
    const consentExtension = buildCoCMConsentExtension(consent);
    const next = consentExtension ? [
        ...others,
        consentExtension
    ] : others;
    return {
        ...patient,
        extension: next.length > 0 ? next : undefined
    };
}

/**
 * Build a FHIR Bundle for CoCM enrollment transaction.
 *
 * - CarePlan + Tasks (POST — CoCM-only sequential activities)
 * - Flag (POST/PUT — `cocm-enrolled`, create or activate)
 * - Patient (PUT — engagement status → engaged when needed)
 *
 * Triage activities and ServiceRequests are intentionally not part of the
 * CoCM bundle. Callers needing dual-plan creation compose the triage
 * portion via a second `buildCarePlanBundle` call and concatenate the
 * resulting entry arrays.
 * @agentic @benchmark/core
 */ function buildCoCMEnrollmentBundle(options) {
    const { patient, practitionerRole, careTeamId, periodStart, consentDate, notes, supportingInfo, replaces, existingFlag, shouldUpdateEngagementStatus } = options;
    if (!patient.id) {
        throw new Error("Patient must have an id to build enrollment bundle");
    }
    const authorDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
    // Step 1: Build CarePlan + Tasks via shared builder (CoCM-only).
    const carePlanBundle = buildCarePlanBundle({
        type: "cocm",
        patientRef: patient.id,
        patientDisplay: getPatientDisplayName(patient),
        authorRef: practitionerRole.id,
        authorDisplay,
        careTeamId,
        periodStart,
        notes,
        supportingInfo,
        replaces,
        extension: consentDate !== undefined ? [
            {
                url: COCM_CONSENT_DATE_EXTENSION_URL,
                valueDate: consentDate
            }
        ] : undefined,
        initialStatus: "active",
        startFirstActivity: true
    }, {
        ownerRef: practitionerRole.id,
        ownerDisplay: authorDisplay,
        requesterRef: practitionerRole.id,
        requesterDisplay: authorDisplay
    });
    const carePlanEntries = carePlanBundle.entry ?? [];
    // Step 2: Build or update CoCM flag.
    const patientId = toPatientId(patient.id);
    const patientDisplayName = getPatientDisplayName(patient);
    const patientRef = buildPatientReference(patientId, patientDisplayName);
    const flag = existingFlag ? {
        ...existingFlag,
        status: "active",
        subject: patientRef
    } : {
        resourceType: "Flag",
        status: "active",
        code: {
            coding: [
                {
                    system: FLAG_LOCAL_CODE_SYSTEM,
                    code: "cocm-enrolled",
                    display: "Enrolled In CoCM"
                }
            ],
            text: "Enrolled In CoCM"
        },
        category: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/flag-category",
                        code: "admin",
                        display: "Administrative"
                    }
                ],
                text: "Administrative"
            }
        ],
        subject: patientRef
    };
    const flagEntry = {
        request: {
            method: existingFlag?.id ? "PUT" : "POST",
            url: existingFlag?.id ? `Flag/${existingFlag.id}` : "Flag"
        },
        resource: flag
    };
    // Step 3: Build patient update entry if needed. A single Patient PUT carries
    // both the engagement-status transition (when needed) and the CoCM consent
    // write-back, so the Patient-level `cocm-consent` extension stays in sync with
    // the CarePlan's enrollment consent date — never two Patient entries for the
    // same resource. Consent is written back only when it differs from what the
    // patient already has (skip-when-equal avoids no-op writes).
    const existingConsent = getCoCMConsentFromPatient(patient);
    const needsConsentWriteBack = consentDate !== undefined && (existingConsent?.date !== consentDate || existingConsent?.verified !== true);
    const patientEntry = (()=>{
        if (!shouldUpdateEngagementStatus && !needsConsentWriteBack) return null;
        let updatedPatient = {
            ...patient,
            id: patient.id
        };
        if (shouldUpdateEngagementStatus) {
            const currentTags = patient.meta?.tag || [];
            const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
            const engagedOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === "engaged");
            if (!engagedOption) {
                throw new Error("Engaged status option not found");
            }
            updatedPatient = {
                ...updatedPatient,
                meta: {
                    ...patient.meta || {},
                    tag: [
                        ...filteredTags,
                        {
                            system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                            code: engagedOption.code,
                            display: engagedOption.display
                        }
                    ]
                }
            };
        }
        if (needsConsentWriteBack && consentDate !== undefined) {
            updatedPatient = withCoCMConsentExtension(updatedPatient, {
                verified: true,
                date: consentDate
            });
        }
        return {
            request: {
                method: "PUT",
                url: `Patient/${patient.id}`
            },
            resource: updatedPatient
        };
    })();
    const additionalEntries = [
        flagEntry,
        ...patientEntry ? [
            patientEntry
        ] : []
    ];
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: [
            ...carePlanEntries,
            ...additionalEntries
        ]
    };
}

/**
 * Returns display name for practitioner from formatted name, parts, or ID fallback.
 * @agentic @benchmark/core
 */ const getPractitionerDisplayName = (practitioner)=>{
    // Try formatted name first
    const formattedName = formatHumanName(practitioner.name?.[0]);
    if (formattedName) return formattedName;
    // Try individual name parts
    const name = practitioner.name?.[0];
    if (name) {
        const parts = [];
        if (name.prefix?.length) parts.push(...name.prefix);
        if (name.given?.length) parts.push(...name.given);
        if (name.family) parts.push(name.family);
        if (parts.length > 0) return parts.join(" ");
    }
    // Fallback to ID-based display
    return `Practitioner ${practitioner.id}`;
};

/**
 * Converts form data to Practitioner and PractitionerRole resources.
 * @param formData - Practitioner form data
 * @param existingPractitioner - Existing practitioner resource (for updates)
 * @param tempPractitionerFullUrl - Temporary full URL for transaction bundles
 * @returns Build result with practitioner and practitionerRole resources
 * @agentic @benchmark/core
 */ function practitionerFromForm(formData, existingPractitioner, tempPractitionerFullUrl) {
    // Find the selected specialty
    const selectedSpecialty = PRACTITIONER_SPECIALTY_OPTIONS.find((specialty)=>specialty.code === formData["practitioner-specialty"]);
    if (!selectedSpecialty) {
        throw new Error("Invalid specialty selected");
    }
    // Build telecom array
    const telecom = [];
    if (formData["practitioner-phone"]) {
        telecom.push({
            system: "phone",
            value: formData["practitioner-phone"],
            use: "work"
        });
    }
    if (formData["practitioner-email"]) {
        telecom.push({
            system: "email",
            value: formData["practitioner-email"],
            use: "work"
        });
    }
    // Build Practitioner resource
    const practitioner = existingPractitioner ? {
        ...existingPractitioner
    } : {
        resourceType: "Practitioner",
        active: true,
        name: [
            {
                given: [
                    formData["practitioner-first-name"]
                ],
                family: formData["practitioner-last-name"]
            }
        ]
    };
    // Add telecom if provided
    if (telecom.length > 0) {
        practitioner.telecom = telecom;
    }
    // Build PractitionerRole resource
    // Use tempPractitionerFullUrl if provided (for transaction bundles),
    // otherwise use existing practitioner ID, or placeholder
    const displayName = getPractitionerDisplayName(practitioner);
    const practitionerReference = tempPractitionerFullUrl ? {
        reference: tempPractitionerFullUrl,
        display: displayName
    } : existingPractitioner?.id ? buildPractitionerReference(toPractitionerId(existingPractitioner.id), displayName) : {
        reference: "Practitioner/placeholder",
        display: displayName
    };
    const practitionerRole = {
        resourceType: "PractitionerRole",
        practitioner: practitionerReference,
        active: true,
        specialty: [
            {
                coding: [
                    {
                        system: selectedSpecialty.system,
                        code: selectedSpecialty.code,
                        display: selectedSpecialty.display
                    }
                ]
            }
        ]
    };
    // Add telecom to PractitionerRole if provided
    if (telecom.length > 0) {
        practitionerRole.telecom = telecom;
    }
    return {
        practitioner,
        practitionerRole
    };
}

/**
 * Builds a FHIR Bundle for creating a practitioner and practitionerRole atomically.
 * @agentic @benchmark/core
 */ function buildCreatePractitionerBundle(input) {
    // Generate unique temporary UUID for practitioner (used for references in bundle)
    const practitionerTempFullUrl = `urn:uuid:${crypto.randomUUID()}`;
    const { practitioner, practitionerRole } = practitionerFromForm(input.formData, undefined, practitionerTempFullUrl);
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: [
            {
                fullUrl: practitionerTempFullUrl,
                request: {
                    method: "POST",
                    url: "Practitioner"
                },
                resource: practitioner
            },
            {
                request: {
                    method: "POST",
                    url: "PractitionerRole"
                },
                resource: practitionerRole
            }
        ]
    };
}

/**
 * Build Goal Transaction Bundle. Constructs a FHIR Bundle for atomic Goal + baseline Observation creation.
 * @agentic @benchmark/core
 */ /**
 * Builds a FHIR Bundle for atomic Goal + baseline Observation creation.
 * @agentic @benchmark/core
 */ function buildGoalTransactionBundle(goal, baselineObservation) {
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: [
            {
                resource: goal,
                request: {
                    method: "POST",
                    url: "Goal"
                }
            },
            {
                resource: baselineObservation,
                request: {
                    method: "POST",
                    url: "Observation"
                }
            }
        ]
    };
}

/**
 * Builds a FHIR Bundle for atomic Intake cancellation (CarePlan, Patient updates).
 * @agentic @benchmark/core
 */ function buildIntakeCancellationBundle(options) {
    const { cancelledCarePlan, patient, updateEngagementStatus = false, activityTasksToCancel = [], cancellationReason } = options;
    if (!cancelledCarePlan.id || !patient.id) {
        throw new Error("Care plan and patient must have IDs for cancellation bundle");
    }
    const entries = [];
    // Step 1: Update care plan status to revoked
    entries.push({
        resource: cancelledCarePlan,
        method: "PUT",
        url: `CarePlan/${cancelledCarePlan.id}`
    });
    // Step 2: Update patient engagement status to "referred" if needed
    if (updateEngagementStatus) {
        const currentTags = patient.meta?.tag || [];
        // Filter out both V1 and V2 engagement status tags
        const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
        // Find the "referred" status option
        const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === "referred");
        if (!statusOption) {
            throw new Error("Invalid engagement status code: referred");
        }
        // Add new status tag
        const updatedPatient = {
            ...patient,
            id: patient.id,
            meta: {
                ...patient.meta || {},
                tag: [
                    ...filteredTags,
                    {
                        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                        code: statusOption.code,
                        display: statusOption.display
                    }
                ]
            }
        };
        entries.push({
            resource: updatedPatient,
            method: "PUT",
            url: `Patient/${patient.id}`
        });
    }
    // Step 3: Cancel activity Tasks atomically
    if (activityTasksToCancel.length > 0) {
        const taskEntries = buildCancelledTaskEntries(activityTasksToCancel, cancellationReason || "Intake care plan cancelled");
        entries.push(...taskEntries);
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Build a FHIR Bundle for Intake completion transaction
 *
 * This builder constructs a transaction bundle for atomic Intake completion:
 * - CarePlan (PUT - update status to completed)
 * - Patient (PUT - update engagement status to "engaged")
 *
 * @param options - Configuration for building the completion bundle
 * @returns FHIR Bundle ready for transaction submission
 */ function buildIntakeCompletionBundle(options) {
    const { completedCarePlan, patient } = options;
    if (!completedCarePlan.id || !patient.id) {
        throw new Error("Care plan and patient must have IDs for completion bundle");
    }
    const entries = [];
    // Step 1: Update care plan status to completed
    entries.push({
        resource: completedCarePlan,
        method: "PUT",
        url: `CarePlan/${completedCarePlan.id}`
    });
    // Step 2: Update patient engagement status to "engaged"
    const currentTags = patient.meta?.tag || [];
    // Filter out both V1 and V2 engagement status tags
    const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    // Find the "engaged" status option
    const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === "engaged");
    if (!statusOption) {
        throw new Error("Invalid engagement status code: engaged");
    }
    // Add new status tag
    const updatedPatient = {
        ...patient,
        id: patient.id,
        meta: {
            ...patient.meta || {},
            tag: [
                ...filteredTags,
                {
                    system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
                    code: statusOption.code,
                    display: statusOption.display
                }
            ]
        }
    };
    entries.push({
        resource: updatedPatient,
        method: "PUT",
        url: `Patient/${patient.id}`
    });
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Library (clinical content management) domain constants.
 *
 * Models the FHIR R4 `Library` resource as the persistence container for
 * editable clinical documentation (Clinical SOPs, Conversation Guides). One
 * `Library` represents one document; the markdown body is stored as a single
 * embedded `content` Attachment. Provides the canonical system URLs, type /
 * topic / category codings, the document-category configuration map, and slug /
 * name helpers used by the builder and adapters.
 *
 * @agentic @benchmark/core
 */ /** Canonical URL prefix for Benchmark `Library` resources. @agentic @benchmark/core */ const LIBRARY_CANONICAL_URL_BASE = "https://benchmark.health/fhir/Library";
/** Business identifier system for `Library` resources. @agentic @benchmark/core */ const LIBRARY_IDENTIFIER_SYSTEM = "https://benchmark.health/library-id";
/** Benchmark document-category code system (carried in `useContext.value`). @agentic @benchmark/core */ const LIBRARY_CATEGORY_SYSTEM = "https://benchmark.health/library-category";
/** Benchmark usage-context-type system for the document-category context. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_TYPE_SYSTEM = "https://benchmark.health/library-context-type";
/** `useContext.code` value marking the document-category context entry. @agentic @benchmark/core */ const LIBRARY_USE_CONTEXT_CATEGORY_CODE = "document-category";
/** HL7 R4 `library-type` CodeSystem URL (`Library.type`). @agentic @benchmark/core */ const LIBRARY_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/library-type";
/** HL7 R4 `definition-topic` CodeSystem URL (`Library.topic`). @agentic @benchmark/core */ const LIBRARY_TOPIC_SYSTEM = "http://terminology.hl7.org/CodeSystem/definition-topic";
/** Content type for the single embedded markdown body Attachment. @agentic @benchmark/core */ const LIBRARY_MARKDOWN_CONTENT_TYPE = "text/markdown";
/** `library-type` codings used per document category. @agentic @benchmark/core */ const LIBRARY_TYPE_CODINGS = {
    "logic-library": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "logic-library",
        display: "Logic Library"
    },
    "asset-collection": {
        system: LIBRARY_TYPE_SYSTEM,
        code: "asset-collection",
        display: "Asset Collection"
    }
};
/** `definition-topic` coding options selectable on the authoring form. @agentic @benchmark/core */ const LIBRARY_TOPIC_OPTIONS = [
    {
        code: "treatment",
        display: "Treatment"
    },
    {
        code: "education",
        display: "Education"
    },
    {
        code: "assessment",
        display: "Assessment"
    }
];
/**
 * Per-category FHIR coding configuration. `clinical-sop` is locked to
 * `type = logic-library` and `topic = treatment` (D10).
 * @agentic @benchmark/core
 */ const LIBRARY_CATEGORY_CONFIG = {
    "clinical-sop": {
        typeCoding: LIBRARY_TYPE_CODINGS["logic-library"],
        defaultTopicCodes: [
            "treatment"
        ],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "clinical-sop",
            display: "Clinical SOP"
        }
    },
    "conversation-guide": {
        typeCoding: LIBRARY_TYPE_CODINGS["asset-collection"],
        defaultTopicCodes: [],
        categoryCoding: {
            system: LIBRARY_CATEGORY_SYSTEM,
            code: "conversation-guide",
            display: "Conversation Guide"
        }
    }
};
/** Returns the FHIR coding configuration for a document category. @agentic @benchmark/core */ function getLibraryCategoryConfig(category) {
    return LIBRARY_CATEGORY_CONFIG[category];
}
/** Returns the display label for a `definition-topic` code. @agentic @benchmark/core */ function getLibraryTopicDisplay(code) {
    return LIBRARY_TOPIC_OPTIONS.find((o)=>o.code === code)?.display ?? code;
}
/**
 * Builds a Benchmark `Coding` for a `definition-topic` code, preserving the
 * known display where one exists.
 * @agentic @benchmark/core
 */ function buildLibraryTopicCoding(code) {
    return {
        system: LIBRARY_TOPIC_SYSTEM,
        code,
        display: getLibraryTopicDisplay(code)
    };
}
/**
 * Slugifies a title into a URL-safe stem (lowercase, hyphen-separated,
 * alphanumeric). Empty/symbol-only titles fall back to `document`.
 * @agentic @benchmark/core
 */ function slugifyLibraryTitle(title) {
    const stem = title.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-")// Runs of non-alphanumerics are already collapsed to a single "-" above,
    // so trimming a single leading/trailing hyphen is sufficient. A
    // quantifier-free pattern avoids the polynomial-ReDoS backtracking that
    // `/^-+|-+$/` exhibits on uncontrolled input (CodeQL js/polynomial-redos).
    .replace(/^-|-$/g, "").slice(0, 64);
    return stem.length > 0 ? stem : "document";
}
/**
 * Generates a short, lowercase alphanumeric suffix for slug uniqueness.
 * @agentic @benchmark/core
 */ function generateLibrarySlugSuffix(length = 6) {
    const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
    let out = "";
    for(let i = 0; i < length; i++){
        out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
}
/**
 * Builds a unique slug for a document from its title plus a random suffix.
 * Pass an explicit `suffix` for deterministic output (e.g. in tests).
 * @agentic @benchmark/core
 */ function buildLibrarySlug(title, suffix) {
    return `${slugifyLibraryTitle(title)}-${generateLibrarySlugSuffix()}`;
}
/** Builds the canonical `Library.url` for a slug. @agentic @benchmark/core */ function buildLibraryCanonicalUrl(slug) {
    return `${LIBRARY_CANONICAL_URL_BASE}/${slug}`;
}
/**
 * Builds a PascalCase machine `name` from a title (FHIR `Library.name`).
 * Falls back to `Document` for empty/symbol-only titles.
 * @agentic @benchmark/core
 */ function buildLibraryName(title) {
    const name = title.replace(/[^a-zA-Z0-9 ]+/g, " ").split(/\s+/).filter(Boolean).map((word)=>word.charAt(0).toUpperCase() + word.slice(1)).join("").slice(0, 64);
    return name.length > 0 ? name : "Document";
}

/** Encodes a UTF-8 string to base64 (browser + Node safe). @agentic @benchmark/core */ function encodeBase64Utf8(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (const byte of bytes){
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}
/**
 * Builds the single embedded markdown `content` Attachment for a document.
 * @agentic @benchmark/core
 */ function encodeMarkdownAttachment(markdown, title) {
    return {
        contentType: LIBRARY_MARKDOWN_CONTENT_TYPE,
        data: encodeBase64Utf8(markdown),
        title
    };
}

/** Extracts the email from a `ContactDetail`'s telecom list. @agentic @benchmark/core */ function getContactDetailEmail(contact) {
    return contact.telecom?.find((telecom)=>telecom.system === "email" && !!telecom.value)?.value;
}

/** Stable identity key for de-duplicating stakeholders (email, else name). */ function stakeholderKey(contact) {
    return (getContactDetailEmail(contact) ?? contact.name ?? "").toLowerCase();
}
/** Appends a stakeholder unless an entry with the same identity already exists. */ function appendStakeholder(list, actor) {
    const existing = list ?? [];
    const key = stakeholderKey(actor);
    if (key && existing.some((contact)=>stakeholderKey(contact) === key)) {
        return existing;
    }
    return [
        ...existing,
        actor
    ];
}
/** Increments an integer-as-string version, defaulting to 1. */ function bumpVersion(version) {
    const parsed = parseInt(version ?? "", 10);
    const base = Number.isFinite(parsed) ? parsed : 0;
    return String(base + 1);
}
/** YYYY-MM-DD for FHIR `date`-typed fields. */ function toDateOnly(now) {
    return now.toISOString().slice(0, 10);
}
/** Builds the document-category `useContext` entry (P4). */ function buildCategoryUseContext(categoryCoding) {
    return {
        code: {
            system: LIBRARY_USE_CONTEXT_TYPE_SYSTEM,
            code: LIBRARY_USE_CONTEXT_CATEGORY_CODE
        },
        valueCodeableConcept: {
            coding: categoryCoding
        }
    };
}
/** Maps form topic codes (falling back to category defaults) to `Library.topic`. */ function buildTopics(formData) {
    const config = getLibraryCategoryConfig(formData.category);
    const codes = formData.topicCodes.length > 0 ? formData.topicCodes : [
        ...config.defaultTopicCodes
    ];
    return codes.map((code)=>({
            coding: [
                buildLibraryTopicCoding(code)
            ]
        }));
}
/** Applies create/edit form content + coding to a draft resource shell. */ function applyFormContent(library, formData) {
    const config = getLibraryCategoryConfig(formData.category);
    library.title = formData.title;
    library.type = {
        coding: [
            config.typeCoding
        ]
    };
    library.topic = buildTopics(formData);
    library.useContext = [
        buildCategoryUseContext([
            config.categoryCoding
        ])
    ];
    library.content = [
        encodeMarkdownAttachment(formData.markdownBody, formData.title)
    ];
}
/**
 * Builds or transitions a `Library` resource for the given action.
 * @throws Error when required `formData`/`existing` inputs are missing.
 * @agentic @benchmark/core
 */ function buildLibraryResource(input) {
    const { action, actor, formData, existing } = input;
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    if (action === "create") {
        if (!formData) {
            throw new Error("formData is required to create a Library");
        }
        const slug = buildLibrarySlug(formData.title);
        const config = getLibraryCategoryConfig(formData.category);
        const library = {
            resourceType: "Library",
            url: buildLibraryCanonicalUrl(slug),
            identifier: [
                {
                    system: LIBRARY_IDENTIFIER_SYSTEM,
                    value: slug
                }
            ],
            version: "1",
            name: buildLibraryName(formData.title),
            status: "draft",
            date: nowIso,
            type: {
                coding: [
                    config.typeCoding
                ]
            },
            author: [
                actor
            ],
            title: formData.title
        };
        applyFormContent(library, formData);
        return library;
    }
    if (!existing) {
        throw new Error(`existing Library is required to ${action}`);
    }
    const library = {
        ...existing,
        date: nowIso
    };
    switch(action){
        case "edit":
            {
                if (!formData) {
                    throw new Error("formData is required to edit a Library");
                }
                applyFormContent(library, formData);
                // Re-opening an active document for revision returns it to draft (P6).
                if (existing.status === "active") {
                    library.status = "draft";
                }
                library.editor = appendStakeholder(existing.editor, actor);
                break;
            }
        case "review":
            {
                library.reviewer = appendStakeholder(existing.reviewer, actor);
                library.lastReviewDate = toDateOnly(now);
                break;
            }
        case "endorse":
            {
                library.endorser = appendStakeholder(existing.endorser, actor);
                library.approvalDate = toDateOnly(now);
                library.status = "active";
                library.version = bumpVersion(existing.version);
                break;
            }
        case "retire":
            {
                library.status = "retired";
                break;
            }
    }
    return library;
}

/**
 * Build a FHIR ServiceRequest representing a single service need.
 * @agentic @benchmark/core
 */ function buildNeedServiceRequest(input) {
    const { needCode, needDisplay, patientRef, patientDisplay, carePlanRef, carePlanDisplay, requesterRef, requesterDisplay, patientId, carePlanId, modality, modalityDisplay, conditions, preferences } = input;
    const patientReference = patientRef.startsWith("urn:uuid:") ? patientRef : `Patient/${patientRef}`;
    const carePlanReference = carePlanRef.startsWith("urn:uuid:") ? carePlanRef : `CarePlan/${carePlanRef}`;
    const snomedMapping = SERVICE_TYPE_SNOMED_MAP[needCode];
    const serviceRequest = {
        resourceType: "ServiceRequest",
        ...patientId && carePlanId && {
            identifier: [
                {
                    system: SERVICE_REQUEST_NEED_IDENTIFIER_SYSTEM,
                    value: `${patientId}|${carePlanId}|${needCode}`
                }
            ]
        },
        status: "active",
        intent: "order",
        category: [
            {
                coding: [
                    SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL
                ],
                text: SERVICE_REQUEST_CATEGORY.OUTBOUND_REFERRAL.display
            }
        ],
        code: {
            coding: [
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE,
                    code: needCode,
                    display: needDisplay
                },
                ...snomedMapping ? [
                    snomedMapping
                ] : []
            ],
            text: needDisplay
        },
        subject: {
            reference: patientReference,
            display: patientDisplay
        },
        basedOn: [
            {
                reference: carePlanReference,
                display: carePlanDisplay
            }
        ],
        authoredOn: new Date().toISOString(),
        ...modality && {
            locationCode: [
                {
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                            code: modality,
                            display: modalityDisplay || modality
                        }
                    ]
                }
            ]
        },
        ...conditions && conditions.length > 0 && {
            reasonCode: conditions.map((c)=>({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_CONDITION,
                            code: c.code,
                            display: c.display
                        }
                    ]
                }))
        },
        ...preferences && {
            note: [
                {
                    text: preferences
                }
            ]
        }
    };
    if (requesterRef && requesterDisplay) {
        const requesterReference = requesterRef.startsWith("urn:uuid:") ? requesterRef : `PractitionerRole/${requesterRef}`;
        serviceRequest.requester = {
            reference: requesterReference,
            display: requesterDisplay
        };
    }
    return serviceRequest;
}

/**
 * Builds an Observation resource from a questionnaire score with LOINC code and value.
 * @agentic @benchmark/core
 */ function buildObservationFromScore(input) {
    const config = input.questionnaireConfig;
    // Start with existing observation or create new one
    const resolvedStatus = input.status ?? "final";
    const baseObservation = input.existingObservation ? {
        ...input.existingObservation,
        status: resolvedStatus
    } : {
        resourceType: "Observation",
        status: resolvedStatus
    };
    const observation = baseObservation;
    // Get standardized display name and system from constants
    const displayName = getObservationDisplayByLoincCode(config.totalScoreLoincCode);
    const observationCoding = getObservationCoding$1(config.totalScoreLoincCode);
    const system = observationCoding?.system ?? "http://loinc.org";
    // Set code with LOINC code from config using standardized display name
    observation.code = {
        coding: [
            {
                system,
                code: config.totalScoreLoincCode,
                display: displayName
            }
        ],
        text: displayName
    };
    // Set value as integer (FHIR best practice for whole number scores)
    observation.valueInteger = input.score;
    // Persist the severity interpretation so saved results show clinical context
    // (e.g. "High"), not a bare integer. Omitted for unknown risk.
    const interpretation = buildScoreInterpretation(input.interpretation, input.riskLevel);
    if (interpretation) {
        observation.interpretation = interpretation;
    }
    // Set subject (patient)
    if (input.patient.id) {
        const displayName = input.patientDisplayName || getPatientDisplayName(input.patient);
        observation.subject = buildPatientReference(toPatientId(input.patient.id), displayName);
    }
    // Set encounter if provided
    if (input.encounter) {
        if (input.tempEncounterFullUrl) {
            // New encounter: placeholder reference rewritten by Medplum on submit.
            // Omit the display — deriving it from the placeholder uuid leaves a
            // display whose id mismatches the resolved reference (Medplum rewrites
            // the reference but not the display).
            observation.encounter = {
                reference: input.tempEncounterFullUrl
            };
        } else if (input.encounter.id) {
            observation.encounter = buildReferenceWithDisplay("Encounter", input.encounter.id, `Encounter ${input.encounter.id}`);
        }
    }
    // Set performer (practitioner role)
    if (input.practitionerRole) {
        // Need practitioner for display name - this is a limitation
        // In practice, this should be passed in or resolved
        observation.performer = [
            buildPractitionerRoleReference(input.practitionerRole.id || "", input.practitionerRole.practitioner?.display || "Practitioner")
        ];
    }
    // Set effective date/time (use current time for new observations)
    if (!observation.effectiveDateTime) {
        observation.effectiveDateTime = new Date().toISOString();
    }
    // Set category (survey for all questionnaire-based observations)
    observation.category = [
        SURVEY_OBSERVATION_CATEGORY
    ];
    // Set issued timestamp (when observation was recorded)
    if (!observation.issued) {
        observation.issued = new Date().toISOString();
    }
    return observation;
}

/**
 * Builds a FHIR Bundle for atomic patient pause (Flag, CarePlan, Task updates).
 * @agentic @benchmark/core
 */ function buildPausePatientBundle(options) {
    const { patient, reason, durationDays, activeCarePlans, tasksToAdjust } = options;
    if (!patient.id) {
        throw new Error("Patient must have an id to build pause bundle");
    }
    // Build transaction entries
    const entries = [];
    // Step 1: Create pause flag
    const now = new Date();
    const endDate = new Date(now);
    endDate.setUTCDate(endDate.getUTCDate() + durationDays);
    // Build patient display name
    const displayName = patient.name?.[0] ? `${patient.name[0].given?.[0] || ""} ${patient.name[0].family || ""}`.trim() || undefined : undefined;
    const pauseFlag = {
        resourceType: "Flag",
        status: "active",
        code: {
            coding: [
                {
                    system: FLAG_LOCAL_CODE_SYSTEM,
                    code: "patient-paused",
                    display: "Paused"
                }
            ],
            text: "Paused"
        },
        category: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/flag-category",
                        code: "admin",
                        display: "Administrative"
                    }
                ],
                text: "Administrative"
            }
        ],
        subject: {
            reference: `Patient/${patient.id}`,
            display: displayName
        },
        period: {
            start: now.toISOString(),
            end: endDate.toISOString()
        },
        extension: [
            {
                url: PAUSE_FLAG_REASON_EXTENSION_URL,
                valueString: reason
            }
        ]
    };
    entries.push({
        resource: pauseFlag,
        method: "POST",
        url: "Flag"
    });
    // Step 2: Update all active care plans to on-hold
    for (const activeCarePlan of activeCarePlans){
        if (!activeCarePlan?.id) {
            continue;
        }
        // Stamp the pre-pause status so unpause restores it instead of assuming
        // "active" (which would promote a plan that was still a draft).
        const extension = [
            ...(activeCarePlan.extension || []).filter((ext)=>ext.url !== CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL),
            {
                url: CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL,
                valueCode: activeCarePlan.status
            }
        ];
        const heldCarePlan = {
            ...activeCarePlan,
            id: activeCarePlan.id,
            status: "on-hold",
            extension,
            note: [
                ...activeCarePlan.note || [],
                {
                    text: `On Hold: ${reason}`,
                    time: now.toISOString()
                }
            ]
        };
        entries.push({
            resource: heldCarePlan,
            method: "PUT",
            url: `CarePlan/${activeCarePlan.id}`
        });
    }
    // Step 3: Adjust task dates (extend due dates by pause duration)
    for (const task of tasksToAdjust){
        if (!task.id || !task.restriction?.period?.end) {
            continue; // Skip tasks without ID or due date
        }
        const currentDueDate = new Date(task.restriction.period.end);
        const newDueDate = new Date(currentDueDate);
        newDueDate.setUTCDate(newDueDate.getUTCDate() + durationDays);
        const updatedTask = {
            ...task,
            id: task.id,
            restriction: {
                ...task.restriction,
                period: {
                    end: newDueDate.toISOString()
                }
            }
        };
        entries.push({
            resource: updatedTask,
            method: "PUT",
            url: `Task/${task.id}`
        });
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Returns a copy of an on-hold CarePlan restored to its pre-pause status.
 *
 * The pre-pause status marker written by the pause bundle is consumed and
 * removed; plans held before that marker existed fall back to "active". A
 * resume note is appended so the plan's narrative shows the hold ending — the
 * pause path writes an "On Hold" note, and a hold with no matching resume is
 * indistinguishable from a plan that is still paused.
 * @agentic @benchmark/core
 */ function buildResumedCarePlan(carePlan, options = {}) {
    const { resumedAt = new Date(), reason } = options;
    const { extension: _heldExtensions, ...withoutExtensions } = carePlan;
    const remainingExtensions = (carePlan.extension || []).filter((ext)=>ext.url !== CARE_PLAN_PRE_PAUSE_STATUS_EXTENSION_URL);
    return {
        ...withoutExtensions,
        ...remainingExtensions.length > 0 ? {
            extension: remainingExtensions
        } : {},
        status: getPrePauseCarePlanStatus(carePlan) ?? "active",
        note: [
            ...carePlan.note || [],
            {
                text: reason ? `Resumed: ${reason}` : "Resumed",
                time: resumedAt.toISOString()
            }
        ]
    };
}

/**
 * Build Triage Cancellation Transaction Bundle. Constructs a FHIR Bundle for atomic Triage cancellation.
 * @agentic @benchmark/core
 */ /**
 * Builds a FHIR Bundle for atomic Triage cancellation (CarePlan update).
 * @agentic @benchmark/core
 */ function buildTriageCancellationBundle(options) {
    const { cancelledCarePlan } = options;
    if (!cancelledCarePlan.id) {
        throw new Error("Care plan must have an ID for cancellation bundle");
    }
    const entries = [];
    // Update care plan status to revoked
    entries.push({
        resource: cancelledCarePlan,
        method: "PUT",
        url: `CarePlan/${cancelledCarePlan.id}`
    });
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Build Triage Completion Transaction Bundle. Constructs a FHIR Bundle for atomic Triage completion.
 * @agentic @benchmark/core
 */ /**
 * Builds a FHIR Bundle for atomic Triage completion (CarePlan update).
 * @agentic @benchmark/core
 */ function buildTriageCompletionBundle(options) {
    const { completedCarePlan } = options;
    if (!completedCarePlan.id) {
        throw new Error("Care plan must have an ID for completion bundle");
    }
    const entries = [];
    // Update care plan status to completed
    entries.push({
        resource: completedCarePlan,
        method: "PUT",
        url: `CarePlan/${completedCarePlan.id}`
    });
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Builds a FHIR Bundle for atomic patient unpause (Flag, CarePlan, Task updates).
 * @agentic @benchmark/core
 */ function buildUnpausePatientBundle(options) {
    const { patient, pauseFlags, onHoldCarePlans, tasksToRecalculate, plannedDurationDays, actualDurationDays, resumeReason } = options;
    if (!patient.id) {
        throw new Error("Patient must have an id to build unpause bundle");
    }
    if (pauseFlags.length === 0) {
        throw new Error("At least one pause flag is required to build unpause bundle");
    }
    if (pauseFlags.some((flag)=>!flag.id)) {
        throw new Error("Pause flag must have an id to build unpause bundle");
    }
    // Build transaction entries
    const entries = [];
    const now = new Date();
    const nowIso = now.toISOString();
    // Step 1: Deactivate every active pause flag
    for (const pauseFlag of pauseFlags){
        // Ending a pause early moves `period.end` to now. A pause whose end has
        // already elapsed genuinely ended then, so preserve it — rewriting it to
        // now would backdate the record of how long the patient was paused.
        const recordedEnd = pauseFlag.period?.end ? new Date(pauseFlag.period.end).getTime() : NaN;
        const endHasElapsed = Number.isFinite(recordedEnd) && recordedEnd <= now.getTime();
        const deactivatedFlag = {
            ...pauseFlag,
            status: "inactive",
            period: {
                ...pauseFlag.period,
                end: endHasElapsed ? pauseFlag.period?.end : nowIso
            }
        };
        entries.push({
            resource: deactivatedFlag,
            method: "PUT",
            url: `Flag/${pauseFlag.id}`
        });
    }
    // Step 2: Resume all on-hold care plans to their pre-pause status
    for (const onHoldCarePlan of onHoldCarePlans){
        if (onHoldCarePlan?.id && onHoldCarePlan.status === "on-hold") {
            const resumedCarePlan = buildResumedCarePlan(onHoldCarePlan, {
                resumedAt: now,
                reason: resumeReason
            });
            entries.push({
                resource: resumedCarePlan,
                method: "PUT",
                url: `CarePlan/${onHoldCarePlan.id}`
            });
        }
    }
    // Step 3: Recalculate task dates based on actual vs planned pause duration
    // Pausing pushed every open due date out by the full planned duration, so
    // unpausing early hands back the days the patient never spent paused.
    // Clamped at zero: once the pause has run its full course there is nothing
    // to hand back, and an unclamped subtraction would push due dates *further*
    // out by however long the unpause was delayed.
    const remainingPauseDays = Math.max(0, plannedDurationDays - actualDurationDays);
    if (remainingPauseDays > 0) {
        for (const task of tasksToRecalculate){
            if (!task.id || !task.restriction?.period?.end) {
                continue; // Skip tasks without ID or due date
            }
            const currentDueDate = new Date(task.restriction.period.end);
            // Calculate the adjusted due date by subtracting remaining pause days
            // This restores the task date to account for the actual pause duration
            const adjustedDueDate = new Date(currentDueDate);
            adjustedDueDate.setUTCDate(adjustedDueDate.getUTCDate() - remainingPauseDays);
            const updatedTask = {
                ...task,
                id: task.id,
                restriction: {
                    ...task.restriction,
                    period: {
                        end: adjustedDueDate.toISOString()
                    }
                }
            };
            entries.push({
                resource: updatedTask,
                method: "PUT",
                url: `Task/${task.id}`
            });
        }
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Converts form data to FHIR Communication resource. Pure mapping, no IO.
 * @param formData - Communication form data
 * @param patient - Patient resource (for building references)
 * @param practitioner - Practitioner resource (for sender)
 * @param practitionerRole - PractitionerRole resource (for sender)
 * @param existingCommunication - Existing communication resource (for updates)
 * @returns Communication domain model
 * @agentic @benchmark/core
 */ function communicationFromForm(formData, patient, practitioner, practitionerRole, existingCommunication) {
    // Start with existing communication or create new one
    const communication = existingCommunication ? {
        ...existingCommunication
    } : {
        resourceType: "Communication",
        status: "completed",
        priority: "routine"
    };
    // Always update references with fresh display names (even when updating)
    // This ensures display names are always correct and up-to-date
    if (!patient.id) {
        throw new Error("Patient ID is required for communication creation");
    }
    communication.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole ID is required for communication creation");
    }
    communication.sender = buildPractitionerRoleReference(practitionerRole.id, getPractitionerDisplayName(practitioner));
    communication.recipient = [
        buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient))
    ];
    // Manual outreach is staff-logged correspondence with a patient. It carries
    // the `manual-outreach` interaction type (NOT `patient-message`, which is
    // reserved for the Dialpad conversational channel) so it stays out of the
    // channel-scoped SMS thread / unreplied / triage queries. Only set when
    // absent so an explicitly-set category on an existing resource is preserved.
    if (!communication.category || communication.category.length === 0) {
        communication.category = buildCommunicationTypeCategory("manual-outreach");
    }
    // Update fields from form data
    if (formData.sent) {
        communication.sent = formData.sent;
    }
    if (formData.status) {
        communication.status = formData.status;
    }
    if (formData.medium) {
        communication.medium = formData.medium;
    }
    if (formData.payload) {
        communication.payload = formData.payload;
    }
    return communication;
}

/**
 * Builds a FHIR Bundle for communication upsert (Communication, Flags, Tasks).
 * @agentic @benchmark/core
 */ function buildUpsertCommunicationBundle(options) {
    const { formData, patient, practitioner, practitionerRole, existingCommunication, existingActiveFlags = [], onWarning } = options;
    // Build the main communication resource
    const communication = communicationFromForm(formData, patient, practitioner, practitionerRole, existingCommunication);
    // Build flags from form data
    const flags = [];
    if (Array.isArray(formData["followup-flags"]) && formData["followup-flags"].length > 0) {
        const flagCodes = formData["followup-flags"];
        for (const code of flagCodes){
            // Use centralized category resolution from constants
            const category = getFlagCategoryByCode(code);
            if (!category) {
                // Log warning if flag code has no category mapping
                onWarning?.(`Flag code "${code}" has no category mapping and will be skipped`);
                continue;
            }
            // Get display values for category and code
            const categoryDisplay = getFlagCategoryDisplay(category);
            const codingInfo = getFlagCodingByCode(code);
            // Check if this flag already exists for the patient
            const targetToken = codingInfo.system ? `${codingInfo.system}|${code}` : code;
            const hasDuplicate = existingActiveFlags.some((f)=>{
                const codings = f.code?.coding || [];
                return codings.some((c)=>{
                    const token = c.system ? `${c.system}|${c.code}` : c.code;
                    return token === targetToken;
                });
            });
            if (hasDuplicate) {
                continue;
            }
            const flag = {
                resourceType: "Flag",
                status: "active",
                category: [
                    {
                        coding: [
                            {
                                system: "http://terminology.hl7.org/CodeSystem/flag-category",
                                code: category,
                                display: categoryDisplay
                            }
                        ],
                        text: categoryDisplay
                    }
                ],
                code: {
                    coding: [
                        {
                            system: codingInfo.system,
                            code,
                            display: codingInfo.display
                        }
                    ],
                    text: codingInfo.display
                },
                subject: buildPatientReference(toPatientId(patient.id || ""), getPatientDisplayName(patient)),
                author: buildPractitionerRoleReference(practitionerRole.id || "", getPractitionerDisplayName(practitioner))
            };
            flags.push(flag);
        }
    }
    // Build optional follow-up task if specified
    const tasks = [];
    if (formData["followup-task-type"]) {
        const taskConfig = getTaskTypeConfig(formData["followup-task-type"]);
        const task = {
            resourceType: "Task",
            status: "ready",
            intent: "plan",
            priority: taskConfig?.defaultPriority || "routine",
            code: {
                coding: [
                    {
                        system: taskConfig?.system || "http://www.benchmarkhealth.com/fhir/ValueSet/task-type",
                        code: formData["followup-task-type"],
                        display: taskConfig?.display || formData["followup-task-type"]
                    }
                ]
            },
            description: formData["followup-description"] || taskConfig?.description || "",
            authoredOn: new Date().toISOString(),
            for: buildPatientReference(toPatientId(patient.id || ""), getPatientDisplayName(patient)),
            requester: buildPractitionerRoleReference(practitionerRole.id || "", getPractitionerDisplayName(practitioner)),
            owner: formData["followup-assigned-to"] ? buildPractitionerRoleReference(formData["followup-assigned-to"], formData["followup-assigned-to-display"] || "") : undefined
        };
        if (formData["followup-due-date"]) {
            task.restriction = {
                period: {
                    end: formData["followup-due-date"]
                }
            };
        }
        tasks.push(task);
    }
    // Collect all resources into transaction entries
    const entries = [];
    // Add communication (POST for new, PUT for update)
    entries.push({
        resource: communication,
        method: communication.id ? "PUT" : "POST",
        url: communication.id ? `Communication/${communication.id}` : "Communication"
    });
    // Add flags
    for (const flag of flags){
        entries.push({
            resource: flag,
            method: "POST",
            url: "Flag"
        });
    }
    // Add tasks
    for (const task of tasks){
        entries.push({
            resource: task,
            method: "POST",
            url: "Task"
        });
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/** Looks up display name for a code from options array. @agentic @benchmark/core */ function getDisplayForCode(options, code) {
    if (!code) {
        return "";
    }
    return options.find((opt)=>opt.value === code)?.label || code;
}
/** Dimension code to display name mapping. @agentic @benchmark/core */ const DIMENSION_DISPLAYS = {
    // Specialty dimensions
    treatmentModality: "Treatment Modality",
    treatmentCondition: "Treatment Condition",
    // Eligibility dimensions
    age: "Age Group",
    gender: "Gender",
    veteranStatus: "Veteran Status",
    studentStatus: "Student Status",
    pregnancyStatus: "Pregnancy Status",
    legalStatus: "Legal Status",
    incomeBand: "Income Band",
    // Program dimensions
    payerNetwork: "Payer Network",
    stateProgram: "State Program",
    insurancePlan: "Insurance Plan",
    // Characteristic dimensions
    access: "Access",
    availability: "Availability",
    scheduling: "Scheduling",
    waitTime: "Wait Time",
    visitMode: "Visit Mode",
    partnership: "Partnership"
};
/** Gets display name for dimension. @agentic @benchmark/core */ function getDimensionDisplay(dimension) {
    return DIMENSION_DISPLAYS[dimension] || dimension;
}
/** Creates dimension + value coding for eligibility. @agentic @benchmark/core */ function createEligibilityDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        age: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_AGE_GROUP,
        gender: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_GENDER,
        veteranStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_VETERAN_STATUS,
        studentStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_STUDENT_STATUS,
        pregnancyStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_PREGNANCY_STATUS,
        legalStatus: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_LEGAL_STATUS,
        incomeBand: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_INCOME_BAND
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_ELIGIBILITY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Creates dimension + value coding for specialty. @agentic @benchmark/core */ function createSpecialtyDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        treatmentModality: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_MODALITY,
        treatmentCondition: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_TREATMENT_CONDITION
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Creates dimension + value coding for program. @agentic @benchmark/core */ function createProgramDimensionValue(dimension, value, valueDisplay) {
    // Skip if dimension or value is empty/invalid
    if (!dimension || !value) {
        return null;
    }
    // Map dimension to dimension-specific CodeSystem
    const dimensionSystemMap = {
        payerNetwork: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_PAYER_NETWORK,
        stateProgram: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_STATE,
        insurancePlan: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_INSURANCE_PLAN
    };
    const valueSystem = dimensionSystemMap[dimension];
    if (!valueSystem) {
        // Return null instead of throwing to handle incomplete form data gracefully
        return null;
    }
    return {
        coding: [
            {
                system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROGRAM_DIMENSION,
                code: dimension,
                display: getDimensionDisplay(dimension)
            },
            {
                system: valueSystem,
                code: value,
                display: valueDisplay || value
            }
        ],
        text: valueDisplay || value
    };
}
/** Maps careModality value to characteristic visitMode. @agentic @benchmark/core */ function mapCareModalityToVisitMode(careModality) {
    // Find display from constants - single source of truth
    const option = SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((opt)=>opt.value === careModality);
    if (option) {
        return {
            code: careModality,
            display: option.label
        };
    }
    // Legacy mapping for backward compatibility
    const legacyMapping = {
        "in-person-only": {
            code: "in-person",
            display: "In-Person"
        },
        "virtual-only": {
            code: "telehealth-video",
            display: "Telehealth (Video)"
        },
        hybrid: {
            code: "hybrid",
            display: "Hybrid"
        }
    };
    return legacyMapping[careModality] || null;
}
/**
 * Converts form data to FHIR HealthcareService resource. Pure mapping, no IO.
 * @param formData - HealthcareService form data
 * @param existingHealthcareService - Existing healthcare service resource (for updates)
 * @returns HealthcareService domain model
 * @agentic @benchmark/core
 */ function healthcareServiceFromForm(formData, existingHealthcareService) {
    // Start with existing healthcare service or create new one
    const healthcareService = existingHealthcareService ? {
        ...existingHealthcareService
    } : {
        resourceType: "HealthcareService"
    };
    // Set id if updating
    if (existingHealthcareService?.id) {
        healthcareService.id = existingHealthcareService.id;
    }
    // Basic fields
    healthcareService.active = formData.active === "true";
    healthcareService.name = formData.name;
    if (formData.description) {
        healthcareService.comment = formData.description;
    }
    // Category
    if (formData.category) {
        const categoryDisplay = getDisplayForCode(SERVICE_CATEGORY_OPTIONS, formData.category);
        healthcareService.category = [
            createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CATEGORY, formData.category, categoryDisplay)
        ];
    }
    // Type
    if (formData.type) {
        const typeDisplay = getDisplayForCode(SERVICE_TYPE_OPTIONS, formData.type);
        healthcareService.type = [
            createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_TYPE, formData.type, typeDisplay)
        ];
    }
    // Specialty - support both new multi-dimensional format and legacy flat format
    const specialtyList = [];
    // New multi-dimensional specialty format (preferred)
    if (formData.specialty && formData.specialty.length > 0) {
        formData.specialty.forEach((spec)=>{
            const specialtyValue = createSpecialtyDimensionValue(spec.dimension, spec.value, spec.valueDisplay);
            if (specialtyValue) {
                specialtyList.push(specialtyValue);
            }
        });
    }
    // Legacy flat format (backward compatibility)
    if (formData.specialtyLegacy && formData.specialtyLegacy.length > 0) {
        formData.specialtyLegacy.forEach((spec)=>{
            // Use the code as display (can be enhanced later with lookup)
            const display = spec;
            specialtyList.push({
                coding: [
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_SPECIALTY,
                        code: spec,
                        display: display
                    },
                    // Optionally add SNOMED mapping if it's a SNOMED code
                    ...spec.match(/^\d+$/) ? [
                        {
                            system: "http://snomed.info/sct",
                            code: spec,
                            display: display
                        }
                    ] : []
                ],
                text: display
            });
        });
    }
    if (specialtyList.length > 0) {
        healthcareService.specialty = specialtyList;
    }
    // Provided by organization
    if (formData.providedBy) {
        healthcareService.providedBy = buildOrganizationReference(toOrganizationId(formData.providedBy), formData["providedBy-display"] || `Organization/${formData.providedBy}`);
    }
    // Locations - support both new format and legacy format
    if (formData.locations && formData.locations.length > 0) {
        // New format - filter out new locations (handled by builder)
        // Only include existing location references here
        const existingLocationRefs = formData.locations.filter((loc)=>!loc.isNew && loc.existingId).map((loc)=>buildLocationReference(loc.existingId, loc.existingDisplay || `Location/${loc.existingId}`));
        if (existingLocationRefs.length > 0) {
            healthcareService.location = existingLocationRefs;
        }
    } else if (formData.location && formData.location.length > 0) {
        // Legacy format - backward compatibility
        healthcareService.location = formData.location.map((loc, index)=>buildLocationReference(loc, formData["location-displays"]?.[index] || `Location/${loc}`));
    }
    // Coverage areas — only resolved entries (those with a Location id) are
    // serialized here. Unresolved entries (form submitted before the action
    // layer ran ensureCoverageAreaLocations) are intentionally dropped; the
    // builder owns the canonical override path the same way it does for
    // locations.
    if (formData.coverageAreas && formData.coverageAreas.length > 0) {
        const coverageAreaRefs = formData.coverageAreas.flatMap((area)=>{
            const locationId = area.locationId;
            if (!locationId) {
                return [];
            }
            return [
                buildLocationReference(locationId, area.locationDisplay || getCoverageAreaLocationName(area.state))
            ];
        });
        if (coverageAreaRefs.length > 0) {
            healthcareService.coverageArea = coverageAreaRefs;
        }
    }
    // Programs - use dimension + value coding pattern
    if (formData.program && formData.program.length > 0) {
        const programValues = formData.program.map((prog)=>createProgramDimensionValue(prog.dimension, prog.value, prog.valueDisplay)).filter((p)=>p !== null);
        if (programValues.length > 0) {
            healthcareService.program = programValues;
        }
    }
    // Simplified insurances field - convert to program with insurancePlan dimension
    // This takes precedence over program field if both are provided
    if (formData.insurances && formData.insurances.length > 0) {
        const insuranceProgramValues = formData.insurances.map((insuranceId, index)=>createProgramDimensionValue("insurancePlan", insuranceId, formData["insurances-displays"]?.[index] || `Insurance/${insuranceId}`)).filter((p)=>p !== null);
        if (insuranceProgramValues.length > 0) {
            healthcareService.program = insuranceProgramValues;
        }
    }
    // Characteristic - create from careModality, partnershipType, and structured characteristic data
    const characteristics = [];
    // Visit mode from careModality (now supports multiple modalities)
    if (formData.careModality && formData.careModality.length > 0) {
        formData.careModality.forEach((modality)=>{
            const visitMode = mapCareModalityToVisitMode(modality);
            if (visitMode) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: "visitMode",
                            display: "Visit Mode"
                        },
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_VISIT_MODE,
                            code: visitMode.code,
                            display: visitMode.display
                        }
                    ],
                    text: visitMode.display
                });
            }
        });
    }
    // Partnership from partnershipType (legacy field)
    if (formData.partnershipType) {
        const partnershipDisplay = getDisplayForCode(SERVICE_CHARACTERISTIC_PARTNERSHIP_OPTIONS, formData.partnershipType);
        characteristics.push({
            coding: [
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                    code: "partnership",
                    display: "Partnership"
                },
                {
                    system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_PARTNERSHIP,
                    code: formData.partnershipType,
                    display: partnershipDisplay
                }
            ],
            text: partnershipDisplay
        });
    }
    // Structured characteristic data (access, availability, scheduling, waitTime, etc.)
    if (formData.characteristic && formData.characteristic.length > 0) {
        formData.characteristic.forEach((char)=>{
            const dimensionSystemMap = {
                access: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_ACCESS,
                availability: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AVAILABILITY,
                scheduling: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_SCHEDULING,
                waitTime: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_WAIT_TIME
            };
            const valueSystem = dimensionSystemMap[char.dimension];
            if (valueSystem) {
                characteristics.push({
                    coding: [
                        {
                            system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                            code: char.dimension,
                            display: getDimensionDisplay(char.dimension)
                        },
                        {
                            system: valueSystem,
                            code: char.value,
                            display: char.valueDisplay || char.value
                        }
                    ],
                    text: char.valueDisplay || char.value
                });
            }
        });
    }
    // Denormalize age group eligibility into characteristic for FHIR searchability
    if (formData.eligibility) {
        formData.eligibility.filter((elig)=>elig.dimension === "age").forEach((elig)=>{
            characteristics.push({
                coding: [
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_DIMENSION,
                        code: "ageGroup",
                        display: "Age Group"
                    },
                    {
                        system: HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_CHARACTERISTIC_AGE_GROUP,
                        code: elig.value,
                        display: elig.valueDisplay || elig.value
                    }
                ],
                text: elig.valueDisplay || elig.value
            });
        });
    }
    if (characteristics.length > 0) {
        healthcareService.characteristic = characteristics;
    }
    // Meta tags
    const tags = [];
    // Keep first careModality in meta tags for backward compatibility
    if (formData.careModality && formData.careModality.length > 0) {
        tags.push({
            system: "http://benchmarkhealth.com/fhir/care-modality",
            code: formData.careModality[0]
        });
    }
    if (formData.partnershipType) {
        tags.push({
            system: "http://benchmarkhealth.com/fhir/partnership-type",
            code: formData.partnershipType
        });
    }
    if (tags.length > 0) {
        healthcareService.meta = {
            ...healthcareService.meta,
            tag: tags
        };
    }
    // Extra details
    if (formData.extraDetails) {
        healthcareService.extraDetails = formData.extraDetails;
    }
    // Service provision codes - use service-provision CodeSystem
    if (formData.serviceProvisionCode && formData.serviceProvisionCode.length > 0) {
        healthcareService.serviceProvisionCode = formData.serviceProvisionCode.map((code)=>createCodeableConcept(HEALTHCARE_SERVICE_CODESYSTEMS.SERVICE_PROVISION, code, getDisplayForCode(SERVICE_PROVISION_OPTIONS, code)));
    }
    // Eligibility - use dimension + value coding pattern
    if (formData.eligibility && formData.eligibility.length > 0) {
        const eligibilityValues = formData.eligibility.map((elig)=>{
            const code = createEligibilityDimensionValue(elig.dimension, elig.value, elig.valueDisplay);
            return code ? {
                code
            } : null;
        }).filter((e)=>e !== null);
        if (eligibilityValues.length > 0) {
            healthcareService.eligibility = eligibilityValues;
        }
    } else if (formData.eligibilityCriteria) {
        // Backward compatibility: keep old text-based eligibility
        healthcareService.eligibility = [
            {
                comment: formData.eligibilityCriteria
            }
        ];
    }
    // Telecom
    if (formData.telecom && formData.telecom.length > 0) {
        healthcareService.telecom = formData.telecom.map((contact)=>({
                system: contact.system,
                value: contact.value,
                use: contact.use
            }));
    }
    // Identifier
    if (formData.identifier && formData.identifier.length > 0) {
        healthcareService.identifier = formData.identifier;
    }
    return healthcareService;
}

/**
 * Converts form data to FHIR Organization resource.
 * @agentic @benchmark/core
 */ function organizationFromForm(formData) {
    const organization = {
        resourceType: "Organization",
        active: true
    };
    // Name (required for new organizations)
    if (formData.name) {
        organization.name = formData.name;
    }
    // Type (required for new organizations)
    if (formData.type) {
        const typeSystem = getOrganizationTypeSystem(formData.type);
        organization.type = [
            {
                coding: [
                    {
                        system: typeSystem,
                        code: formData.type,
                        display: getOrganizationTypeDisplay(formData.type)
                    }
                ]
            }
        ];
    }
    // Tier (optional - stored in meta.tag)
    if (formData.tier) {
        organization.meta = {
            ...organization.meta,
            tag: [
                ...organization.meta?.tag || [],
                {
                    system: ORGANIZATION_TIER_SYSTEM,
                    code: formData.tier
                }
            ]
        };
    }
    // Alias (optional)
    if (formData.alias && formData.alias.length > 0) {
        organization.alias = formData.alias;
    }
    return organization;
}
/**
 * Generates display name for Organization from form data.
 * @agentic @benchmark/core
 */ function getOrganizationDisplayFromForm(formData) {
    if (formData.existingDisplay) {
        return formData.existingDisplay;
    }
    return formData.name || "New Organization";
}

/**
 * Returns display name for a service type code.
 * @agentic @benchmark/core
 */ function getTypeDisplay(code) {
    const option = SERVICE_TYPE_OPTIONS.find((opt)=>opt.value === code);
    return option?.label || code;
}
/**
 * Builds a FHIR Bundle for HealthcareService upsert with optional Location and Organization creation.
 * @agentic @benchmark/core
 */ function buildUpsertHealthcareServiceBundle(options) {
    const { formData, existingHealthcareService } = options;
    const entries = [];
    const newLocationTempUrls = new Map();
    const serviceTypeTempUrls = new Map();
    let newOrganizationTempUrl;
    // Track Organization reference for HealthcareService.providedBy
    let organizationReference;
    // Track Location references to set on HealthcareService
    const locationReferences = [];
    // Track coverage-area references (canonical state-coverage Locations)
    // pre-resolved by the action layer via ensureCoverageAreaLocations.
    const coverageAreaReferences = (formData.coverageAreas ?? []).flatMap((area)=>{
        const locationId = area.locationId;
        if (!locationId) {
            return [];
        }
        return [
            {
                reference: `Location/${locationId}`,
                display: area.locationDisplay || getCoverageAreaLocationName(area.state)
            }
        ];
    });
    // Process provider organization - create new one or reference existing one
    if (formData.providerOrganization) {
        if (isNewProviderOrganization(formData.providerOrganization)) {
            // Generate temp URL for new Organization
            newOrganizationTempUrl = `urn:uuid:${crypto.randomUUID()}`;
            // Build Organization resource
            const organization = organizationFromForm(formData.providerOrganization);
            entries.push({
                fullUrl: newOrganizationTempUrl,
                resource: organization,
                method: "POST",
                url: "Organization"
            });
            // Reference using temp URL (FHIR server resolves this)
            organizationReference = {
                reference: newOrganizationTempUrl,
                display: getOrganizationDisplayFromForm(formData.providerOrganization)
            };
        } else if (formData.providerOrganization.existingId) {
            // Reference existing Organization
            organizationReference = {
                reference: `Organization/${formData.providerOrganization.existingId}`,
                display: formData.providerOrganization.existingDisplay || `Organization/${formData.providerOrganization.existingId}`
            };
        }
    } else if (formData.providedBy) {
        // Legacy format - use providedBy string
        organizationReference = {
            reference: `Organization/${formData.providedBy}`,
            display: formData["providedBy-display"] || `Organization/${formData.providedBy}`
        };
    }
    // Process locations - create new ones, reference existing ones
    // Support both new format (locations array) and legacy format (location string array)
    const locations = formData.locations;
    if (locations && locations.length > 0) {
        // New format - structured LocationFormData array
        locations.forEach((locationData, index)=>{
            if (isNewLocation(locationData)) {
                // Generate temp URL for new Location
                const tempUrl = `urn:uuid:${crypto.randomUUID()}`;
                newLocationTempUrls.set(index, tempUrl);
                // Build Location resource
                // For managing organization, check if we're creating a new org alongside this location
                const providerOrg = formData.providerOrganization;
                const isCreatingNewOrg = providerOrg && isNewProviderOrganization(providerOrg);
                let location;
                if (isCreatingNewOrg && newOrganizationTempUrl) {
                    // New org being created - build location without org reference first
                    location = locationFromForm(locationData);
                    // Set managingOrganization to use the temp URL directly (FHIR server resolves)
                    location.managingOrganization = {
                        reference: newOrganizationTempUrl,
                        display: getOrganizationDisplayFromForm(providerOrg)
                    };
                } else {
                    // Existing org - use organization ID from new format or fall back to legacy format
                    const organizationId = providerOrg?.existingId || formData.providedBy;
                    const organizationDisplay = providerOrg?.existingDisplay || formData["providedBy-display"];
                    location = locationFromForm(locationData, organizationId, organizationDisplay);
                }
                entries.push({
                    fullUrl: tempUrl,
                    resource: location,
                    method: "POST",
                    url: "Location"
                });
                // Reference using temp URL (FHIR server resolves this)
                locationReferences.push({
                    reference: tempUrl,
                    display: getLocationDisplayFromForm(locationData)
                });
            } else if (locationData.existingId) {
                // Reference existing Location
                locationReferences.push({
                    reference: `Location/${locationData.existingId}`,
                    display: locationData.existingDisplay || `Location/${locationData.existingId}`
                });
            }
        });
    } else if (formData.location && formData.location.length > 0) {
        // Legacy format - string array of location IDs
        // These are all existing locations, no new ones to create
        formData.location.forEach((locId, index)=>{
            locationReferences.push({
                reference: `Location/${locId}`,
                display: formData["location-displays"]?.[index] || `Location/${locId}`
            });
        });
    }
    // Determine service types to create
    const isUpdate = !!existingHealthcareService?.id;
    const serviceTypes = isUpdate ? [
        formData.type
    ] // Edit mode: single type
     : formData.types || [
        formData.type
    ]; // Create mode: multiple types
    // Filter out empty values
    const validServiceTypes = serviceTypes.filter((t)=>!!t);
    // Get organization display name for service name generation
    const organizationDisplay = formData.providerOrganization?.existingDisplay || formData["providedBy-display"] || formData.providerOrganization?.name || "Unknown";
    // Build HealthcareService for each type
    validServiceTypes.forEach((serviceType, index)=>{
        // Create form data with single type for adapter
        const singleTypeFormData = {
            ...formData,
            type: serviceType,
            "type-display": formData["types-displays"]?.[index] || formData["type-display"] || getTypeDisplay(serviceType),
            // Generate unique name for each service
            name: `${formData["types-displays"]?.[index] || getTypeDisplay(serviceType)} by ${organizationDisplay}`
        };
        const healthcareService = healthcareServiceFromForm(singleTypeFormData, isUpdate ? existingHealthcareService : undefined);
        // Override organization reference if we processed a new one
        if (organizationReference) {
            healthcareService.providedBy = organizationReference;
        }
        // Override location references with our processed list
        if (locationReferences.length > 0) {
            healthcareService.location = locationReferences;
        }
        // Override coverage-area references with the action-resolved list
        if (coverageAreaReferences.length > 0) {
            healthcareService.coverageArea = coverageAreaReferences;
        } else if (existingHealthcareService) {
            // Edit mode with no coverage areas selected: clear the field on the
            // updated resource so a previous value is removed by the PUT.
            delete healthcareService.coverageArea;
        }
        // Generate temp URL for new services
        const tempUrl = isUpdate ? undefined : `urn:uuid:${crypto.randomUUID()}`;
        if (tempUrl) {
            serviceTypeTempUrls.set(serviceType, tempUrl);
        }
        // Add HealthcareService entry
        entries.push({
            ...tempUrl && {
                fullUrl: tempUrl
            },
            resource: healthcareService,
            method: isUpdate ? "PUT" : "POST",
            url: isUpdate ? `HealthcareService/${healthcareService.id}` : "HealthcareService"
        });
    });
    return {
        bundle: {
            resourceType: "Bundle",
            type: "transaction",
            entry: entries.map((entry)=>({
                    ...entry.fullUrl && {
                        fullUrl: entry.fullUrl
                    },
                    request: {
                        method: entry.method || "POST",
                        url: entry.url || entry.resource.resourceType
                    },
                    resource: entry.resource
                }))
        },
        newLocationTempUrls,
        newOrganizationTempUrl,
        serviceTypeCount: validServiceTypes.length,
        serviceTypeTempUrls
    };
}

/** Maps coverage type code to display string. @agentic @benchmark/core */ function coverageTypeToDisplay(code) {
    const specialtyMap = {
        EHCPOL: "Extended Healthcare",
        PUBLICPOL: "Public Healthcare",
        DENTPRG: "Dental Program",
        MENTPRG: "Mental Health Program"
    };
    return specialtyMap[code] || code;
}
/** Maps subscriber relationship code to display string. @agentic @benchmark/core */ function relationshipToDisplay(code) {
    switch(code){
        case "self":
            return "Self";
        case "spouse":
            return "Spouse";
        case "child":
            return "Child";
        case "other":
            return "Other";
        default:
            return code;
    }
}
/**
 * Converts form data to FHIR Coverage resource. Pure mapping, no IO.
 * @param formData - Coverage form data
 * @param patient - Patient resource (for building references)
 * @param existingCoverage - Existing coverage resource (for updates)
 * @returns Coverage domain model
 * @agentic @benchmark/core
 */ function coverageFromForm(formData, patient, existingCoverage) {
    // Start with existing coverage or create new one
    const coverage = {
        resourceType: "Coverage",
        status: formData.status,
        meta: {
            profile: [
                PROFILE_URLS.Coverage
            ]
        }
    };
    // Update fields from form data
    coverage.status = formData.status;
    coverage.type = {
        coding: [
            {
                system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
                code: formData["coverage-type"],
                display: coverageTypeToDisplay(formData["coverage-type"])
            }
        ]
    };
    coverage.beneficiary = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    coverage.period = {
        start: formData["period-start"],
        ...formData["period-end"] && {
            end: formData["period-end"]
        }
    };
    // Build payor reference with display name if available
    if (formData["payor-display"]) {
        coverage.payor = [
            buildOrganizationReference(toOrganizationId(formData["payor-name"]), formData["payor-display"])
        ];
    } else {
        // Fallback to reference only if display not provided (for backward compatibility)
        coverage.payor = [
            {
                reference: `Organization/${formData["payor-name"]}`
            }
        ];
    }
    // Add InsurancePlan reference in Coverage.class if plan is selected
    // Preserve existing classes and add/update plan class
    if (formData["insurance-plan"]) {
        const existingClasses = coverage.class || [];
        const nonPlanClasses = existingClasses.filter((cls)=>cls.type?.coding?.[0]?.code !== "plan");
        const planClass = {
            type: {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/coverage-class",
                        code: "plan",
                        display: "Plan"
                    }
                ]
            },
            value: formData["insurance-plan"],
            ...formData["insurance-plan-display"] && {
                name: formData["insurance-plan-display"]
            }
        };
        coverage.class = [
            ...nonPlanClasses,
            planClass
        ];
    }
    coverage.subscriberId = formData["subscriber-id"];
    if (formData["dependent-number"]) {
        coverage.dependent = formData["dependent-number"];
    }
    coverage.relationship = {
        coding: [
            {
                system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
                code: formData.relationship,
                display: relationshipToDisplay(formData.relationship)
            }
        ]
    };
    if (formData.order !== undefined) {
        coverage.order = formData.order;
    }
    return coverage;
}

/** Creation-only referral evidence, retained with the patient. @agentic @benchmark/core */ const ORIGINAL_REFERRAL_EXTENSION_URL = `${EXTENSION_BASE}/original-pcp-referral`;
const REFERRER_DISPLAY_ERROR = "Select a primary care provider with a recorded name.";
function referrerDisplay(values) {
    const id = values["primary-care-provider"];
    const display = values["primary-care-provider-display"]?.trim();
    return display && display.toLowerCase() !== "unknown" && display !== id && !/^(?:Practitioner|PractitionerRole)\//.test(display) ? display : undefined;
}
/** Validate referral inputs and supplied referrer names before any write. @agentic @benchmark/core */ function validatePatientReferral(values, today = localCalendarDateTodayISO()) {
    const errors = {};
    if (values["primary-care-provider"] && !referrerDisplay(values)) {
        errors["primary-care-provider"] = REFERRER_DISPLAY_ERROR;
    }
    const date = values["cocm-consent-date"];
    if (values["cocm-consent-verified"] === true) {
        const parsed = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00Z`) : undefined;
        if (!date || !parsed || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date || date > today) {
            errors["cocm-consent-date"] = "Enter a consent date of today or earlier.";
        }
    }
    const recommendation = values["pcp-recommendation"];
    if (recommendation && ![
        "refer-in",
        "refer-out",
        "defer-to-advocate"
    ].includes(recommendation)) {
        errors["pcp-recommendation"] = "Select a PCP recommendation from the list.";
    }
    if ((values["pcp-additional-recommendations"]?.length ?? 0) > 4000) {
        errors["pcp-additional-recommendations"] = "Enter no more than 4,000 characters.";
    }
    return errors;
}
/** Capture once; later patient adapters preserve this extension untouched. @agentic @benchmark/core */ function buildOriginalReferralExtension(values, author, recordedAt) {
    const display = referrerDisplay(values);
    if (values["primary-care-provider"] && !display) throw new Error(REFERRER_DISPLAY_ERROR);
    const extension = [
        {
            url: "recorded-at",
            valueInstant: recordedAt
        }
    ];
    const add = (entry)=>extension.push(entry);
    if (author) add({
        url: "author",
        valueReference: author
    });
    if (values["pcp-recommendation"]) add({
        url: "recommendation",
        valueCode: values["pcp-recommendation"]
    });
    if (values["pcp-high-risk"] !== undefined) add({
        url: "high-risk",
        valueBoolean: values["pcp-high-risk"]
    });
    if (values["cocm-consent-verified"] !== undefined) add({
        url: "consent",
        valueBoolean: values["cocm-consent-verified"]
    });
    if (values["cocm-consent-verified"] && values["cocm-consent-date"]) add({
        url: "consent-date",
        valueDate: values["cocm-consent-date"]
    });
    if (values["pcp-source-reference"]) add({
        url: "source",
        valueString: values["pcp-source-reference"]
    });
    if (values["pcp-additional-recommendations"]) add({
        url: "recommendations",
        valueString: values["pcp-additional-recommendations"]
    });
    if (values["referral-date"]) add({
        url: "referral-date",
        valueDate: values["referral-date"]
    });
    if (values["primary-care-provider"]) add({
        url: "referrer",
        valueReference: {
            reference: `PractitionerRole/${values["primary-care-provider"]}`,
            display
        }
    });
    return {
        url: ORIGINAL_REFERRAL_EXTENSION_URL,
        extension
    };
}

/**
 * Normalizes phone number to E.164 US format.
 * @agentic @benchmark/phone
 */ function toE164US(phone) {
    if (!phone) {
        return "";
    }
    // Remove common formatting characters
    const base = phone.replace(/[\s\-().]/g, "");
    // Normalize to E.164 US format
    return normalizePhoneNumber(base, "US");
}

/**
 * FHIR-Compliant Contact Relationship Constants
 *
 * Based on FHIR R4 v2-0131 Contact Role code system
 * http://terminology.hl7.org/CodeSystem/v2-0131
 *
 * These constants define the types of relationships a patient contact can have.
 * Used in Patient.contact[].relationship field.
 */ // FHIR v2-0131 system URL
const CONTACT_RELATIONSHIP_SYSTEM = "http://terminology.hl7.org/CodeSystem/v2-0131";
const CONTACT_RELATIONSHIP_OPTIONS = [
    /*
  // Removed since covered elsewhere
   {
    code: "EP",
    display: "Emergency contact person",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency contact person",
  },

    code: "PR",
    display: "Person preparing referral",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Person preparing referral",
  },
  {
    code: "C",
    display: "Emergency Contact",
    system: CONTACT_RELATIONSHIP_SYSTEM,
    description: "Emergency Contact",
  },
  */ // Deprecated codes (v2.9+) - included for backwards compatibility
    {
        code: "BP",
        display: "Billing contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Billing contact person"
    },
    {
        code: "CP",
        display: "Contact person",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Contact person"
    },
    // Active codes
    {
        code: "E",
        display: "Employer",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Employer"
    },
    {
        code: "F",
        display: "Federal Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Federal Agency"
    },
    {
        code: "I",
        display: "Insurance Company",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Insurance Company"
    },
    {
        code: "N",
        display: "Next-of-Kin",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Next-of-Kin"
    },
    {
        code: "O",
        display: "Other",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Other"
    },
    {
        code: "S",
        display: "State Agency",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "State Agency"
    },
    {
        code: "U",
        display: "Unknown",
        system: CONTACT_RELATIONSHIP_SYSTEM,
        description: "Unknown"
    }
];
/**
 * Simplified options for Mantine Select components
 * Maps to { value, label } format expected by Select
 */ CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));

/** Code system URL for family and caregiver patient contacts. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM = CODE_SYSTEM.V3_ROLE_CODE;
const PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS = [
    {
        code: "MTH",
        display: "Mother",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FTH",
        display: "Father",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "PRN",
        display: "Parent",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "CHILD",
        display: "Child",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SONC",
        display: "Son",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "DAUC",
        display: "Daughter",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SPS",
        display: "Spouse",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "SIB",
        display: "Sibling",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "FRND",
        display: "Friend",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    },
    {
        code: "GUARD",
        display: "Primary caregiver",
        system: PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM
    }
];
/** Set of curated family and caregiver relationship codes. @agentic @benchmark/core */ const PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES = new Set(PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>option.code));
/** Select options for family and caregiver contact relationships. @agentic @benchmark/core */ PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));
/**
 * Returns display text for a curated family contact relationship code.
 * @agentic @benchmark/core
 */ const getPatientFamilyContactRelationshipDisplay = (code)=>PATIENT_FAMILY_CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
/**
 * Returns whether a contact relationship belongs to the family/caregiver set.
 * @agentic @benchmark/core
 */ const isPatientFamilyContactRelationship = (code, system)=>{
    if (system === PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM) {
        return true;
    }
    if (system === CODE_SYSTEM.V2_0131) {
        return false;
    }
    if (!code) {
        return false;
    }
    return PATIENT_FAMILY_CONTACT_RELATIONSHIP_CODES.has(code);
};
/**
 * Resolves the relationship code system for a patient contact row.
 * @agentic @benchmark/core
 */ const inferPatientContactRelationshipSystem = (code, system)=>{
    if (system) {
        return system;
    }
    if (code && isPatientFamilyContactRelationship(code)) {
        return PATIENT_FAMILY_CONTACT_RELATIONSHIP_SYSTEM;
    }
    return CODE_SYSTEM.V2_0131;
};

const getContactRelationshipDisplay = (code, system)=>{
    if (system === CODE_SYSTEM.V3_ROLE_CODE) {
        return getPatientFamilyContactRelationshipDisplay(code);
    }
    return CONTACT_RELATIONSHIP_OPTIONS.find((option)=>option.code === code)?.display;
};
/**
 * Maps a single FHIR Patient.contact entry to form data, or null when empty.
 * @agentic @benchmark/core
 */ const patientContactFromFhir = (contact)=>{
    if (!contact.name && !contact.telecom) {
        return null;
    }
    const coding = contact.relationship?.[0]?.coding?.[0];
    const relationship = coding?.code || "";
    const relationshipSystem = inferPatientContactRelationshipSystem(relationship, coding?.system);
    return {
        "first-name": contact.name?.given?.[0] || "",
        "last-name": contact.name?.family || "",
        phone: contact.telecom?.find((entry)=>entry.system === "phone")?.value || "",
        relationship,
        "relationship-system": relationshipSystem
    };
};
/**
 * Maps FHIR Patient.contact[] to patient form contact rows.
 * @agentic @benchmark/core
 */ const patientContactsFromFhir = (contacts)=>{
    if (!contacts || contacts.length === 0) {
        return [];
    }
    return contacts.map((contact)=>patientContactFromFhir(contact)).filter((contact)=>contact !== null);
};
/**
 * Maps patient form contact rows to FHIR Patient.contact[].
 * @agentic @benchmark/core
 */ const patientContactsToFhir = (contacts)=>contacts.map((contact)=>{
        const relationshipSystem = inferPatientContactRelationshipSystem(contact.relationship, contact["relationship-system"]);
        return {
            name: contact["first-name"] || contact["last-name"] ? {
                given: contact["first-name"] ? [
                    contact["first-name"]
                ] : undefined,
                family: contact["last-name"]
            } : undefined,
            telecom: contact.phone ? [
                {
                    system: "phone",
                    value: contact.phone
                }
            ] : undefined,
            relationship: contact.relationship ? [
                {
                    coding: [
                        {
                            system: relationshipSystem,
                            code: contact.relationship,
                            display: getContactRelationshipDisplay(contact.relationship, relationshipSystem)
                        }
                    ]
                }
            ] : undefined
        };
    });

/**
 * Builds the individual-pronouns extension for Patient.extension (trimmed free text in value slice).
 * @agentic @benchmark/core
 */ const buildIndividualPronounsExtension = (pronouns)=>{
    const text = pronouns.trim();
    return {
        url: INDIVIDUAL_PRONOUNS_EXTENSION_URL,
        extension: [
            {
                url: "value",
                valueCodeableConcept: {
                    text
                }
            }
        ]
    };
};
/**
 * Reads pronouns display text from Patient if the individual-pronouns extension is present.
 * @agentic @benchmark/core
 */ const extractIndividualPronounsFromPatient = (patient)=>{
    const root = patient.extension?.find((e)=>e.url === INDIVIDUAL_PRONOUNS_EXTENSION_URL);
    const valueSlice = root?.extension?.find((e)=>e.url === "value");
    const concept = valueSlice?.valueCodeableConcept;
    if (concept?.text?.trim()) {
        return concept.text.trim();
    }
    const display = concept?.coding?.[0]?.display?.trim();
    if (display) {
        return display;
    }
    const code = concept?.coding?.[0]?.code?.trim();
    return code || undefined;
};

/**
 * Converts form data to FHIR Patient resource. Pure mapping, no IO.
 * @param formData - Patient form data
 * @param authorReference - Optional PractitionerRole reference for summary extension author
 * @returns Patient domain model
 * @agentic @benchmark/core
 */ function patientFromForm(formData, authorReference) {
    // Start with basic patient structure
    const patient = {
        resourceType: "Patient",
        active: true
    };
    // Basic patient information
    if (formData["first-name"] || formData["last-name"]) {
        patient.name = [
            {
                use: "official",
                given: formData["first-name"] ? [
                    formData["first-name"]
                ] : undefined,
                family: formData["last-name"]
            }
        ];
        if (formData["preferred-name"]?.trim()) {
            patient.name.push({
                use: "nickname",
                given: [
                    formData["preferred-name"].trim()
                ]
            });
        }
    }
    if (formData.dob) {
        patient.birthDate = formData.dob;
    }
    if (formData["gender-identity"]) {
        patient.gender = formData["gender-identity"];
    }
    // Contact information
    if (formData.phoneNumbers || formData.email) {
        const telecom = [];
        // Add phone numbers with rank
        if (formData.phoneNumbers && formData.phoneNumbers.length > 0) {
            formData.phoneNumbers.forEach((entry)=>{
                if (entry.value) {
                    telecom.push({
                        system: "phone",
                        value: toE164US(entry.value),
                        use: entry.use,
                        rank: entry.rank
                    });
                }
            });
        }
        if (formData.email) {
            telecom.push({
                system: "email",
                value: formData.email
            });
        }
        if (telecom.length > 0) {
            patient.telecom = telecom;
        }
    }
    // Address (skip when explicitly marked as not available)
    if (!formData["address-not-available"] && (formData.street || formData.city || formData.state || formData.zip)) {
        const addressLines = [];
        if (formData.street) addressLines.push(formData.street);
        if (formData["street-2"]) addressLines.push(formData["street-2"]);
        patient.address = [
            {
                line: addressLines.length > 0 ? addressLines : undefined,
                city: formData.city,
                state: formData.state,
                postalCode: formData.zip
            }
        ];
    }
    // Preferred language
    if (formData["preferred-language"]) {
        patient.communication = [
            {
                language: {
                    coding: [
                        {
                            system: "urn:ietf:bcp:47",
                            code: formData["preferred-language"]
                        }
                    ]
                }
            }
        ];
    }
    // Identifiers (MRN/EMR)
    const identifiers = [];
    if (formData.mrn) {
        identifiers.push({
            type: {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                        code: "MR",
                        display: "Medical Record Number"
                    }
                ]
            },
            system: formData.emr,
            value: formData.mrn
        });
    }
    if (identifiers.length > 0) {
        patient.identifier = identifiers;
    }
    // Build generalPractitioner array with both PCP and LCSW
    const generalPractitioners = [];
    // Add referring provider (PCP)
    if (formData["primary-care-provider"]) {
        generalPractitioners.push({
            reference: `PractitionerRole/${formData["primary-care-provider"]}`,
            display: formData["primary-care-provider-display"] || "Practitioner",
            extension: [
                {
                    url: GP_ROLE_TYPE_EXTENSION,
                    valueCode: GP_ROLE_TYPES.REFERRING_PROVIDER
                }
            ]
        });
        // Set managingOrganization from the primary care provider's organization
        if (formData["primary-care-provider-organization-ref"] && formData["primary-care-provider-organization-display"]) {
            patient.managingOrganization = {
                reference: formData["primary-care-provider-organization-ref"],
                display: formData["primary-care-provider-organization-display"]
            };
        }
    }
    // Add Benchmark primary advocate
    if (formData["benchmark-lcsw"]) {
        generalPractitioners.push({
            reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
            display: formData["benchmark-lcsw-display"] || "Primary Advocate",
            extension: [
                {
                    url: GP_ROLE_TYPE_EXTENSION,
                    valueCode: GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE
                }
            ]
        });
    }
    if (generalPractitioners.length > 0) {
        patient.generalPractitioner = generalPractitioners;
    }
    // Extensions (race, ethnicity, sexual orientation)
    const extensions = [];
    // Race extension
    if (formData.race) {
        const raceDisplayMapping = {
            "1002-5": "American Indian or Alaska Native",
            "2028-9": "Asian",
            "2054-5": "Black or African American",
            "2076-8": "Native Hawaiian or Other Pacific Islander",
            "2106-3": "White",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const raceCode = formData.race;
        extensions.push({
            url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race",
            extension: [
                {
                    url: "ombCategory",
                    valueCoding: {
                        system: "urn:oid:2.16.840.1.113883.6.238",
                        code: raceCode,
                        display: raceDisplayMapping[raceCode] || "Unknown"
                    }
                }
            ]
        });
    }
    // Ethnicity extension
    if (formData.ethnicity) {
        const ethnicityDisplayMapping = {
            "2135-2": "Hispanic or Latino",
            "2186-5": "Not Hispanic or Latino",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const ethnicityCode = formData.ethnicity;
        extensions.push({
            url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity",
            extension: [
                {
                    url: "ombCategory",
                    valueCoding: {
                        system: "urn:oid:2.16.840.1.113883.6.238",
                        code: ethnicityCode,
                        display: ethnicityDisplayMapping[ethnicityCode] || "Unknown"
                    }
                }
            ]
        });
    }
    // Sexual orientation extension
    if (formData["sexual-orientation"] && formData["sexual-orientation"].length > 0) {
        extensions.push({
            url: "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation",
            valueCodeableConcept: {
                coding: formData["sexual-orientation"].map((orientation)=>({
                        code: orientation,
                        display: orientation
                    }))
            }
        });
    }
    if (formData.pronouns?.trim()) {
        extensions.push(buildIndividualPronounsExtension(formData.pronouns));
    }
    // Patient summary extension
    if (formData.summary?.trim()) {
        if (authorReference) {
            const summaryExtension = buildPatientSummaryExtension({
                text: formData.summary.trim(),
                authorReference,
                generatedBy: "manual"
            });
            extensions.push(summaryExtension);
        } else {
            // Summary provided but no authorReference - this should not happen in normal UI flow
            // Log warning to catch any code paths that don't pass authorReference
            console.warn("Patient summary was provided but authorReference is missing. Summary will not be saved.", {
                summaryLength: formData.summary.trim().length
            });
        }
    }
    // CoCM consent extension
    const cocmConsentExtension = buildCoCMConsentExtension({
        verified: formData["cocm-consent-verified"],
        date: formData["cocm-consent-date"]
    });
    if (cocmConsentExtension) {
        extensions.push(cocmConsentExtension);
    }
    if (extensions.length > 0) {
        patient.extension = extensions;
    }
    // Build tags for new patient (engagement status and veteran status)
    const tags = [];
    if (formData["patient-engagement-status"]) {
        const code = formData["patient-engagement-status"];
        const option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
        if (option) {
            tags.push({
                system: PATIENT_TAG_MAPPINGS.engagementStatus.system,
                code: option.code,
                display: option.display
            });
        }
    }
    if (formData["veteran-status"]) {
        tags.push({
            system: "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus",
            code: "veteran",
            display: "Veteran"
        });
    }
    if (tags.length > 0) {
        patient.meta = {
            tag: tags
        };
    }
    // Patient contacts
    if (formData["patient-contacts"] && formData["patient-contacts"].length > 0) {
        patient.contact = patientContactsToFhir(formData["patient-contacts"]);
    }
    return patient;
}

/**
 * Creates a Problem List Item Condition from ICD-10 code data (category: problem-list-item, clinicalStatus: active).
 * @agentic @benchmark/core
 */ function buildProblemListCondition(input) {
    const { patient, icd10Code, tempPatientFullUrl, recordedDate } = input;
    // Build patient reference - support both temp URLs (for transaction bundles) and real IDs
    const displayName = getPatientDisplayName(patient);
    const patientReference = tempPatientFullUrl ? {
        reference: tempPatientFullUrl,
        display: displayName
    } : {
        reference: `Patient/${patient.id}`,
        display: displayName
    };
    return {
        resourceType: "Condition",
        subject: patientReference,
        category: [
            {
                coding: [
                    {
                        system: CONDITION_CATEGORY_SYSTEM,
                        code: PROBLEM_LIST_ITEM_CATEGORY.code,
                        display: PROBLEM_LIST_ITEM_CATEGORY.display
                    }
                ]
            }
        ],
        code: {
            coding: [
                {
                    system: ICD10_CM_SYSTEM,
                    code: icd10Code.code,
                    display: icd10Code.display
                }
            ],
            text: icd10Code.display
        },
        clinicalStatus: {
            coding: [
                {
                    system: CONDITION_CLINICAL_STATUS_SYSTEM,
                    code: "active",
                    display: "Active"
                }
            ]
        },
        recordedDate: recordedDate || localCalendarDateTodayISO()
    };
}
/**
 * Builds multiple Problem List Conditions from an array of ICD-10 codes.
 * @agentic @benchmark/core
 */ function buildProblemListConditions(patient, icd10Codes, tempPatientFullUrl) {
    const recordedDate = localCalendarDateTodayISO();
    return icd10Codes.map((icd10Code)=>buildProblemListCondition({
            patient,
            icd10Code,
            tempPatientFullUrl,
            recordedDate
        }));
}

/**
 * Builds a comprehensive FHIR Bundle for atomic patient creation (Patient, Coverages, RelatedPersons, CareTeam, etc.).
 * @agentic @benchmark/core
 */ function buildCreatePatientBundle(options) {
    const { formData, authorReference } = options;
    const errors = validatePatientReferral(formData);
    if (Object.keys(errors).length) throw new Error(Object.values(errors)[0]);
    // Generate temporary UUID for patient (used for references in bundle)
    const patientTempFullUrl = `urn:uuid:${crypto.randomUUID()}`;
    // Build the main patient resource using adapter
    const patient = patientFromForm({
        ...formData,
        "cocm-consent-date": formData["cocm-consent-verified"] ? formData["cocm-consent-date"] : undefined
    }, authorReference);
    patient.extension = [
        ...patient.extension ?? [],
        buildOriginalReferralExtension(formData, authorReference, new Date().toISOString())
    ];
    // Build transaction entries
    const entries = [];
    // Add patient entry
    entries.push({
        fullUrl: patientTempFullUrl,
        resource: patient,
        method: "POST",
        url: "Patient"
    });
    // Build coverages if insurance information is provided
    if (!formData["self-pay"]) {
        // Primary coverage
        if (formData["primary-insurance-provider"]) {
            const primaryCoverageFormData = {
                order: COVERAGE_APPLICABILITY_PRIMARY,
                status: "active",
                "coverage-type": "EHCPOL",
                "period-start": localCalendarDateTodayISO(),
                "period-end": "",
                "payor-name": formData["primary-insurance-provider"],
                "payor-display": formData["primary-insurance-provider-display"],
                "insurance-plan": formData["primary-insurance-plan"],
                "insurance-plan-display": formData["primary-insurance-plan-display"],
                "subscriber-id": formData["primary-subscriber-id"] || "",
                "dependent-number": "",
                relationship: formData["primary-relationship-to-subscriber"] || "self"};
            const patientForAdapter = {
                ...patient,
                id: "temp-for-adapter"
            };
            const primaryCoverage = coverageFromForm(primaryCoverageFormData, patientForAdapter);
            // Override beneficiary reference to use temp fullUrl
            primaryCoverage.beneficiary = {
                reference: patientTempFullUrl,
                display: getPatientDisplayName(patient)
            };
            // Handle subscriber reference (RelatedPerson if not self)
            if (formData["primary-relationship-to-subscriber"] !== "self" && formData["primary-subscriber-first-name"] && formData["primary-subscriber-last-name"]) {
                // Create RelatedPerson for subscriber
                const subscriberTempFullUrl = `urn:uuid:${crypto.randomUUID()}`;
                const subscriber = {
                    resourceType: "RelatedPerson",
                    patient: {
                        reference: patientTempFullUrl,
                        display: getPatientDisplayName(patient)
                    },
                    relationship: [
                        {
                            coding: [
                                {
                                    system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
                                    code: formData["primary-relationship-to-subscriber"] || "other",
                                    display: formData["primary-relationship-to-subscriber"] || "Other"
                                }
                            ]
                        }
                    ],
                    name: [
                        {
                            given: [
                                formData["primary-subscriber-first-name"]
                            ],
                            family: formData["primary-subscriber-last-name"]
                        }
                    ],
                    birthDate: formData["primary-subscriber-dob"],
                    telecom: formData["primary-subscriber-phone"] ? [
                        {
                            system: "phone",
                            value: formData["primary-subscriber-phone"]
                        }
                    ] : undefined
                };
                entries.push({
                    fullUrl: subscriberTempFullUrl,
                    resource: subscriber,
                    method: "POST",
                    url: "RelatedPerson"
                });
                primaryCoverage.subscriber = {
                    reference: subscriberTempFullUrl
                };
            } else {
                // Self relationship - subscriber is the patient
                primaryCoverage.subscriber = {
                    reference: patientTempFullUrl,
                    display: getPatientDisplayName(patient)
                };
            }
            entries.push({
                resource: primaryCoverage,
                method: "POST",
                url: "Coverage"
            });
        }
        // Secondary coverage
        if (formData["has-secondary-insurance"] && formData["secondary-insurance-provider"]) {
            const secondaryCoverageFormData = {
                order: COVERAGE_APPLICABILITY_SECONDARY,
                status: "active",
                "coverage-type": "EHCPOL",
                "period-start": localCalendarDateTodayISO(),
                "period-end": "",
                "payor-name": formData["secondary-insurance-provider"],
                "payor-display": formData["secondary-insurance-provider-display"],
                "insurance-plan": formData["secondary-insurance-plan"],
                "insurance-plan-display": formData["secondary-insurance-plan-display"],
                "subscriber-id": formData["secondary-subscriber-id"] || "",
                "dependent-number": "",
                relationship: formData["secondary-relationship-to-subscriber"] || "self"};
            const patientForAdapter = {
                ...patient,
                id: "temp-for-adapter"
            };
            const secondaryCoverage = coverageFromForm(secondaryCoverageFormData, patientForAdapter);
            // Override beneficiary reference to use temp fullUrl
            secondaryCoverage.beneficiary = {
                reference: patientTempFullUrl,
                display: getPatientDisplayName(patient)
            };
            // Handle subscriber reference (RelatedPerson if not self)
            if (formData["secondary-relationship-to-subscriber"] !== "self" && formData["secondary-subscriber-first-name"] && formData["secondary-subscriber-last-name"]) {
                const subscriberTempFullUrl = `urn:uuid:${crypto.randomUUID()}`;
                const subscriber = {
                    resourceType: "RelatedPerson",
                    patient: {
                        reference: patientTempFullUrl,
                        display: getPatientDisplayName(patient)
                    },
                    relationship: [
                        {
                            coding: [
                                {
                                    system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
                                    code: formData["secondary-relationship-to-subscriber"] || "other",
                                    display: formData["secondary-relationship-to-subscriber"] || "Other"
                                }
                            ]
                        }
                    ],
                    name: [
                        {
                            given: [
                                formData["secondary-subscriber-first-name"]
                            ],
                            family: formData["secondary-subscriber-last-name"]
                        }
                    ],
                    birthDate: formData["secondary-subscriber-dob"],
                    telecom: formData["secondary-subscriber-phone"] ? [
                        {
                            system: "phone",
                            value: formData["secondary-subscriber-phone"]
                        }
                    ] : undefined
                };
                entries.push({
                    fullUrl: subscriberTempFullUrl,
                    resource: subscriber,
                    method: "POST",
                    url: "RelatedPerson"
                });
                secondaryCoverage.subscriber = {
                    reference: subscriberTempFullUrl
                };
            } else {
                secondaryCoverage.subscriber = {
                    reference: patientTempFullUrl,
                    display: getPatientDisplayName(patient)
                };
            }
            entries.push({
                resource: secondaryCoverage,
                method: "POST",
                url: "Coverage"
            });
        }
    }
    // Build emergency contact (RelatedPerson)
    if (formData["emergency-contact-first-name"] || formData["emergency-contact-last-name"] || formData["emergency-contact-phone"]) {
        const emergencyContact = {
            resourceType: "RelatedPerson",
            patient: {
                reference: patientTempFullUrl,
                display: getPatientDisplayName(patient)
            },
            relationship: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v2-0131",
                            code: "emergency",
                            display: "Emergency contact person"
                        }
                    ]
                }
            ],
            name: formData["emergency-contact-first-name"] || formData["emergency-contact-last-name"] ? [
                {
                    given: formData["emergency-contact-first-name"] ? [
                        formData["emergency-contact-first-name"]
                    ] : undefined,
                    family: formData["emergency-contact-last-name"]
                }
            ] : undefined,
            telecom: formData["emergency-contact-phone"] ? [
                {
                    system: "phone",
                    value: formData["emergency-contact-phone"]
                }
            ] : undefined
        };
        entries.push({
            resource: emergencyContact,
            method: "POST",
            url: "RelatedPerson"
        });
    }
    // Build CareTeam if LCSW or Advocate specified
    if (formData["benchmark-lcsw"] || formData["benchmark-advocate"]) {
        const participants = [];
        // Add patient as participant
        const patientName = patient.name?.[0];
        const patientDisplayName = patientName ? `${patientName.given?.[0] || ""} ${patientName.family || ""}`.trim() : getPatientDisplayName(patient);
        participants.push({
            role: [
                {
                    coding: [
                        {
                            system: "http://snomed.info/sct",
                            code: "116154003",
                            display: "Patient"
                        }
                    ]
                }
            ],
            member: {
                reference: patientTempFullUrl,
                display: patientDisplayName
            }
        });
        // Add LCSW if specified
        if (formData["benchmark-lcsw"] && formData["benchmark-lcsw-display"]) {
            participants.push({
                role: [
                    {
                        coding: [
                            {
                                system: "http://snomed.info/sct",
                                code: "224598009",
                                display: "Licensed Clinical Social Worker"
                            }
                        ]
                    }
                ],
                member: {
                    reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
                    display: formData["benchmark-lcsw-display"]
                }
            });
        }
        // Add Advocate if specified
        if (formData["benchmark-advocate"] && formData["benchmark-advocate-display"]) {
            participants.push({
                role: [
                    {
                        coding: [
                            {
                                system: "http://snomed.info/sct",
                                code: "429577009",
                                display: "Patient advocate"
                            }
                        ]
                    }
                ],
                member: {
                    reference: `PractitionerRole/${formData["benchmark-advocate"]}`,
                    display: formData["benchmark-advocate-display"]
                }
            });
        }
        const careTeam = {
            resourceType: "CareTeam",
            status: "active",
            name: "Benchmark Care Team",
            category: [
                {
                    coding: [
                        {
                            system: "http://loinc.org",
                            code: "LA28865-6",
                            display: "Longitudinal Care-Coordination"
                        }
                    ]
                }
            ],
            subject: {
                reference: patientTempFullUrl,
                display: patientDisplayName
            },
            participant: participants
        };
        entries.push({
            resource: careTeam,
            method: "POST",
            url: "CareTeam"
        });
    }
    // Build ServiceRequest if primary care provider specified
    if (formData["primary-care-provider"] && formData["primary-care-provider-display"]) {
        const referralDate = formData["referral-date"] ? `${formData["referral-date"]}T12:00:00Z` : `${localCalendarDateTodayISO()}T12:00:00Z`;
        const serviceRequest = {
            resourceType: "ServiceRequest",
            status: "active",
            intent: "order",
            category: [
                {
                    coding: [
                        {
                            system: "http://snomed.info/sct",
                            code: "305932003",
                            display: "Referral by own general practitioner"
                        }
                    ],
                    text: "Referral by own general practitioner"
                }
            ],
            code: {
                coding: [
                    {
                        system: "http://snomed.info/sct",
                        code: "1003472007",
                        display: "Referral to patient navigation service"
                    }
                ],
                text: "Referral to patient navigation service"
            },
            subject: {
                reference: patientTempFullUrl,
                display: getPatientDisplayName(patient)
            },
            authoredOn: referralDate,
            requester: buildReferenceWithDisplay("PractitionerRole", formData["primary-care-provider"], formData["primary-care-provider-display"]),
            note: [
                {
                    text: "ServiceRequest created automatically during patient intake process"
                }
            ]
        };
        entries.push({
            resource: serviceRequest,
            method: "POST",
            url: "ServiceRequest"
        });
    }
    // Build Problem List Conditions (ICD-10 codes)
    if (formData["icd10-codes"] && formData["icd10-codes"].length > 0) {
        const conditions = buildProblemListConditions(patient, formData["icd10-codes"], patientTempFullUrl);
        for (const condition of conditions){
            entries.push({
                resource: condition,
                method: "POST",
                url: "Condition"
            });
        }
    }
    // OCPE Phase 3: Deprecated standalone new-member-intake Task removed.
    // Intake activities are now plan-backed Tasks created with the CarePlan
    // via onboardNewPatient → createCarePlan (buildCarePlanWithTasksBundle).
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Get patient engagement status (V2 preferred, falls back to V1)
 */ function getEngagementStatus(patient) {
    if (!patient.meta?.tag) return null;
    // First check for V2 system tags (preferred)
    const v2Tags = patient.meta.tag.filter((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v2Tags.length > 0) {
        const mostRecentV2Tag = v2Tags[v2Tags.length - 1];
        if (mostRecentV2Tag?.code) {
            // Try to find matching V2 option
            const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV2Tag.code);
            if (v2Option) {
                return {
                    code: v2Option.code,
                    display: v2Option.display,
                    system: PATIENT_ENGAGEMENT_STATUS_SYSTEM
                };
            }
            // Fallback to tag display if no matching option found
            return {
                code: mostRecentV2Tag.code,
                display: mostRecentV2Tag.display || mostRecentV2Tag.code,
                system: mostRecentV2Tag.system
            };
        }
    }
    // Fall back to V1 system tags (which may contain V2 codes in production)
    const v1Tags = patient.meta.tag.filter((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM);
    if (v1Tags.length === 0) return null;
    const mostRecentV1Tag = v1Tags[v1Tags.length - 1];
    if (!mostRecentV1Tag?.code) return null;
    // Try to find matching option from V2 first (V2 codes stored under V1 system), then V1
    const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v2Option) {
        return {
            code: v2Option.code,
            display: v2Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((option)=>option.code === mostRecentV1Tag.code);
    if (v1Option) {
        return {
            code: v1Option.code,
            display: v1Option.display,
            system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM
        };
    }
    // Fallback to tag display if no matching option found
    return {
        code: mostRecentV1Tag.code,
        display: mostRecentV1Tag.display || mostRecentV1Tag.code,
        system: mostRecentV1Tag.system
    };
}

/**
 * Extracts ICD-10 code from Condition resource. Returns null if not found.
 * @param condition - FHIR Condition resource
 * @returns ICD10Code or null if not found
 * @agentic @benchmark/core
 */ function conditionToICD10Code(condition) {
    // Find ICD-10-CM coding in the condition's code
    const icd10Coding = condition.code?.coding?.find((coding)=>coding.system === ICD10_CM_SYSTEM);
    if (!icd10Coding?.code) {
        return null;
    }
    return {
        code: icd10Coding.code,
        display: icd10Coding.display || condition.code?.text || icd10Coding.code
    };
}
/**
 * Converts Condition array to ICD-10 codes for form. Filters non-ICD-10 conditions.
 * @param conditions - Array of FHIR Condition resources
 * @returns Array of ICD10Code objects for form use
 * @agentic @benchmark/core
 */ function conditionsToICD10Codes(conditions) {
    return conditions.map(conditionToICD10Code).filter((code)=>code !== null);
}

/**
 * Converts FHIR Patient and related resources to PatientFormData.
 * @param context - All resources needed for transformation
 * @returns PatientFormData ready for form editing
 * @agentic @benchmark/core
 */ function patientToForm(context) {
    const { patient, coverages, relatedPersons, careTeams, conditions, practitionerRole, referralDate } = context;
    const formData = initialisePatientFormData();
    populatePersonalInfo(formData, patient);
    populateIdentifiers(formData, patient, referralDate);
    populateTelecom(formData, patient);
    populateAddress(formData, patient);
    populateCommunicationDetails(formData, patient);
    populateDemographics(formData, patient);
    populatePrimaryCareProvider(formData, patient, practitionerRole);
    populateRelatedResources(formData, patient, coverages, relatedPersons, careTeams);
    populateConditions(formData, conditions);
    populateCocmConsent(formData, patient);
    populateSummary(formData, patient);
    return formData;
}
/** Initializes empty PatientFormData. @agentic @benchmark/core */ const initialisePatientFormData = ()=>({
        "first-name": "",
        "last-name": "",
        "preferred-name": "",
        dob: "",
        "referral-date": "",
        "primary-care-provider": "",
        mrn: "",
        emr: "",
        "cocm-consent-verified": undefined,
        "cocm-consent-date": "",
        "preferred-language": "",
        phoneNumbers: [
            {
                value: "",
                use: "mobile",
                rank: 1
            }
        ],
        email: "",
        street: "",
        "street-2": "",
        city: "",
        state: "",
        zip: "",
        "address-not-available": false,
        "primary-insurance-provider": "",
        "primary-insurance-plan": "",
        "primary-subscriber-id": "",
        "primary-relationship-to-subscriber": "self",
        "primary-subscriber-first-name": "",
        "primary-subscriber-last-name": "",
        "primary-subscriber-dob": "",
        "primary-subscriber-phone": "",
        "has-secondary-insurance": false,
        "secondary-insurance-provider": "",
        "secondary-insurance-plan": "",
        "secondary-subscriber-id": "",
        "secondary-relationship-to-subscriber": "self",
        "secondary-subscriber-first-name": "",
        "secondary-subscriber-last-name": "",
        "secondary-subscriber-dob": "",
        "secondary-subscriber-phone": "",
        "emergency-contact-first-name": "",
        "emergency-contact-last-name": "",
        "emergency-contact-phone": "",
        "patient-contacts": [],
        race: "",
        ethnicity: "",
        "veteran-status": false,
        "gender-identity": "",
        pronouns: "",
        "sexual-orientation": [],
        "self-pay": false,
        "benchmark-lcsw": "",
        "benchmark-advocate": "",
        "icd10-codes": []
    });
/** Populates personal info from Patient. @agentic @benchmark/core */ const populatePersonalInfo = (formData, patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (officialName) {
        formData["first-name"] = officialName.given?.[0] || "";
        formData["last-name"] = officialName.family || "";
    }
    const nickname = patient.name?.find((n)=>n.use === "nickname");
    if (nickname?.given?.[0]) {
        formData["preferred-name"] = nickname.given[0];
    }
    if (patient.birthDate) {
        formData["dob"] = patient.birthDate;
    }
};
/** Populates identifiers from Patient. @agentic @benchmark/core */ const populateIdentifiers = (formData, patient, referralDate)=>{
    if (referralDate) {
        formData["referral-date"] = referralDate;
    }
    const mrnInfo = getPatientMRN(patient);
    if (mrnInfo) {
        formData["mrn"] = mrnInfo.value;
        let emrSystem = mrnInfo.system;
        // Fallback: legacy EI identifier stored the EMR system name as its value
        if (!emrSystem) {
            const legacyEI = patient.identifier?.find((id)=>id.type?.coding?.[0]?.code === "EI" && !id.system && !!id.value);
            if (legacyEI?.value) {
                emrSystem = legacyEI.value;
            }
        }
        if (emrSystem) {
            const emrMapping = {
                "urn:oid:1.2.36.146.595.217.0.1": "www.athenahealth.com",
                "www.athenahealth.com": "www.athenahealth.com",
                "www.epic.com": "www.epic.com"
            };
            formData["emr"] = emrMapping[emrSystem] || emrSystem;
        }
    }
};
/** Populates telecom from Patient. @agentic @benchmark/core */ const populateTelecom = (formData, patient)=>{
    const telecom = getActivePatientTelecom(patient);
    if (!telecom || telecom.length === 0) return;
    const phoneNumbers = telecom.filter((entry)=>entry.system === "phone" && entry.value).map((entry, index)=>buildPhoneNumberEntry(entry.value, entry.use, entry.rank, index));
    if (phoneNumbers.length > 0) {
        formData.phoneNumbers = phoneNumbers.sort((a, b)=>a.rank - b.rank);
    }
    const email = telecom.find((entry)=>entry.system === "email");
    if (email?.value) {
        formData["email"] = email.value;
    }
};
/** Builds PhoneNumberEntry from telecom. @agentic @benchmark/core */ const buildPhoneNumberEntry = (value, use, rank, index)=>{
    // Map FHIR use codes to PhoneNumberEntry use (exclude "old")
    const validUse = use === "home" || use === "work" || use === "temp" || use === "mobile" ? use : "mobile";
    return {
        value,
        use: validUse,
        rank: rank || index + 1
    };
};
/** Populates address from Patient. @agentic @benchmark/core */ const populateAddress = (formData, patient)=>{
    const address = getActivePatientAddress(patient);
    if (!address) {
        return;
    }
    formData["street"] = address.line?.[0] || "";
    formData["street-2"] = address.line?.[1] || "";
    formData["city"] = address.city || "";
    formData["state"] = address.state || "";
    formData["zip"] = address.postalCode || "";
    if (!hasStructuredAddress(formData) && address.text) {
        populateAddressFromText(formData, address.text);
    }
};
/** Checks if form has structured address. @agentic @benchmark/core */ const hasStructuredAddress = (formData)=>Boolean(formData["street"] || formData["city"] || formData["state"] || formData["zip"]);
/** Populates address from text. @agentic @benchmark/core */ const populateAddressFromText = (formData, addressText)=>{
    const trimmed = addressText.trim();
    // Match 5-digit ZIP (with or without +4 extension)
    const zipMatch = trimmed.match(/(\d{5})-\d{4}/) || trimmed.match(/(\d{5})/);
    if (zipMatch) {
        formData["zip"] = zipMatch[1];
        const zipIndex = trimmed.indexOf(zipMatch[0]);
        const beforeZip = trimmed.substring(0, zipIndex).trim().replace(/,\s*$/, "");
        const stateMatch = beforeZip.match(/,?\s*([A-Z]{2})\s*$/);
        if (stateMatch) {
            formData["state"] = stateMatch[1];
            const withoutState = beforeZip.replace(/,?\s*[A-Z]{2}\s*$/, "").trim();
            parseStreetAndCity(formData, withoutState);
            return;
        }
        formData["street"] = beforeZip;
        return;
    }
    const stateMatch = trimmed.match(/,\s*([A-Z]{2})(?:\s|,|$)/);
    if (stateMatch) {
        formData["state"] = stateMatch[1];
        const beforeState = trimmed.substring(0, stateMatch.index).trim();
        parseStreetAndCity(formData, beforeState);
        return;
    }
    formData["street"] = trimmed;
};
/** Parses street and city from value. @agentic @benchmark/core */ const parseStreetAndCity = (formData, value)=>{
    if (!value) {
        return;
    }
    const duplicateMatch = value.match(/^(\d+\s+[^,]+),\s*\d+\s+[^,]+,\s*(.+)$/);
    const addressToProcess = duplicateMatch ? `${duplicateMatch[1].trim()}, ${duplicateMatch[2].trim()}` : value;
    const streetIndicatorRegex = /\b(St\.?|Street|Ave\.?|Avenue|Rd\.?|Road|Dr\.?|Drive|Ln\.?|Lane|Way|Blvd\.?|Boulevard|Ct\.?|Court|Pl\.?|Place|Pkwy\.?|Parkway|Cir\.?|Circle|Ter\.?|Terrace)\b/i;
    if (streetIndicatorRegex.test(addressToProcess)) {
        const matches = [];
        const regex = new RegExp(streetIndicatorRegex.source, "gi");
        let match;
        while((match = regex.exec(addressToProcess)) !== null){
            matches.push(match);
        }
        if (matches.length > 0) {
            const lastMatch = matches[matches.length - 1];
            const lastIndicatorEnd = lastMatch.index + lastMatch[0].length;
            formData["street"] = addressToProcess.substring(0, lastIndicatorEnd).trim();
            formData["city"] = addressToProcess.substring(lastIndicatorEnd).replace(/^[,\s]+/, "").trim();
            return;
        }
    }
    const lastCommaIndex = addressToProcess.lastIndexOf(",");
    if (lastCommaIndex > 0) {
        formData["street"] = addressToProcess.substring(0, lastCommaIndex).trim();
        formData["city"] = addressToProcess.substring(lastCommaIndex + 1).trim();
        return;
    }
    const words = addressToProcess.split(/\s+/);
    if (words.length > 3) {
        if (words.length > 4) {
            formData["city"] = words.slice(-2).join(" ");
            formData["street"] = words.slice(0, -2).join(" ");
        } else {
            formData["city"] = words[words.length - 1];
            formData["street"] = words.slice(0, -1).join(" ");
        }
        return;
    }
    formData["street"] = addressToProcess;
};
/**
 * Maps the first Patient.communication language to the form's language option
 * code, resolving display names and free text against LANGUAGES so the edit
 * Select can show it. Values outside the list pass through unchanged.
 * @agentic @benchmark/core
 */ const getPreferredLanguageFormValue = (patient)=>{
    const language = patient.communication?.[0]?.language;
    const value = (language?.coding?.[0]?.code || language?.coding?.[0]?.display || language?.text || "").trim();
    return LANGUAGES.find((option)=>option.display.toLowerCase() === value.toLowerCase())?.code ?? value;
};
/** Populates communication details from Patient. @agentic @benchmark/core */ const populateCommunicationDetails = (formData, patient)=>{
    formData["preferred-language"] = getPreferredLanguageFormValue(patient);
};
/** Populates demographics from Patient. @agentic @benchmark/core */ const populateDemographics = (formData, patient)=>{
    if (patient.gender) {
        formData["gender-identity"] = patient.gender;
    }
    const extensions = patient.extension || [];
    const raceExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race");
    if (raceExtension?.extension) {
        const raceCategory = raceExtension.extension.find((ext)=>ext.url === "ombCategory");
        if (raceCategory?.valueCoding?.code) {
            formData["race"] = raceCategory.valueCoding.code;
        }
    }
    const ethnicityExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity");
    if (ethnicityExtension?.extension) {
        const ethnicityCategory = ethnicityExtension.extension.find((ext)=>ext.url === "ombCategory");
        if (ethnicityCategory?.valueCoding?.code) {
            formData["ethnicity"] = ethnicityCategory.valueCoding.code;
        }
    }
    const sexualOrientationExtension = extensions.find((ext)=>ext.url === "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation");
    if (sexualOrientationExtension?.valueCodeableConcept?.coding) {
        formData["sexual-orientation"] = sexualOrientationExtension.valueCodeableConcept.coding.map((coding)=>coding.code).filter(Boolean);
    }
    const pronounsText = extractIndividualPronounsFromPatient(patient);
    if (pronounsText) {
        formData.pronouns = pronounsText;
    }
    const veteranTag = patient.meta?.tag?.find((tag)=>tag.system === "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus");
    formData["veteran-status"] = veteranTag?.code === "veteran";
    // Read engagement status preferring the V2 system, then falling back to V1.
    // The engagement modal writes V2-system tags (e.g. "activated"), so reading
    // only the V1 system — as this adapter previously did — missed those
    // statuses entirely. When the status was not read, the edit form kept its
    // default value ("referred") and silently overwrote the real status on the
    // next profile save, resetting activated patients back to referred.
    const engagementStatus = getEngagementStatus(patient);
    if (engagementStatus?.code) {
        formData["patient-engagement-status"] = engagementStatus.code;
    }
};
/** Finds general practitioner by role type. @agentic @benchmark/core */ const findGeneralPractitionerByType = (patient, roleType)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === roleType));
};
/** Populates primary care provider from Patient. @agentic @benchmark/core */ const populatePrimaryCareProvider = (formData, patient, practitionerRole)=>{
    // Extract PCP (referring provider)
    const pcpRef = findGeneralPractitionerByType(patient, GP_ROLE_TYPES.REFERRING_PROVIDER) || patient.generalPractitioner?.[0]; // Fallback for legacy data
    if (pcpRef?.reference) {
        if (pcpRef.reference.startsWith("PractitionerRole/")) {
            formData["primary-care-provider"] = pcpRef.reference.replace("PractitionerRole/", "");
            if (pcpRef.display) {
                formData["primary-care-provider-display"] = pcpRef.display;
            }
        } else if (practitionerRole?.id) {
            formData["primary-care-provider"] = practitionerRole.id;
            // Try to get display name from practitionerRole
            const displayName = practitionerRole.practitioner?.display || practitionerRole.organization?.display;
            if (displayName) {
                formData["primary-care-provider-display"] = displayName;
            }
        }
    }
    // Populate managing organization fields from patient.managingOrganization
    // This ensures the organization reference is preserved during form round-trips
    if (patient.managingOrganization?.reference) {
        formData["primary-care-provider-organization-ref"] = patient.managingOrganization.reference;
        if (patient.managingOrganization.display) {
            formData["primary-care-provider-organization-display"] = patient.managingOrganization.display;
        }
    }
};
/** Populates related resources (insurance, contacts, care team). @agentic @benchmark/core */ const populateRelatedResources = (formData, patient, coverages, relatedPersons, careTeams)=>{
    populateInsurance(formData, patient, coverages, relatedPersons);
    populateEmergencyContact(formData, relatedPersons);
    populatePatientContacts(formData, patient);
    populateCareTeam(formData, patient, careTeams);
};
/** Populates insurance from coverages. @agentic @benchmark/core */ const populateInsurance = (formData, patient, coverages, relatedPersons)=>{
    // Only populate form with active coverages - cancelled coverages should not appear
    const activeCoverages = coverages.filter((coverage)=>coverage.status === "active");
    formData["self-pay"] = activeCoverages.length === 0;
    if (activeCoverages.length > 0) {
        populateCoverage(formData, patient, activeCoverages[0], "primary", relatedPersons);
    }
    if (activeCoverages.length > 1) {
        formData["has-secondary-insurance"] = true;
        populateCoverage(formData, patient, activeCoverages[1], "secondary", relatedPersons);
    }
};
/** Populates coverage for primary/secondary. @agentic @benchmark/core */ const populateCoverage = (formData, patient, coverage, role, relatedPersons)=>{
    const prefix = role === "primary" ? "primary" : "secondary";
    const payor = coverage.payor?.[0];
    if (payor?.reference) {
        formData[`${prefix}-insurance-provider`] = payor.reference.replace("Organization/", "");
        // Extract display name for the insurance provider
        if (payor.display) {
            formData[`${prefix}-insurance-provider-display`] = payor.display;
        }
    }
    // Extract InsurancePlan ID and display name from Coverage.class[type=plan]
    if (coverage.class && Array.isArray(coverage.class)) {
        const planClass = coverage.class.find((cls)=>cls.type?.coding?.[0]?.code === "plan");
        if (planClass?.value) {
            formData[`${prefix}-insurance-plan`] = planClass.value;
        }
        // Extract display name for the insurance plan
        if (planClass?.name) {
            formData[`${prefix}-insurance-plan-display`] = planClass.name;
        }
    }
    if (coverage.subscriberId) {
        formData[`${prefix}-subscriber-id`] = coverage.subscriberId;
    }
    const relationshipCode = coverage.relationship?.coding?.[0]?.code;
    if (relationshipCode) {
        formData[`${prefix}-relationship-to-subscriber`] = relationshipCode;
    }
    populateSubscriberInfo(formData, coverage.subscriber, prefix, relationshipCode, patient, relatedPersons);
};
/** Populates subscriber info from coverage. @agentic @benchmark/core */ const populateSubscriberInfo = (formData, subscriberRef, prefix, relationshipCode, patient, relatedPersons)=>{
    if (!subscriberRef?.reference) {
        return;
    }
    if (relationshipCode === "self" && patient) {
        formData[`${prefix}-subscriber-first-name`] = patient.name?.[0]?.given?.[0] || "";
        formData[`${prefix}-subscriber-last-name`] = patient.name?.[0]?.family || "";
        formData[`${prefix}-subscriber-dob`] = patient.birthDate || "";
        const phone = getActivePatientPhone(patient);
        if (phone) {
            formData[`${prefix}-subscriber-phone`] = phone;
        }
        return;
    }
    // For non-self relationships, try to find subscriber in RelatedPersons
    if (relatedPersons) {
        const subscriberRelatedPerson = relatedPersons.find((rp)=>`RelatedPerson/${rp.id}` === subscriberRef.reference);
        if (subscriberRelatedPerson) {
            fillSubscriberFromRelatedPerson(formData, subscriberRelatedPerson, prefix);
            return;
        }
    }
// Note: Patient subscribers (non-self) would need to be loaded separately
// This is a less common case and can be enhanced if needed
};
/** Fills subscriber fields from RelatedPerson. @agentic @benchmark/core */ const fillSubscriberFromRelatedPerson = (formData, subscriber, prefix)=>{
    const data = formData;
    const name = subscriber.name?.[0];
    if (name) {
        data[`${prefix}-subscriber-first-name`] = name.given?.[0] || "";
        data[`${prefix}-subscriber-last-name`] = name.family || "";
    }
    if (subscriber.birthDate) {
        data[`${prefix}-subscriber-dob`] = subscriber.birthDate;
    }
    const phone = subscriber.telecom?.find((t)=>t.system === "phone");
    if (phone?.value) {
        data[`${prefix}-subscriber-phone`] = phone.value;
    }
};
/** Populates emergency contact from RelatedPersons. @agentic @benchmark/core */ const populateEmergencyContact = (formData, relatedPersons)=>{
    const emergencyContact = relatedPersons.find((rp)=>rp.relationship?.[0]?.coding?.[0]?.code === "emergency");
    if (!emergencyContact) {
        return;
    }
    const name = emergencyContact.name?.[0];
    if (name) {
        formData["emergency-contact-first-name"] = name.given?.[0] || "";
        formData["emergency-contact-last-name"] = name.family || "";
    }
    const phone = emergencyContact.telecom?.find((t)=>t.system === "phone");
    if (phone?.value) {
        formData["emergency-contact-phone"] = phone.value;
    }
};
/** Populates patient contacts from Patient.contact. @agentic @benchmark/core */ const populatePatientContacts = (formData, patient)=>{
    if (!patient.contact || patient.contact.length === 0) {
        return;
    }
    formData["patient-contacts"] = patientContactsFromFhir(patient.contact);
};
/** Populates care team (LCSW, advocate) from CareTeams. @agentic @benchmark/core */ const populateCareTeam = (formData, patient, careTeams)=>{
    // Extract LCSW from generalPractitioner (new!)
    const lcswRef = getPatientBenchmarkLCSW(patient);
    if (lcswRef?.reference?.startsWith("PractitionerRole/")) {
        formData["benchmark-lcsw"] = lcswRef.reference.replace("PractitionerRole/", "");
        formData["benchmark-lcsw-display"] = lcswRef.display;
    }
    // NOTE: CareTeam-based LCSW extraction (existing code) should remain as fallback
    // for patients not yet backfilled. Remove after backfill is complete.
    const benchmarkCareTeam = careTeams.find((ct)=>ct.category?.[0]?.coding?.[0]?.code === "LA28865-6" || ct.name === "Benchmark Care Team");
    if (!benchmarkCareTeam?.participant) {
        return;
    }
    benchmarkCareTeam.participant.forEach((participant)=>{
        const code = participant.role?.[0]?.coding?.[0]?.code;
        const reference = participant.member?.reference;
        if (!code || !reference) {
            return;
        }
        // Only populate LCSW from CareTeam if not already set from generalPractitioner
        if (code === "224598009" && reference.startsWith("PractitionerRole/") && !formData["benchmark-lcsw"]) {
            formData["benchmark-lcsw"] = reference.replace("PractitionerRole/", "");
            formData["benchmark-lcsw-display"] = participant.member?.display;
        }
        if (code === "429577009" && reference.startsWith("PractitionerRole/")) {
            formData["benchmark-advocate"] = reference.replace("PractitionerRole/", "");
            formData["benchmark-advocate-display"] = participant.member?.display;
        }
    });
};
/** Populates ICD-10 codes from Conditions. @agentic @benchmark/core */ const populateConditions = (formData, conditions)=>{
    if (!conditions || conditions.length === 0) {
        return;
    }
    const icd10Codes = conditionsToICD10Codes(conditions);
    if (icd10Codes.length > 0) {
        formData["icd10-codes"] = icd10Codes;
    }
};
/** Populates CoCM consent from Patient extension. @agentic @benchmark/core */ const populateCocmConsent = (formData, patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return;
    const verified = cocmExtension.extension.find((sub)=>sub.url === "verified");
    if (verified?.valueBoolean !== undefined) {
        formData["cocm-consent-verified"] = verified.valueBoolean;
    }
    const date = cocmExtension.extension.find((sub)=>sub.url === "date");
    if (date?.valueDate) {
        formData["cocm-consent-date"] = date.valueDate;
    }
};
/** Populates summary from Patient extension. @agentic @benchmark/core */ const populateSummary = (formData, patient)=>{
    const summary = getPatientSummary(patient);
    if (summary) {
        formData.summary = summary.text;
    }
};

/** Returns true when gp-role-type code is current or legacy primary advocate. @agentic @benchmark/core */ const isBenchmarkPrimaryAdvocateCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Merges form data with existing Patient. Handles telecom, address, identifier lifecycle.
 * @param formData - Patient form data
 * @param existingPatient - Existing patient resource to update
 * @param authorReference - Optional PractitionerRole reference for summary extension author
 * @returns Updated Patient resource ready for update
 * @agentic @benchmark/core
 */ function patientUpdateFromForm(formData, existingPatient, authorReference) {
    // Start with existing patient
    const updatedPatient = {
        ...existingPatient,
        resourceType: "Patient"
    };
    // The form holds only what patientToForm can load, so rebuilding a field the
    // user left alone drops detail it cannot show (detailed race codes, coded
    // pronouns, contact email/address). Rewrite such fields only on a real change.
    const loaded = patientToForm({
        patient: existingPatient,
        coverages: [],
        relatedPersons: [],
        careTeams: [],
        conditions: []
    });
    const changed = (key)=>formData[key] !== undefined && !deepEquals(formData[key], loaded[key]);
    // Update name - direct update for current name
    if (formData["first-name"] || formData["last-name"]) {
        const existingOfficialName = updatedPatient.name?.find((n)=>n.use === "official") || updatedPatient.name?.[0] || {};
        const officialName = {
            ...existingOfficialName,
            use: "official",
            // The form edits only the first given name; keep middle names.
            given: formData["first-name"] ? [
                formData["first-name"],
                ...(existingOfficialName.given ?? []).slice(1)
            ] : existingOfficialName.given,
            family: formData["last-name"] || existingOfficialName.family
        };
        // Preserve any name entries that aren't the official or nickname
        const otherNames = (updatedPatient.name || []).filter((n)=>n.use !== "official" && n.use !== "nickname" && n !== existingOfficialName);
        updatedPatient.name = [
            officialName,
            ...otherNames
        ];
        if (formData["preferred-name"]?.trim()) {
            updatedPatient.name.push({
                use: "nickname",
                given: [
                    formData["preferred-name"].trim()
                ]
            });
        }
    }
    if (formData.dob) {
        updatedPatient.birthDate = formData.dob;
    }
    if (formData["gender-identity"]) {
        updatedPatient.gender = formData["gender-identity"];
    }
    // Update contact information - properly handle lifecycle management
    if (formData.phoneNumbers !== undefined || formData.email !== undefined) {
        const existingTelecom = updatedPatient.telecom || [];
        const newTelecom = [];
        // Handle phone numbers array
        if (formData.phoneNumbers !== undefined) {
            const phoneEntries = formData.phoneNumbers || [];
            if (phoneEntries.length > 0) {
                // Add all new phone numbers, keeping the stored detail of unchanged ones
                phoneEntries.forEach((entry)=>{
                    if (entry.value) {
                        const value = toE164US(entry.value); // Normalize to E.164
                        const unchanged = existingTelecom.find((t)=>t.system === "phone" && !t.period?.end && t.value === value);
                        newTelecom.push({
                            ...unchanged,
                            system: "phone",
                            value,
                            use: entry.use,
                            rank: entry.rank
                        });
                    }
                });
                // Mark any existing phones not in new list as ended
                existingTelecom.forEach((t)=>{
                    if (t.system === "phone" && !t.period?.end) {
                        const stillExists = phoneEntries.some((e)=>toE164US(e.value) === t.value);
                        if (!stillExists) {
                            newTelecom.push({
                                ...t,
                                period: {
                                    ...t.period,
                                    end: localCalendarDateTodayISO()
                                }
                            });
                        }
                    }
                });
            } else {
                // Mark all existing phones as ended if array is empty
                existingTelecom.forEach((t)=>{
                    if (t.system === "phone" && !t.period?.end) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            }
            // The form loads only current phones; keep the ended ones as history.
            newTelecom.push(...existingTelecom.filter((t)=>t.system === "phone" && t.period?.end));
        } else {
            // Keep existing phone entries unchanged if not in form data
            existingTelecom.forEach((t)=>{
                if (t.system === "phone") {
                    newTelecom.push(t);
                }
            });
        }
        // Handle email
        if (formData.email !== undefined) {
            if (formData.email) {
                // Add new/updated email
                const existingEmail = existingTelecom.find((t)=>t.system === "email" && !t.period?.end);
                const unchangedEmail = existingTelecom.find((t)=>t.system === "email" && !t.period?.end && t.value === formData.email);
                newTelecom.push({
                    ...unchangedEmail,
                    system: "email",
                    value: formData.email,
                    use: existingEmail?.use || "home"
                });
                // Mark any existing active email as old
                existingTelecom.forEach((t)=>{
                    if (t.system === "email" && !t.period?.end && t.value !== formData.email) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            } else {
                // Mark existing email as ended if user cleared the field
                existingTelecom.forEach((t)=>{
                    if (t.system === "email" && !t.period?.end) {
                        newTelecom.push({
                            ...t,
                            period: {
                                ...t.period,
                                end: localCalendarDateTodayISO()
                            }
                        });
                    }
                });
            }
            newTelecom.push(...existingTelecom.filter((t)=>t.system === "email" && t.period?.end));
        } else {
            // Keep existing email entries unchanged if not in form data
            existingTelecom.forEach((t)=>{
                if (t.system === "email") {
                    newTelecom.push(t);
                }
            });
        }
        // Preserve any other telecom entries not handled by the form
        const otherTelecom = existingTelecom.filter((t)=>t.system !== "phone" && t.system !== "email");
        updatedPatient.telecom = [
            ...newTelecom,
            ...otherTelecom
        ];
    }
    // Update address - properly handle lifecycle management
    if (formData.street !== undefined || formData.city !== undefined || formData.state !== undefined || formData.zip !== undefined) {
        const existingAddresses = updatedPatient.address || [];
        const newAddresses = [];
        // Check if any address fields have content
        const hasAddressData = formData.street || formData.city || formData.state || formData.zip;
        if (hasAddressData) {
            // Create new current address
            const currentAddress = existingAddresses.find((addr)=>addr.use !== "old" && (!addr.period || !addr.period.end)) || {};
            // Don't preserve text field when updating structured fields - it can conflict
            const { text, ...addressMetadata } = currentAddress;
            const addressLines = [];
            if (formData.street) addressLines.push(formData.street);
            if (formData["street-2"]) addressLines.push(formData["street-2"]);
            newAddresses.push({
                ...addressMetadata,
                use: "home",
                line: addressLines.length > 0 ? addressLines : undefined,
                city: formData.city || undefined,
                state: formData.state || undefined,
                postalCode: formData.zip || undefined,
                period: {
                    start: currentAddress.period?.start || localCalendarDateTodayISO()
                }
            });
            // Mark existing addresses as old if they're different from the new one.
            // Normalize empty strings and undefined to match consistently.
            const normalizeField = (val)=>val || "";
            existingAddresses.forEach((addr)=>{
                if (addr.use !== "old" && (!addr.period || !addr.period.end) && (normalizeField(addr.line?.[0]) !== normalizeField(formData.street) || normalizeField(addr.city) !== normalizeField(formData.city) || normalizeField(addr.state) !== normalizeField(formData.state) || normalizeField(addr.postalCode) !== normalizeField(formData.zip))) {
                    newAddresses.push({
                        ...addr,
                        use: "old",
                        period: {
                            ...addr.period,
                            end: localCalendarDateTodayISO()
                        }
                    });
                } else if (addr.use === "old" || addr.period?.end) {
                    // Preserve already-old addresses
                    newAddresses.push(addr);
                }
            });
        } else {
            // Mark all existing addresses as old if all fields are cleared
            existingAddresses.forEach((addr)=>{
                if (addr.use !== "old" && (!addr.period || !addr.period.end)) {
                    newAddresses.push({
                        ...addr,
                        use: "old",
                        period: {
                            ...addr.period,
                            end: localCalendarDateTodayISO()
                        }
                    });
                } else {
                    newAddresses.push(addr);
                }
            });
        }
        updatedPatient.address = newAddresses;
    }
    if (changed("preferred-language")) {
        updatedPatient.communication = formData["preferred-language"] ? [
            {
                language: {
                    coding: [
                        {
                            system: "urn:ietf:bcp:47",
                            code: formData["preferred-language"]
                        }
                    ]
                }
            }
        ] : [];
    }
    // Update MRN identifier (EMR system is stored as the MR identifier's `system` field)
    const existingIdentifiers = updatedPatient.identifier || [];
    const newIdentifiers = [];
    const legacyEmrFromMalformedEI = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "EI" && !id.system && !!id.value)?.value;
    // Handle MRN + EMR as a single MR identifier (system = EMR, value = MRN)
    if (formData.mrn !== undefined || formData.emr !== undefined) {
        const mrnIdentifier = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "MR");
        const newMrn = formData.mrn !== undefined ? formData.mrn : mrnIdentifier?.value;
        const newEmr = formData.emr !== undefined ? formData.emr : mrnIdentifier?.system || legacyEmrFromMalformedEI;
        if (newMrn) {
            newIdentifiers.push({
                ...mrnIdentifier,
                type: mrnIdentifier?.type ?? {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v2-0203",
                            code: "MR",
                            display: "Medical Record Number"
                        }
                    ]
                },
                system: newEmr || undefined,
                value: newMrn
            });
        }
    } else {
        // Preserve existing MRN
        const mrnIdentifier = existingIdentifiers.find((id)=>id.type?.coding?.[0]?.code === "MR");
        if (mrnIdentifier) {
            // Backfill legacy EMR data (stored on malformed EI.value) onto MR.system.
            if (!mrnIdentifier.system && legacyEmrFromMalformedEI) {
                newIdentifiers.push({
                    ...mrnIdentifier,
                    system: legacyEmrFromMalformedEI
                });
            } else {
                newIdentifiers.push(mrnIdentifier);
            }
        }
    }
    // Preserve other identifiers (e.g. Mirah patient-id / institute identifiers
    // still attached to legacy patients — historical reads power the patient
    // profile card's Mirah deep-link, even though the form no longer writes them).
    // Drop legacy EI (Employer Identifier) entries — the EMR system is now stored
    // on the MR identifier's `system` field, so standalone EI identifiers are defunct.
    existingIdentifiers.forEach((id)=>{
        const identifierTypeCode = id.type?.coding?.[0]?.code;
        const isMRN = identifierTypeCode === "MR";
        const isLegacyEMR = identifierTypeCode === "EI";
        if (!isMRN && !isLegacyEMR) {
            newIdentifiers.push(id);
        }
    });
    updatedPatient.identifier = newIdentifiers.length > 0 ? newIdentifiers : undefined;
    // Handle generalPractitioner updates (both PCP and primary advocate)
    const shouldUpdatePCP = formData["primary-care-provider"] !== undefined;
    const shouldUpdateLCSW = formData["benchmark-lcsw"] !== undefined;
    if (shouldUpdatePCP || shouldUpdateLCSW) {
        // Start with existing entries we want to preserve
        const existingEntries = updatedPatient.generalPractitioner || [];
        const newGeneralPractitioners = [];
        // Handle PCP
        if (shouldUpdatePCP) {
            if (formData["primary-care-provider"]) {
                newGeneralPractitioners.push({
                    reference: `PractitionerRole/${formData["primary-care-provider"]}`,
                    display: formData["primary-care-provider-display"] || "Practitioner",
                    extension: [
                        {
                            url: GP_ROLE_TYPE_EXTENSION,
                            valueCode: GP_ROLE_TYPES.REFERRING_PROVIDER
                        }
                    ]
                });
            }
        // If PCP is cleared (empty string), we don't add it - array will be empty or just LCSW
        } else {
            // Preserve existing PCP if not being updated
            const existingPCP = existingEntries.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER));
            if (existingPCP) {
                newGeneralPractitioners.push(existingPCP);
            }
        }
        // Handle Primary Advocate
        if (shouldUpdateLCSW) {
            if (formData["benchmark-lcsw"]) {
                newGeneralPractitioners.push({
                    reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
                    display: formData["benchmark-lcsw-display"] || "Primary Advocate",
                    extension: [
                        {
                            url: GP_ROLE_TYPE_EXTENSION,
                            valueCode: GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE
                        }
                    ]
                });
            }
        } else {
            // Preserve existing primary advocate if not being updated
            const existingLCSW = existingEntries.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateCode(ext.valueCode)));
            if (existingLCSW) {
                newGeneralPractitioners.push(existingLCSW);
            }
        }
        // Also preserve any entries without our extension (legacy data)
        const legacyEntries = existingEntries.filter((ref)=>!ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION));
        // Only preserve legacy if we're not explicitly setting PCP (legacy = PCP)
        if (!shouldUpdatePCP && legacyEntries.length > 0) {
            newGeneralPractitioners.push(...legacyEntries);
        }
        // Set generalPractitioner - use empty array if PCP was cleared and no LCSW
        if (shouldUpdatePCP && !formData["primary-care-provider"] && newGeneralPractitioners.length === 0) {
            // PCP was explicitly cleared - set to empty array
            updatedPatient.generalPractitioner = [];
        } else {
            updatedPatient.generalPractitioner = newGeneralPractitioners.length > 0 ? newGeneralPractitioners : undefined;
        }
        // Handle managingOrganization
        if (shouldUpdatePCP) {
            if (formData["primary-care-provider"] && formData["primary-care-provider-organization-ref"] && formData["primary-care-provider-organization-display"]) {
                // Set managingOrganization when PCP and org info are provided
                updatedPatient.managingOrganization = {
                    reference: formData["primary-care-provider-organization-ref"],
                    display: formData["primary-care-provider-organization-display"]
                };
            } else {
                // Only clear managingOrganization if there was an existing PCP being removed.
                // This prevents incorrectly clearing managingOrganization when:
                // - PCP was never set (form defaults to empty string)
                // - managingOrganization was set independently of PCP
                const hadExistingPCP = existingPatient.generalPractitioner?.some((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && ext.valueCode === GP_ROLE_TYPES.REFERRING_PROVIDER)) || (existingPatient.generalPractitioner?.length ?? 0) > 0;
                if (hadExistingPCP) {
                    updatedPatient.managingOrganization = undefined;
                }
            // Otherwise preserve existing managingOrganization from initial spread
            }
        }
    }
    // Update extensions (race, ethnicity, sexual orientation)
    const extensions = updatedPatient.extension || [];
    // Race extension
    if (changed("race")) {
        const raceDisplayMapping = {
            "1002-5": "American Indian or Alaska Native",
            "2028-9": "Asian",
            "2054-5": "Black or African American",
            "2076-8": "Native Hawaiian or Other Pacific Islander",
            "2106-3": "White",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const raceCode = formData.race;
        const raceExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race");
        if (raceCode) {
            const raceExtension = {
                url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race",
                extension: [
                    {
                        url: "ombCategory",
                        valueCoding: {
                            system: "urn:oid:2.16.840.1.113883.6.238",
                            code: raceCode,
                            display: raceDisplayMapping[raceCode] || "Unknown"
                        }
                    }
                ]
            };
            if (raceExtensionIndex === -1) {
                extensions.push(raceExtension);
            } else {
                extensions[raceExtensionIndex] = raceExtension;
            }
        } else if (raceExtensionIndex !== -1) {
            // Remove race extension if cleared
            extensions.splice(raceExtensionIndex, 1);
        }
    }
    // Ethnicity extension
    if (changed("ethnicity")) {
        const ethnicityDisplayMapping = {
            "2135-2": "Hispanic or Latino",
            "2186-5": "Not Hispanic or Latino",
            UNK: "Unknown",
            ASKU: "Asked but no answer"
        };
        const ethnicityCode = formData.ethnicity;
        const ethnicityExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity");
        if (ethnicityCode) {
            const ethnicityExtension = {
                url: "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity",
                extension: [
                    {
                        url: "ombCategory",
                        valueCoding: {
                            system: "urn:oid:2.16.840.1.113883.6.238",
                            code: ethnicityCode,
                            display: ethnicityDisplayMapping[ethnicityCode] || "Unknown"
                        }
                    }
                ]
            };
            if (ethnicityExtensionIndex === -1) {
                extensions.push(ethnicityExtension);
            } else {
                extensions[ethnicityExtensionIndex] = ethnicityExtension;
            }
        } else if (ethnicityExtensionIndex !== -1) {
            // Remove ethnicity extension if cleared
            extensions.splice(ethnicityExtensionIndex, 1);
        }
    }
    // Sexual orientation extension
    if (changed("sexual-orientation")) {
        const sexualOrientationExtensionIndex = extensions.findIndex((ext)=>ext.url === "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation");
        if (formData["sexual-orientation"] && formData["sexual-orientation"].length > 0) {
            const sexualOrientationExtension = {
                url: "http://hl7.org/fhir/StructureDefinition/patient-sexualOrientation",
                valueCodeableConcept: {
                    coding: formData["sexual-orientation"].map((orientation)=>({
                            code: orientation,
                            display: orientation
                        }))
                }
            };
            if (sexualOrientationExtensionIndex === -1) {
                extensions.push(sexualOrientationExtension);
            } else {
                extensions[sexualOrientationExtensionIndex] = sexualOrientationExtension;
            }
        } else if (sexualOrientationExtensionIndex !== -1) {
            // Remove sexual orientation extension if cleared
            extensions.splice(sexualOrientationExtensionIndex, 1);
        }
    }
    if (changed("pronouns")) {
        const pronounsExtensionIndex = extensions.findIndex((ext)=>ext.url === INDIVIDUAL_PRONOUNS_EXTENSION_URL);
        const trimmed = formData.pronouns?.trim() ?? "";
        if (trimmed) {
            const pronounsExtension = buildIndividualPronounsExtension(trimmed);
            if (pronounsExtensionIndex === -1) {
                extensions.push(pronounsExtension);
            } else {
                extensions[pronounsExtensionIndex] = pronounsExtension;
            }
        } else if (pronounsExtensionIndex !== -1) {
            extensions.splice(pronounsExtensionIndex, 1);
        }
    }
    // CoCM consent extension
    if (formData["cocm-consent-verified"] !== undefined || formData["cocm-consent-date"] !== undefined) {
        const cocmExtensionIndex = extensions.findIndex((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
        const cocmExtension = buildCoCMConsentExtension({
            verified: formData["cocm-consent-verified"],
            date: formData["cocm-consent-date"]
        });
        if (cocmExtension) {
            if (cocmExtensionIndex === -1) {
                extensions.push(cocmExtension);
            } else {
                extensions[cocmExtensionIndex] = cocmExtension;
            }
        } else if (cocmExtensionIndex !== -1) {
            extensions.splice(cocmExtensionIndex, 1);
        }
    }
    // Patient summary extension
    if (formData.summary !== undefined) {
        const trimmedSummary = formData.summary.trim();
        if (trimmedSummary && authorReference) {
            // Update or add summary extension when we have both content and author
            updatedPatient.extension = updatePatientSummaryExtension(extensions, {
                text: trimmedSummary,
                authorReference,
                generatedBy: "manual"
            });
        } else if (!trimmedSummary && authorReference) {
            // Remove summary extension if cleared (empty or whitespace only) AND we have authorReference
            // Only remove if we can safely update (have authorReference)
            updatedPatient.extension = removePatientSummaryExtension(extensions);
        } else {
            // authorReference is missing
            if (trimmedSummary) {
                // Summary provided but no authorReference - this should not happen in normal UI flow
                // Log warning to catch any code paths that don't pass authorReference
                console.warn("Patient summary update was provided but authorReference is missing. Summary will not be updated.", {
                    summaryLength: trimmedSummary.length
                });
            }
            // Preserve existing extension regardless of summary value
            // This prevents data loss when updatePatient.action.ts calls without authorReference
            // The form always initializes summary to "", so we can't distinguish "cleared" from "not touched"
            if (extensions.length > 0) {
                updatedPatient.extension = extensions;
            } else {
                updatedPatient.extension = undefined;
            }
        }
    } else {
        // Summary not in form data - preserve existing extensions
        if (extensions.length > 0) {
            updatedPatient.extension = extensions;
        } else {
            updatedPatient.extension = undefined;
        }
    }
    // Update veteran status in tags
    if (formData["veteran-status"] !== undefined) {
        const tags = updatedPatient.meta?.tag || [];
        const veteranTagIndex = tags.findIndex((tag)=>tag.system === "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus");
        if (formData["veteran-status"]) {
            const veteranTag = {
                system: "http://terminology.hl7.org/CodeSystem/v3-VeteranMilitaryStatus",
                code: "veteran",
                display: "Veteran"
            };
            if (veteranTagIndex === -1) {
                tags.push(veteranTag);
            } else {
                tags[veteranTagIndex] = veteranTag;
            }
        } else if (veteranTagIndex !== -1) {
            // Remove veteran tag if status is false
            tags.splice(veteranTagIndex, 1);
        }
        updatedPatient.meta = {
            ...updatedPatient.meta,
            tag: tags
        };
    }
    // Update engagement status tag
    if (formData["patient-engagement-status"] !== undefined) {
        const existingTags = updatedPatient.meta?.tag || [];
        // Filter out ALL engagement status tags before adding the new one.
        // The engagement modal writes V2-system tags while this form writes V1,
        // so filtering only the V1 system would leave a stale V2 tag behind and
        // accumulate multiple engagement tags. That breaks the member list filter,
        // which matches a patient if ANY tag matches the requested status.
        const filteredTags = existingTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM);
        const code = formData["patient-engagement-status"] || "";
        const option = PATIENT_ENGAGEMENT_STATUS_OPTIONS.find((s)=>s.code === code);
        const newTags = option ? [
            ...filteredTags,
            {
                system: PATIENT_TAG_MAPPINGS.engagementStatus.system,
                code: option.code,
                display: option.display
            }
        ] : filteredTags;
        updatedPatient.meta = {
            ...updatedPatient.meta || {},
            tag: newTags
        };
    }
    // The edit form submits contact phones as E.164 while stored ones may use
    // any format, so compare on normalized phones.
    const comparableContacts = (data)=>data["patient-contacts"]?.map((contact)=>({
                ...contact,
                phone: toE164US$1(contact.phone ?? "")
            }));
    // ponytail: editing any contact rebuilds every contact from the form, so
    // the untouched ones still lose email/address/gender. Upgrade: per-row merge.
    if (formData["patient-contacts"] !== undefined && !deepEquals(comparableContacts(formData), comparableContacts(loaded))) {
        if (formData["patient-contacts"] && formData["patient-contacts"].length > 0) {
            updatedPatient.contact = patientContactsToFhir(formData["patient-contacts"]);
        } else {
            // Clear contacts if none provided
            updatedPatient.contact = [];
        }
    }
    return updatedPatient;
}

const EMERGENCY_CONTACT_FIELDS = [
    "emergency-contact-first-name",
    "emergency-contact-last-name",
    "emergency-contact-phone"
];
/**
 * Builds a FHIR Bundle for atomic patient update (Patient, RelatedPersons, CareTeam).
 * @agentic @benchmark/core
 */ function buildUpdatePatientBundle(options) {
    const { formData, existingPatient, existingEmergencyContact, existingCareTeam, authorReference } = options;
    if (!existingPatient.id) {
        throw new Error("Existing patient must have an id");
    }
    const patientId = existingPatient.id;
    const patientReference = `Patient/${patientId}`;
    // Build transaction entries
    const entries = [];
    // Step 1: Update patient resource (always PUT)
    const updatedPatient = patientUpdateFromForm(formData, existingPatient, authorReference);
    entries.push({
        resource: updatedPatient,
        method: "PUT",
        url: `Patient/${patientId}`
    });
    // Step 2: Handle coverages (via updatePatientCoverage logic)
    // This is complex - we'll handle it in the workflow by calling updatePatientCoverage
    // For now, we'll skip coverage in the bundle and handle it separately
    // TODO(#982): Refactor updatePatientCoverage to support transaction bundles
    // Step 3: Handle emergency contact. The form shows only the contact's first
    // given name, family name, and first phone, so rebuilding an unchanged
    // contact drops the rest (address, email, other phones and relationships)
    // and deletes a contact that has neither name nor phone. Write only on a
    // real change; a field the caller omits keeps its loaded value.
    const loaded = patientToForm({
        patient: existingPatient,
        coverages: [],
        relatedPersons: existingEmergencyContact ? [
            existingEmergencyContact
        ] : [],
        careTeams: [],
        conditions: []
    });
    // The edit form submits the phone as E.164; the stored one may use any format.
    const comparable = (field, value = "")=>field === "emergency-contact-phone" ? toE164US$1(value) : value;
    const emergencyContactChanged = EMERGENCY_CONTACT_FIELDS.some((field)=>formData[field] !== undefined && comparable(field, formData[field]) !== comparable(field, loaded[field]));
    const [contactFirstName, contactLastName, contactPhone] = EMERGENCY_CONTACT_FIELDS.map((field)=>formData[field] ?? loaded[field]);
    const writeEmergencyContact = !existingEmergencyContact || emergencyContactChanged;
    if (writeEmergencyContact && (contactFirstName || contactLastName || contactPhone)) {
        const emergencyContact = {
            resourceType: "RelatedPerson",
            ...existingEmergencyContact?.id && {
                id: existingEmergencyContact.id
            },
            patient: {
                reference: patientReference,
                display: getPatientDisplayName(existingPatient)
            },
            relationship: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v2-0131",
                            code: "emergency",
                            display: "Emergency contact person"
                        }
                    ]
                }
            ],
            name: contactFirstName || contactLastName ? [
                {
                    given: contactFirstName ? [
                        contactFirstName
                    ] : undefined,
                    family: contactLastName
                }
            ] : undefined,
            telecom: contactPhone ? [
                {
                    system: "phone",
                    value: contactPhone
                }
            ] : undefined
        };
        entries.push({
            resource: emergencyContact,
            method: existingEmergencyContact?.id ? "PUT" : "POST",
            url: existingEmergencyContact?.id ? `RelatedPerson/${existingEmergencyContact.id}` : "RelatedPerson"
        });
    } else if (writeEmergencyContact && existingEmergencyContact?.id) {
        // Delete emergency contact if cleared
        entries.push({
            resource: existingEmergencyContact,
            method: "DELETE",
            url: `RelatedPerson/${existingEmergencyContact.id}`
        });
    }
    // Step 4: Handle CareTeam
    if (formData["benchmark-lcsw"] || formData["benchmark-advocate"]) {
        const participants = [];
        // Add patient as participant
        const patientName = updatedPatient.name?.[0];
        const patientDisplayName = patientName ? `${patientName.given?.[0] || ""} ${patientName.family || ""}`.trim() : getPatientDisplayName(updatedPatient);
        participants.push({
            role: [
                {
                    coding: [
                        {
                            system: "http://snomed.info/sct",
                            code: "116154003",
                            display: "Patient"
                        }
                    ]
                }
            ],
            member: {
                reference: patientReference,
                display: patientDisplayName
            }
        });
        // Add LCSW if specified
        if (formData["benchmark-lcsw"]) {
            participants.push({
                role: [
                    {
                        coding: [
                            {
                                system: "http://snomed.info/sct",
                                code: "224598009",
                                display: "Licensed Clinical Social Worker"
                            }
                        ]
                    }
                ],
                member: {
                    reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
                    display: formData["benchmark-lcsw-display"] || "LCSW"
                }
            });
        }
        // Add Advocate if specified
        if (formData["benchmark-advocate"]) {
            participants.push({
                role: [
                    {
                        coding: [
                            {
                                system: "http://snomed.info/sct",
                                code: "429577009",
                                display: "Patient advocate"
                            }
                        ]
                    }
                ],
                member: {
                    reference: `PractitionerRole/${formData["benchmark-advocate"]}`,
                    display: formData["benchmark-advocate-display"] || "Advocate"
                }
            });
        }
        const careTeam = {
            resourceType: "CareTeam",
            ...existingCareTeam?.id && {
                id: existingCareTeam.id
            },
            status: "active",
            name: "Benchmark Care Team",
            category: [
                {
                    coding: [
                        {
                            system: "http://loinc.org",
                            code: "LA28865-6",
                            display: "Longitudinal Care-Coordination"
                        }
                    ]
                }
            ],
            subject: {
                reference: patientReference,
                display: patientDisplayName
            },
            participant: participants
        };
        entries.push({
            resource: careTeam,
            method: existingCareTeam?.id ? "PUT" : "POST",
            url: existingCareTeam?.id ? `CareTeam/${existingCareTeam.id}` : "CareTeam"
        });
    }
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

/**
 * Converts form data to domain model for builder consumption.
 * @param formData - Encounter form data
 * @param existingTags - Existing tags from encounter (for filtering)
 * @returns Domain model ready for builder consumption
 * @agentic @benchmark/core
 */ function encounterFromForm(formData, existingTags = []) {
    const tags = [];
    const extensions = [];
    // Filter out encounter-specific tags from existing tags
    // (we'll rebuild them from form data)
    const encounterTagSystems = new Set([
        VALUE_SET.ENCOUNTER_INOUT,
        VALUE_SET.ENCOUNTER_METHOD,
        VALUE_SET.ENCOUNTER_TYPE,
        VALUE_SET.ENCOUNTER_BILLABLE,
        VALUE_SET.ENCOUNTER_SUCCESS
    ]);
    const preservedTags = existingTags.filter((tag)=>!encounterTagSystems.has(tag.system || ""));
    tags.push(...preservedTags);
    // Build encounter-specific tags from form data
    if (formData["encounter-inout"]) {
        const inoutCode = formData["encounter-inout"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_INOUT,
            code: inoutCode,
            display: inoutCode === "inbound" ? "Inbound" : inoutCode === "outbound" ? "Outbound" : "Unknown"
        });
    }
    if (formData["encounter-method"]) {
        const methodCode = formData["encounter-method"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_METHOD,
            code: methodCode,
            display: methodCode === "phone" ? "Phone" : methodCode === "text" ? "Text" : methodCode === "video" ? "Video" : methodCode === "in-person" ? "In-Person" : "Unknown"
        });
    }
    if (formData["encounter-type"]) {
        const typeCode = formData["encounter-type"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_TYPE,
            code: typeCode,
            display: getEncounterTypeDisplay(typeCode)
        });
    }
    // Entry-path provenance (encounter-origin). Callers set `_encounterOrigin`
    // only at create time; the system is deliberately NOT in the strip set above,
    // so an existing stamp is preserved on edits. The dedupe guard is defensive:
    // even if a caller passes the field on an edit, an already-stamped encounter
    // is never restamped.
    if (formData._encounterOrigin && !preservedTags.some((tag)=>tag.system === VALUE_SET.ENCOUNTER_ORIGIN)) {
        tags.push({
            system: VALUE_SET.ENCOUNTER_ORIGIN,
            code: formData._encounterOrigin,
            display: formData._encounterOrigin === "activity-completion" ? "Activity Completion" : "Free-Form"
        });
    }
    if (formData["encounter-billable"]) {
        const billableCode = formData["encounter-billable"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_BILLABLE,
            code: billableCode,
            display: billableCode === "billable" ? "Billable" : billableCode === "non-billable" ? "Non-Billable" : "Unknown"
        });
    }
    if (formData["encounter-success"]) {
        const successCode = formData["encounter-success"];
        tags.push({
            system: VALUE_SET.ENCOUNTER_SUCCESS,
            code: successCode,
            display: successCode === "pending" ? "Pending" : successCode === "successful" ? "Successful" : successCode === "unsuccessful" ? "Unsuccessful" : "Unknown"
        });
    }
    // Build extensions from form data
    if (formData["intake-scratch"]) {
        extensions.push({
            url: ENCOUNTER_SCRATCH_EXTENSION_URL,
            valueString: formData["intake-scratch"]
        });
    }
    // Per-section scratch on its own extension so it survives finalize (the
    // draft-form-state extension is stripped when the encounter is finished).
    // Only non-empty sections are persisted.
    const sectionScratchEntries = Object.entries(formData._sectionScratch ?? {}).filter(([, value])=>typeof value === "string" && value.trim().length > 0);
    if (sectionScratchEntries.length > 0) {
        extensions.push({
            url: ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL,
            valueString: JSON.stringify(Object.fromEntries(sectionScratchEntries))
        });
    }
    // Determine encounter class from method
    let encounterClass;
    if (formData["encounter-method"]) {
        const methodCode = formData["encounter-method"];
        encounterClass = methodCode === "in-person" ? {
            system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
            code: "FLD",
            display: "field"
        } : {
            system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
            code: "VR",
            display: "virtual"
        };
    }
    return {
        period: formData.period,
        tags,
        extensions,
        class: encounterClass,
        scribeCompositionId: formData._scribeCompositionId
    };
}

/**
 * Converts form data to FHIR QuestionnaireResponse resource.
 * @param formData - QuestionnaireResponse form data
 * @param patient - Patient resource (for building references)
 * @param practitioner - Practitioner resource (for building references)
 * @param practitionerRole - PractitionerRole resource (for building references)
 * @param encounter - Optional encounter resource (for building references)
 * @param existingResponse - Optional existing questionnaire response for updates
 * @param tempEncounterFullUrl - Optional temporary full URL for encounter in transactions
 * @returns QuestionnaireResponse resource ready for creation/update
 * @agentic @benchmark/core
 */ function questionnaireResponseFromForm(formData, patient, practitioner, practitionerRole, encounter, existingResponse, tempEncounterFullUrl) {
    let questionnaireResponse;
    if (existingResponse) {
        questionnaireResponse = {
            ...existingResponse
        };
    } else {
        questionnaireResponse = {
            resourceType: "QuestionnaireResponse",
            status: "in-progress",
            authored: new Date().toISOString()
        };
    }
    // Always (re)derive the deterministic actor references. On updates the
    // encounter bundle builder passes only a stub { resourceType, id } as
    // existingResponse, so a PUT built from it would otherwise drop
    // subject/author/source and wipe the patient/clinician linkage (which also
    // removes the resource from `patient`-scoped searches). `??=` preserves a
    // real existing value while populating a stub's missing one — mirroring how
    // the `encounter` reference below is always set.
    questionnaireResponse.subject ??= buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    questionnaireResponse.author ??= buildPractitionerRoleReference(practitionerRole.id || "", getPractitionerDisplayName(practitioner));
    questionnaireResponse.source ??= buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Update fields from form data
    // Note: Form data identifier type doesn't match FHIR exactly, but utils builder assigns directly
    // Match that behavior - the form data structure is compatible
    if (formData.identifier !== undefined) {
        questionnaireResponse.identifier = formData.identifier;
    }
    if (formData.basedOn !== undefined) {
        questionnaireResponse.basedOn = formData.basedOn;
    }
    if (formData.partOf !== undefined) {
        questionnaireResponse.partOf = formData.partOf;
    }
    if (formData.questionnaire !== undefined) {
        questionnaireResponse.questionnaire = formData.questionnaire;
    }
    if (formData.status !== undefined) {
        questionnaireResponse.status = formData.status;
    }
    if (formData.subject !== undefined) {
        questionnaireResponse.subject = formData.subject;
    }
    if (formData.encounter !== undefined) {
        questionnaireResponse.encounter = formData.encounter;
    }
    if (formData.authored !== undefined) {
        questionnaireResponse.authored = formData.authored;
    }
    if (formData.author !== undefined) {
        questionnaireResponse.author = formData.author;
    }
    if (formData.source !== undefined) {
        questionnaireResponse.source = formData.source;
    }
    // Encounter reference if provided (takes precedence over form data)
    if (encounter) {
        if (tempEncounterFullUrl) {
            // New encounter: the reference is a transaction-local placeholder that
            // Medplum rewrites to the real Encounter id on submit. Do NOT derive a
            // display from the placeholder uuid — Medplum rewrites the reference but
            // not the display, leaving a display whose id mismatches the resolved
            // reference. Omit the display; it carries no real id at build time.
            questionnaireResponse.encounter = {
                reference: tempEncounterFullUrl
            };
        } else if (encounter.id) {
            questionnaireResponse.encounter = buildReferenceWithDisplay("Encounter", encounter.id, `Encounter ${encounter.id}`);
        }
    }
    // Content
    if (formData.item !== undefined) {
        questionnaireResponse.item = formData.item;
    }
    return questionnaireResponse;
}

/**
 * Converts form data to Observation FHIR resource (BH-GoalProgress).
 * @param formData - Goal progress form data
 * @param goal - Goal resource (for focus reference)
 * @param patient - Patient resource (for subject reference)
 * @param practitionerRole - PractitionerRole resource (for performer)
 * @param existingObservation - Existing observation resource (for updates)
 * @returns Observation FHIR resource
 * @agentic @benchmark/core
 */ function goalProgressFromForm(formData, goal, patient, practitionerRole, existingObservation) {
    // Validate GAS score
    if (!isValidGASScore(formData.gasScore)) {
        throw new Error(`Invalid GAS score: ${formData.gasScore}. Must be between -2 and +2.`);
    }
    // Start with existing observation or create new one
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation"
    };
    // Status (default to final for goal progress)
    observation.status = "final";
    // Category - Survey (assessment)
    observation.category = [
        SURVEY_OBSERVATION_CATEGORY
    ];
    // Code - GAS assessment (what we're measuring)
    observation.code = {
        coding: [
            {
                system: GAS_ASSESSMENT_CODE.system,
                code: GAS_ASSESSMENT_CODE.code,
                display: GAS_ASSESSMENT_CODE.display
            }
        ],
        text: GAS_ASSESSMENT_CODE.display
    };
    // Value - GAS score as integer
    observation.valueInteger = formData.gasScore;
    // Interpretation - What the score means
    observation.interpretation = [
        {
            coding: [
                {
                    system: GAS_CODE_SYSTEM,
                    code: String(formData.gasScore),
                    display: getGASScoreDisplay(formData.gasScore)
                }
            ],
            text: getGASScoreDescription(formData.gasScore)
        }
    ];
    // Subject (required) - Patient reference WITH display name
    observation.subject = buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient));
    // Focus (required) - Goal reference WITH display name (goal description)
    const goalDisplay = goal.description?.text || `Goal/${goal.id}`;
    observation.focus = [
        buildReferenceWithDisplay("Goal", goal.id, goalDisplay)
    ];
    // Effective date time (required)
    observation.effectiveDateTime = formData.effectiveDateTime;
    // Issued timestamp (when observation was recorded)
    observation.issued = new Date().toISOString();
    // Performer (optional) - PractitionerRole reference WITH display name
    if (practitionerRole) {
        // Use practitioner display from role if available, otherwise fallback
        const displayName = practitionerRole.practitioner?.display || `PractitionerRole/${practitionerRole.id}`;
        observation.performer = [
            buildPractitionerRoleReference(practitionerRole.id, displayName)
        ];
    }
    // Note (optional)
    if (formData.note) {
        observation.note = [
            {
                text: formData.note
            }
        ];
    }
    return observation;
}

/**
 * Convert care-time form data into a FHIR Observation. Pure.
 * @agentic @benchmark/core
 */ const careTimeFromForm = (form, existingObservation)=>buildCareTimeObservation(form, existingObservation);

/** Gets questionnaire configuration by URL. @agentic @benchmark/core */ const getQuestionnaireConfig = (url)=>Object.values(STANDARD_QUESTIONNAIRES).find((config)=>config.url === url);
/** Gets enhanced questionnaire configuration with questions included. @agentic @benchmark/core */ const getEnhancedQuestionnaireConfig = (url)=>{
    const baseConfig = getQuestionnaireConfig(url);
    if (!baseConfig) return null;
    // Items come from the single canonical registry source (no per-call map).
    return {
        ...baseConfig,
        questions: getQuestionnaireItems(url)
    };
};
/** Gets set of non-scorable linkIds for a questionnaire. @agentic @benchmark/core */ const getNonScorableLinkIds = (questionnaireUrl)=>{
    const config = getEnhancedQuestionnaireConfig(questionnaireUrl);
    if (!config?.questions) return new Set();
    return new Set(config.questions.filter((q)=>q.scorable === false).map((q)=>q.linkId));
};
/** Filters responses to only include scorable items. @agentic @benchmark/core */ const filterScorableResponses = (responses, questionnaireUrl)=>{
    const nonScorableLinkIds = getNonScorableLinkIds(questionnaireUrl);
    return responses.filter((r)=>!nonScorableLinkIds.has(r.linkId));
};

// =============================================================================
// SPECIALIZED SCORING METHODS
// =============================================================================
/**
 * Calculates WPAI score based on percentage-based impairment (not simple summation).
 * @agentic @benchmark/core
 */ const calculateWPAIScore = (responses)=>{
    // Get individual response values
    const getResponse = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value ?? 0;
    const employed = getResponse("wpai-1"); // 0 = No, 1 = Yes
    const hoursHealthMissed = getResponse("wpai-2"); // 0-6 scale (0=0hrs, 6=40+ hrs)
    const hoursWorked = getResponse("wpai-4"); // 0-5 scale (0=0hrs, 5=40+ hrs)
    const workProductivity = getResponse("wpai-5"); // 0-10 scale
    const activityImpairment = getResponse("wpai-6"); // 0-10 scale
    // Convert scales to approximate hours for calculation
    const healthMissedHours = [
        0,
        4.5,
        12.5,
        20.5,
        28.5,
        36.5,
        45
    ][hoursHealthMissed] || 0;
    const actualWorkedHours = [
        0,
        5.5,
        15.5,
        25.5,
        35.5,
        45
    ][hoursWorked] || 0;
    let maxImpairment = 0;
    // Calculate Activity Impairment (always calculated)
    const activityImpairmentPercent = activityImpairment / 10 * 100;
    maxImpairment = Math.max(maxImpairment, activityImpairmentPercent);
    // Calculate work-related metrics only if employed
    if (employed === 1) {
        // Calculate Absenteeism (percentage of work time missed due to health)
        let absenteeismPercent = 0;
        if (healthMissedHours > 0 && healthMissedHours + actualWorkedHours > 0) {
            absenteeismPercent = healthMissedHours / (healthMissedHours + actualWorkedHours) * 100;
        }
        // Calculate Presenteeism (impairment while working) only if actually worked
        const presenteeismPercent = actualWorkedHours > 0 ? workProductivity / 10 * 100 : 0;
        // Calculate Overall Work Productivity Loss
        const workProductivityLossPercent = absenteeismPercent + presenteeismPercent * (1 - absenteeismPercent / 100);
        // Use the highest impairment percentage as the overall score
        if (presenteeismPercent > maxImpairment) {
            maxImpairment = presenteeismPercent;
        }
        if (workProductivityLossPercent > maxImpairment) {
            maxImpairment = workProductivityLossPercent;
        }
    }
    // Find the appropriate range for the maximum impairment percentage. Absenteeism
    // and overall work-productivity loss are fractional (derived from the WPAI hour
    // approximations), so match the range against the same rounded score we report:
    // matching the raw fraction against integer band boundaries leaves gaps
    // (e.g. 50.5 falls between the 26–50 and 51–75 bands and would misreport as
    // "Unable to determine" for a valid response).
    const roundedImpairment = Math.round(maxImpairment);
    const ranges = [
        {
            min: 0,
            max: 25,
            level: "minimal",
            interpretation: "Minimal impairment"
        },
        {
            min: 26,
            max: 50,
            level: "mild",
            interpretation: "Mild impairment"
        },
        {
            min: 51,
            max: 75,
            level: "moderate",
            interpretation: "Moderate impairment"
        },
        {
            min: 76,
            max: 100,
            level: "severe",
            interpretation: "Severe impairment"
        }
    ];
    const range = ranges.find((r)=>roundedImpairment >= r.min && roundedImpairment <= r.max);
    return {
        score: roundedImpairment,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level || "unknown",
        riskColor: range?.level === "severe" ? "red" : range?.level === "moderate" ? "orange" : range?.level === "mild" ? "yellow" : "gray"
    };
};
/**
 * Calculates C-SSRS score from ordinal severity levels (highest level reached).
 * @agentic @benchmark/core
 */ const calculateCSSRSScore = (responses, scoring)=>{
    // C-SSRS certified branching: Q1 (wish to be dead) and Q2 (active thoughts)
    // are always asked. Q3-Q5 are asked ONLY if Q2 is Yes. `gateFor` hides Q3-Q5
    // in the UI when Q2 is No, but stale answers can still survive on the saved
    // response, so scoring must enforce the gate itself — never derive a level
    // from Q3-Q5 unless Q2 is positive.
    const answerFor = (linkId)=>responses.find((r)=>r.linkId === linkId)?.value || 0;
    let highestLevel = 0;
    if (answerFor("c-ssrs-ideation-1") > 0) {
        highestLevel = 1;
    }
    const activeIdeation = answerFor("c-ssrs-ideation-2") > 0;
    if (activeIdeation) {
        highestLevel = 2;
        // Q3-Q5 only count when Q2 (active ideation) is positive.
        if (answerFor("c-ssrs-ideation-3") > 0) {
            highestLevel = 3;
        }
        if (answerFor("c-ssrs-ideation-4") > 0) {
            highestLevel = 4;
        }
        if (answerFor("c-ssrs-ideation-5") > 0) {
            highestLevel = 5;
        }
    }
    // Suicidal behavior raises risk regardless of ideation level, per the
    // certified Columbia triage: lifetime behavior floors at Moderate (level 3),
    // behavior within the past 3 months floors at High (level 4).
    const hasLifetimeBehavior = answerFor("c-ssrs-behavior-1") > 0;
    if (hasLifetimeBehavior && highestLevel < 3) {
        highestLevel = 3;
    }
    // Recency is only asked after lifetime behavior is Yes (certified branching).
    // A recency answer without positive lifetime behavior is stale/contradictory
    // data and must NOT raise risk.
    if (hasLifetimeBehavior && answerFor("c-ssrs-behavior-recency") > 0 && highestLevel < 4) {
        highestLevel = 4;
    }
    const severityLevels = scoring.severityLevels;
    const severityLevel = severityLevels?.find((level)=>level.level === highestLevel);
    const riskLevelValue = severityLevel?.riskLevel?.value || (highestLevel === 0 ? "minimal" : "unknown");
    return {
        score: highestLevel,
        interpretation: severityLevel?.interpretation || (highestLevel === 0 ? "No suicidal ideation" : "Unable to determine"),
        riskLevel: riskLevelValue,
        // Tier-appropriate color (D5): never signal "green/normal" for active
        // suicidal ideation. Overrides the shared risk-level palette where its
        // color (e.g. "low" → green) would under-signal C-SSRS urgency.
        riskColor: cssrsRiskColor(riskLevelValue)
    };
};
/**
 * Maps a C-SSRS risk-tier value to a display color reflecting triage urgency.
 * @agentic @benchmark/core
 */ const cssrsRiskColor = (riskLevelValue)=>{
    switch(riskLevelValue){
        case "minimal":
            return "green";
        case "low":
            return "yellow";
        case "moderate":
            return "orange";
        case "high":
            return "red";
        default:
            return "gray";
    }
};
/**
 * Calculates WSAS total with work-item N/A rescale (PRD §8.8).
 * @agentic @benchmark/core
 */ const calculateWSASScore = (responses, scoring)=>{
    const WORK_LINK_ID = "wsas-1";
    const workValue = responses.find((r)=>r.linkId === WORK_LINK_ID)?.value;
    const workNotApplicable = workValue === WSAS_WORK_NOT_APPLICABLE_VALUE;
    let sum = 0;
    let scoredCount = 0;
    for (const linkId of scoring.linkIds){
        const value = responses.find((r)=>r.linkId === linkId)?.value;
        if (value === undefined || value < 0) {
            continue;
        }
        if (linkId === WORK_LINK_ID && workNotApplicable) {
            continue;
        }
        sum += value;
        scoredCount += 1;
    }
    let totalScore = sum;
    if (workNotApplicable && scoredCount === 4) {
        totalScore = Math.round(sum * (5 / 4));
    }
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates ASRS score: sum Part A items (0-4 each), cutpoint ≥14.
 * @agentic @benchmark/core
 */ const calculateASRSScore = (responses, scoring)=>{
    const partALinkIds = scoring.partA?.linkIds ?? [];
    let partASum = 0;
    for (const linkId of partALinkIds){
        const response = responses.find((r)=>r.linkId === linkId);
        partASum += response?.value ?? 0;
    }
    const range = scoring.ranges?.find((r)=>partASum >= r.min && partASum <= r.max);
    return {
        score: partASum,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};
/**
 * Calculates questionnaire score from responses and scoring config; handles WPAI, C-SSRS, ASRS, summation.
 * @agentic @benchmark/core
 */ const calculateQuestionnaireScore = (responses, scoring)=>{
    // Strongly typed validation using type guard
    if (!isValidQuestionnaireScoring(scoring)) {
        console.error("Invalid scoring configuration: failed validation", {
            scoring
        });
        return {
            score: 0,
            interpretation: "Unable to determine: Invalid scoring configuration.",
            riskLevel: "unknown",
            riskColor: "gray"
        };
    }
    // Handle special scoring methods
    if (scoring.method === "wpai") {
        return calculateWPAIScore(responses);
    }
    if (scoring.method === "c-ssrs-ordinal") {
        return calculateCSSRSScore(responses, scoring);
    }
    if (scoring.method === "asrs-threshold") {
        return calculateASRSScore(responses, scoring);
    }
    if (scoring.method === "wsas-summation") {
        return calculateWSASScore(responses, scoring);
    }
    // Default summation method
    // NOTE: Callers are responsible for filtering out non-scorable responses
    // (items with scorable: false) before calling this function
    const totalScore = responses.reduce((sum, response)=>{
        return sum + (response.value || 0);
    }, 0);
    // Find the appropriate range for the score
    const range = scoring.ranges?.find((r)=>totalScore >= r.min && totalScore <= r.max);
    return {
        score: totalScore,
        interpretation: range?.interpretation || "Unable to determine",
        riskLevel: range?.level?.value || "unknown",
        riskColor: range?.level?.color || "gray"
    };
};

const flattenItems = (items)=>items.flatMap((item)=>item.type === "group" && item.item ? [
            item,
            ...flattenItems(item.item)
        ] : [
            item
        ]);
const answered = (answer)=>answer.value !== undefined || answer.valueString !== undefined && answer.valueString.length > 0;
/**
 * Evaluate completion against the current canonical questionnaire definition.
 * Gated-out required items are excluded when their gate parent is answered 0.
 *
 * @agentic @benchmark/core
 */ const evaluateQuestionnaireCompletion = (questionnaireUrl, answers, options = {})=>{
    const items = getQuestionnaireItems(questionnaireUrl);
    if (!items) {
        return {
            supported: false,
            reason: "unknown-questionnaire"
        };
    }
    const flattened = flattenItems(items);
    const itemByLinkId = new Map(flattened.map((item)=>[
            item.linkId,
            item
        ]));
    for (const answer of answers){
        if (!itemByLinkId.has(answer.linkId)) {
            return {
                supported: false,
                reason: "unknown-link-id"
            };
        }
        if (!answered(answer) && answer.coding !== undefined) {
            return {
                supported: false,
                reason: "unsupported-answer"
            };
        }
    }
    const valueByLinkId = new Map(answers.map((answer)=>[
            answer.linkId,
            answer.value
        ]));
    const gateForByLinkId = new Map();
    for (const item of flattened){
        if (item.gateFor) {
            gateForByLinkId.set(item.linkId, item.gateFor);
        }
    }
    const hidden = new Set();
    for (const item of flattened){
        if (!item.gateFor || valueByLinkId.get(item.linkId) !== 0) {
            continue;
        }
        const queue = [
            ...item.gateFor
        ];
        while(queue.length > 0){
            const linkId = queue.shift();
            if (hidden.has(linkId)) {
                continue;
            }
            hidden.add(linkId);
            queue.push(...gateForByLinkId.get(linkId) ?? []);
        }
    }
    const answerByLinkId = new Map(answers.filter(answered).map((answer)=>[
            answer.linkId,
            answer
        ]));
    const missingRequired = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId) && !answerByLinkId.has(item.linkId)).map((item)=>item.linkId);
    const activeRequiredCount = flattened.filter((item)=>item.type !== "group" && item.required !== false && !hidden.has(item.linkId)).length;
    return {
        supported: true,
        complete: missingRequired.length === 0 && (activeRequiredCount > 0 || options.declaredComplete === true),
        missingRequired
    };
};

/** `meta.tag` system that marks a QR as part of an adaptive-screener sitting. */ const ADAPTIVE_SCREENER_SESSION_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/adaptive-screener-session";
/** Extension url carrying why the section was asked (F7). */ const ADAPTIVE_SCREENER_REASON_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-reason";
/** Extension url carrying whether the sitting was the patient's baseline. */ const ADAPTIVE_SCREENER_BASELINE_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-baseline";
/** Extension url carrying how the section settled — answered vs a decline kind (D8). */ const ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-settlement";
/**
 * Extension url carrying the link ids the patient explicitly skipped (asked and
 * not answered — PRD §8.4). Skips are deliberately persisted as an extension,
 * never as answerless `item` entries: item presence would read as "content" to
 * cross-visit memory (flipping a later administration to follow-up against
 * D12) and would complicate every raw-item consumer, while an extension is
 * invisible to memory, completion, and scoring.
 */ const ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-skipped-items";
/**
 * Extension url freezing the administration's composition inputs at the first
 * persisted write: which short-check sections were opened-in-full by memory
 * and whether the rating anchor was established. Without this, every request
 * re-derives composition from the patient's *current* prior responses, so a
 * prior sitting completing mid-administration would flip the composed section
 * list (and kind) between two saves of the same sitting. The marker makes the
 * first write's derivation authoritative for the administration's lifetime.
 */ const ADAPTIVE_SCREENER_COMPOSITION_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-composition";
/** Extension urls capturing the suicide-risk acknowledgement author + time (D13). */ const ADAPTIVE_SCREENER_ACK_AT_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-at";
const ADAPTIVE_SCREENER_ACK_BY_EXTENSION = "https://benchmarkhealth.org/fhir/StructureDefinition/adaptive-screener-acknowledged-by";
/**
 * Return a copy of the QR tagged as part of an adaptive-screener sitting: a
 * `meta.tag` with the shared session id, plus extensions for the baseline flag
 * and the reason the section was asked. Existing tags/extensions are preserved.
 *
 * @agentic @benchmark/core
 */ const tagAdaptiveScreenerResponse = (qr, marker)=>{
    const existingTags = qr.meta?.tag ?? [];
    const managedExtensionUrls = new Set([
        ADAPTIVE_SCREENER_REASON_EXTENSION,
        ADAPTIVE_SCREENER_BASELINE_EXTENSION,
        ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION,
        ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION,
        ADAPTIVE_SCREENER_COMPOSITION_EXTENSION,
        ADAPTIVE_SCREENER_ACK_AT_EXTENSION,
        ADAPTIVE_SCREENER_ACK_BY_EXTENSION
    ]);
    const existingExtensions = (qr.extension ?? []).filter((ext)=>!managedExtensionUrls.has(ext.url));
    return {
        ...qr,
        meta: {
            ...qr.meta,
            tag: [
                ...existingTags.filter((tag)=>tag.system !== ADAPTIVE_SCREENER_SESSION_SYSTEM),
                {
                    system: ADAPTIVE_SCREENER_SESSION_SYSTEM,
                    code: marker.sessionId
                }
            ]
        },
        extension: [
            ...existingExtensions,
            {
                url: ADAPTIVE_SCREENER_BASELINE_EXTENSION,
                valueBoolean: marker.baseline
            },
            ...marker.reasonCode ? [
                {
                    url: ADAPTIVE_SCREENER_REASON_EXTENSION,
                    valueCode: marker.reasonCode
                }
            ] : [],
            ...marker.settlement ? [
                {
                    url: ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION,
                    valueCode: marker.settlement
                }
            ] : [],
            ...marker.skippedLinkIds && marker.skippedLinkIds.length > 0 ? [
                {
                    url: ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION,
                    // Link ids never contain whitespace, so a space-joined list keeps
                    // the extension a single human-readable valueString.
                    valueString: [
                        ...marker.skippedLinkIds
                    ].join(" ")
                }
            ] : [],
            ...marker.composition ? [
                {
                    url: ADAPTIVE_SCREENER_COMPOSITION_EXTENSION,
                    valueString: JSON.stringify({
                        openedInFullBefore: [
                            ...marker.composition.openedInFullBefore
                        ],
                        ratingAnchorEstablished: marker.composition.ratingAnchorEstablished
                    })
                }
            ] : [],
            ...marker.acknowledgedAt ? [
                {
                    url: ADAPTIVE_SCREENER_ACK_AT_EXTENSION,
                    valueDateTime: marker.acknowledgedAt
                }
            ] : [],
            ...marker.acknowledgedBy ? [
                {
                    url: ADAPTIVE_SCREENER_ACK_BY_EXTENSION,
                    valueString: marker.acknowledgedBy
                }
            ] : []
        ]
    };
};
/**
 * Read the adaptive-screener marker off a QR, or null when the QR was not part
 * of an adaptive-screener sitting.
 *
 * @agentic @benchmark/core
 */ /**
 * Parse the frozen-composition extension fail-soft: a corrupt or unexpected
 * payload yields `undefined` (the caller re-derives from prior responses)
 * rather than throwing on a persisted-value read.
 */ const parseComposition = (raw)=>{
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return undefined;
        const candidate = parsed;
        if (!Array.isArray(candidate.openedInFullBefore) || !candidate.openedInFullBefore.every((key)=>typeof key === "string") || typeof candidate.ratingAnchorEstablished !== "boolean") {
            return undefined;
        }
        return {
            openedInFullBefore: candidate.openedInFullBefore,
            ratingAnchorEstablished: candidate.ratingAnchorEstablished
        };
    } catch  {
        return undefined;
    }
};
const readAdaptiveScreenerMarker = (qr)=>{
    const sessionTag = (qr.meta?.tag ?? []).find((tag)=>tag.system === ADAPTIVE_SCREENER_SESSION_SYSTEM);
    if (!sessionTag?.code) {
        return null;
    }
    const findExt = (url)=>(qr.extension ?? []).find((ext)=>ext.url === url);
    const skippedRaw = findExt(ADAPTIVE_SCREENER_SKIPPED_ITEMS_EXTENSION)?.valueString;
    const skippedLinkIds = skippedRaw ? skippedRaw.split(/\s+/).filter((id)=>id.length > 0) : undefined;
    const composition = parseComposition(findExt(ADAPTIVE_SCREENER_COMPOSITION_EXTENSION)?.valueString);
    return {
        sessionId: sessionTag.code,
        baseline: findExt(ADAPTIVE_SCREENER_BASELINE_EXTENSION)?.valueBoolean === true,
        reasonCode: findExt(ADAPTIVE_SCREENER_REASON_EXTENSION)?.valueCode,
        settlement: findExt(ADAPTIVE_SCREENER_SETTLEMENT_EXTENSION)?.valueCode,
        ...skippedLinkIds && skippedLinkIds.length > 0 ? {
            skippedLinkIds
        } : {},
        ...composition ? {
            composition
        } : {},
        acknowledgedAt: findExt(ADAPTIVE_SCREENER_ACK_AT_EXTENSION)?.valueDateTime,
        acknowledgedBy: findExt(ADAPTIVE_SCREENER_ACK_BY_EXTENSION)?.valueString
    };
};
new Map(LIKERT_FREQUENCY_OPTIONS.map((option)=>[
        option.coding.code,
        option.value
    ]));

/**
 * LOINC codes for questionnaire panels/forms (used for search/discovery and canonical URL construction)
 * These identify the questionnaire definition itself, NOT the scores/observations
 *
 * NOTE: These are used for:
 * 1. Searching/discovering questionnaires in FHIR servers
 * 2. Constructing canonical URLs for Questionnaire.url
 * 3. Initial questionnaire creation
 *
 * IMPORTANT: Once a questionnaire is fetched, prefer extracting LOINC codes
 * dynamically from the actual FHIR resource rather than using these static values.
 *
 * Only includes assessments with official LOINC questionnaire panel codes.
 * Questionnaires without official LOINC panel codes use organizational URLs.
 *
 * IMPORTANT: These codes should ONLY be used for Questionnaire and QuestionnaireResponse
 * resources. For total score observations, use OBSERVATION_LOINC_CODES from
 * observation.ts which contains the score codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const QUESTIONNAIRE_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale - LOINC 62750-5 ✅ VALIDATED */ ASRS: "62750-5",
    /** AUDIT-C Alcohol Screening Panel - LOINC 72109-2 ✅ VALIDATED */ AUDIT_C: "72109-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale - LOINC 93245-9 ✅ VALIDATED */ C_SSRS: "93245-9",
    /** GAD-7 Anxiety Assessment Panel - LOINC 69737-5 ✅ VALIDATED */ GAD7: "69737-5",
    /** PC-PTSD-5 PTSD Screening Panel - LOINC 102010-6 ✅ VALIDATED */ PC_PTSD_5: "102010-6",
    /** PHQ-9 Depression Assessment Panel - LOINC 44249-1 ✅ VALIDATED */ PHQ9: "44249-1",
    /** PHQ-9A (Adolescent) Depression Assessment Panel - LOINC 89206-7 ✅ VALIDATED */ PHQ9A: "89206-7",
    /** PHQ-4 Combined Depression/Anxiety Panel - LOINC 69724-3 ✅ VALIDATED */ PHQ4: "69724-3",
    /** Health Leads Screening Panel (SDOH) - LOINC 99549-8 ✅ VALIDATED */ SDOH: "99549-8"
};
/**
 * Common questionnaire URLs used in FHIR Questionnaire.url
 * Uses official LOINC canonical URLs where available, organizational URLs otherwise
 */ const QUESTIONNAIRE_URLS = {
    ASRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.ASRS}`,
    C_SSRS: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.C_SSRS}`,
    GAD7: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.GAD7}`,
    OCI_4: "https://benchmarkhealth.org/fhir/Questionnaire/oci-4",
    PC_PTSD_5: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PC_PTSD_5}`,
    PSC_17: "https://benchmarkhealth.org/fhir/Questionnaire/psc-17",
    PHQ9: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9}`,
    PHQ9A: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ9A}`,
    PHQ4: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.PHQ4}`,
    SBIRT_ED: "https://benchmarkhealth.org/fhir/Questionnaire/sbirt-ed",
    SAFETY_PLAN: "https://benchmarkhealth.org/fhir/Questionnaire/safety-plan",
    TAPS_1: "https://benchmarkhealth.org/fhir/Questionnaire/taps-1",
    TAPS_2: "https://benchmarkhealth.org/fhir/Questionnaire/taps-2",
    WPAI: "https://benchmarkhealth.org/fhir/Questionnaire/wpai-functional",
    WHO5: "https://benchmarkhealth.org/fhir/Questionnaire/who-5",
    SDOH: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.SDOH}`,
    MIRAH_COMPLEXITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-complexity",
    MIRAH_SEVERITY: "https://benchmarkhealth.org/fhir/Questionnaire/mirah-severity",
    NPS: "https://benchmarkhealth.org/fhir/Questionnaire/nps-member-feedback",
    DRUG_USE: "https://benchmarkhealth.org/fhir/Questionnaire/drug-use-screen",
    GAD2: "https://benchmarkhealth.org/fhir/Questionnaire/gad-2",
    AUDIT_C: `http://loinc.org/q/${QUESTIONNAIRE_LOINC_CODES.AUDIT_C}`,
    UCLA3: "https://benchmarkhealth.org/fhir/Questionnaire/ucla-3",
    PGI_S: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-s",
    PGI_C: "https://benchmarkhealth.org/fhir/Questionnaire/pgi-c",
    WSAS: "https://benchmarkhealth.org/fhir/Questionnaire/wsas"
};

/**
 * The full nine-section order (§8.2). Suicide-risk (3) and safety-plan (4) are
 * trigger-only and excluded from base composition; they are exported separately.
 */ const SECTION_SPECS = [
    {
        key: "patient-rating",
        order: 1,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "PGI_S"
        ]
    },
    {
        key: "depression",
        order: 2,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "PHQ9"
        ]
    },
    {
        key: "anxiety",
        order: 5,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "GAD2",
            "GAD7"
        ]
    },
    {
        key: "loneliness",
        order: 6,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "UCLA3"
        ]
    },
    {
        key: "alcohol",
        order: 7,
        baseGating: "short-check",
        everyoneAsked: false,
        instruments: [
            "AUDIT_C"
        ]
    },
    {
        key: "drug-use",
        order: 8,
        baseGating: "categorical",
        everyoneAsked: true,
        instruments: [
            "DRUG_USE"
        ]
    },
    {
        key: "functioning",
        order: 9,
        baseGating: "always",
        everyoneAsked: true,
        instruments: [
            "WSAS"
        ]
    }
];
/**
 * The trigger-added sections (PRD §8.4 / D9), in order, inserted live by the UI
 * directly after depression on a positive self-harm answer — never carried by
 * memory (suicide-risk is re-derived each visit, E6).
 */ const TRIGGER_ADDED_SECTIONS = [
    {
        key: "suicide-risk",
        order: 3,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now by the depression self-harm answer",
        instruments: [
            "C_SSRS"
        ]
    },
    {
        key: "safety-plan",
        order: 4,
        gating: "always",
        reasonCode: "triggered",
        reason: "Triggered now — always paired with suicide risk",
        instruments: [
            "SAFETY_PLAN"
        ]
    }
];
/**
 * Every instrument that can appear in an adaptive-screener sitting, in actual
 * administration order. The list is derived from the section definitions so
 * timeline/read-back surfaces cannot drift from screener composition. PGI-S
 * and PGI-C occupy the same final section and are mutually exclusive per visit.
 */ const ADAPTIVE_SCREENER_INSTRUMENT_ORDER = [
    ...SECTION_SPECS,
    ...TRIGGER_ADDED_SECTIONS
].sort((a, b)=>a.order - b.order).flatMap((section)=>section.key === "patient-rating" ? [
        "PGI_S",
        "PGI_C"
    ] : section.instruments);
/** Canonical URLs matching `ADAPTIVE_SCREENER_INSTRUMENT_ORDER`. */ const ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER = ADAPTIVE_SCREENER_INSTRUMENT_ORDER.map((instrument)=>QUESTIONNAIRE_URLS[instrument]);
new Map(ADAPTIVE_SCREENER_QUESTIONNAIRE_ORDER.map((url, index)=>[
        url,
        index
    ]));
const REASON_TEXT = {
    everyone: "Asked of everyone",
    "short-check": "Short check first — opens the full set only if positive",
    "opened-at-baseline": "Opened at an earlier visit — asked in full",
    triggered: "Triggered now"
};
/**
 * Reconstruct the original section list for draft resume/finalized edit.
 *
 * Session reason maps are broader than actual administration (they predeclare
 * GAD-7 and trigger reasons), so responses/settlements and the current PHQ-9
 * trigger decide which conditional instruments really belonged to the sitting.
 *
 * @agentic @benchmark/core
 */ function composePersistedAdaptiveSession(ctx) {
    const urlForKey = (key)=>QUESTIONNAIRE_URLS[key];
    const sections = [];
    for (const spec of SECTION_SPECS){
        let instruments = spec.instruments;
        let reasonCode;
        let gating = spec.baseGating;
        if (spec.key === "patient-rating") {
            const usePgic = ctx.reasons[QUESTIONNAIRE_URLS.PGI_C] !== undefined || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.PGI_C);
            instruments = usePgic ? [
                "PGI_C"
            ] : [
                "PGI_S"
            ];
        }
        if (spec.key === "anxiety") {
            const gad7Reason = ctx.reasons[QUESTIONNAIRE_URLS.GAD7];
            const fullByMemory = gad7Reason === "opened-at-baseline";
            if (fullByMemory) {
                gating = "full-by-memory";
                instruments = [
                    "GAD7"
                ];
                reasonCode = gad7Reason;
            } else {
                instruments = [
                    "GAD2",
                    "GAD7"
                ];
                reasonCode = ctx.reasons[QUESTIONNAIRE_URLS.GAD2] ?? gad7Reason;
            }
        }
        reasonCode ??= instruments.map(urlForKey).map((url)=>ctx.reasons[url]).find((reason)=>reason !== undefined);
        const represented = reasonCode !== undefined || instruments.map(urlForKey).some((url)=>ctx.administeredUrls.has(url));
        if (!represented) continue;
        const resolvedReason = reasonCode ?? (spec.everyoneAsked ? "everyone" : gating === "full-by-memory" ? "opened-at-baseline" : "short-check");
        sections.push({
            key: spec.key,
            order: spec.order,
            gating,
            reasonCode: resolvedReason,
            reason: REASON_TEXT[resolvedReason],
            instruments
        });
    }
    const includeTriggers = ctx.selfHarmPositive || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.C_SSRS) || ctx.administeredUrls.has(QUESTIONNAIRE_URLS.SAFETY_PLAN);
    if (includeTriggers) {
        const depIndex = sections.findIndex((section)=>section.key === "depression");
        const triggers = TRIGGER_ADDED_SECTIONS.map((section)=>({
                ...section,
                reasonCode: ctx.reasons[urlForKey(section.instruments[0])] ?? section.reasonCode
            }));
        sections.splice(depIndex >= 0 ? depIndex + 1 : sections.length, 0, ...triggers);
    }
    return {
        eligible: true,
        sections
    };
}

/** True once a section has reached any terminal (non-pending) status. */ const isSectionSettled = (status)=>status !== "pending";
/**
 * Whether a status is an explicit advocate/patient decision — a patient decline
 * or the advocate's not-clinically-appropriate judgement — as opposed to the
 * `answered` status (re-derived from responses) or the transient `pending`
 * default. Only these survive the rail remounting (e.g. toggling the screener
 * off and back on) and must be restored as settlements; `answered` and `pending`
 * are always derived from the live responses, never rehydrated as a settlement,
 * so a not-yet-settled section can always still be declined after a remount.
 */ const isExplicitSettlement = (status)=>status === "declined-remaining" || status === "declined-section" || status === "not-administered-clinical";
/** Published GAD-2 two-item cut-point: ≥3 opens the full GAD-7. */ const ANXIETY_SHORT_CHECK_CUTOFF = 3;
/** Whether a GAD-2 short-check score opens the full anxiety set now. */ const shouldRevealAnxietyFullSet = (gad2Score)=>gad2Score >= ANXIETY_SHORT_CHECK_CUTOFF;
/**
 * Whether the full GAD-7 should render. A positive GAD-2 opens it live; an
 * existing GAD-7 answer re-opens it on draft resume when GAD-2 was not
 * persisted (adaptive-only short check).
 */ const shouldRevealAnxietyFullSetFromState = (input)=>shouldRevealAnxietyFullSet(input.gad2Score) || input.hasGad7Responses;
/**
 * Whether the depression self-harm answer is positive (any answer above the
 * lowest option on PHQ-9 item 9), which triggers the suicide-risk + safety-plan
 * sections (§8.4). Re-derived from today's answer each visit (E6).
 */ const isSelfHarmPositive = (phq9Responses)=>{
    const item9 = phq9Responses.find((r)=>r.linkId === "phq9-9");
    return item9?.value !== undefined && item9.value > 0;
};
/**
 * Whether the "turn the adaptive screener off" toggle must be locked so a
 * suicide-risk obligation can never be bypassed by switching to the classic
 * picker (D9/D13). A positive PHQ-9 self-harm answer triggers C-SSRS, so the
 * toggle stays locked until C-SSRS is completed; a positive C-SSRS additionally
 * requires acknowledgement. A completed, negative C-SSRS resolves it.
 *
 * @agentic @benchmark/core
 */ const isScreenerOffLocked = (input)=>input.selfHarmPositive && !input.suicideRiskCompleted || input.suicideRiskPositive && !input.suicideRiskAcknowledged;
/**
 * Whether the screening may be recorded (C6 + D13): there is at least one
 * section, every section has settled, and — if a suicide-risk screen is
 * positive — the advocate has acknowledged the escalate-now notice. A positive
 * suicide-risk screen can never be closed with the risk unacknowledged.
 *
 * @agentic @benchmark/core
 */ const canRecordScreening = (input)=>{
    if (input.statuses.length === 0) {
        return false;
    }
    if (!input.statuses.every(isSectionSettled)) {
        return false;
    }
    if (input.suicideRiskPositive && !input.suicideRiskAcknowledged) {
        return false;
    }
    return true;
};
const questionnaireUrl = (key)=>QUESTIONNAIRE_URLS[key];
const responseIsComplete = (responses, url)=>{
    const response = responses[url];
    if (!response) return false;
    const completion = evaluateQuestionnaireCompletion(url, response.responses, {
        declaredComplete: response.completed
    });
    return completion.supported && completion.complete;
};
/**
 * Recompute whether an adaptive sitting may switch to the classic flow.
 *
 * A positive PHQ-9 self-harm answer keeps the adaptive safety obligation active
 * until C-SSRS is complete or explicitly settled, and any positive C-SSRS
 * answers are durably acknowledged.
 *
 * @agentic @benchmark/core
 */ const isAdaptiveSafetyObligationSatisfied = (input)=>{
    const responses = input.questionnaireResponses ?? {};
    const selfHarmPositive = isSelfHarmPositive(responses[QUESTIONNAIRE_URLS.PHQ9]?.responses ?? []);
    const suicideRiskPositive = selfHarmPositive && (responses[QUESTIONNAIRE_URLS.C_SSRS]?.responses ?? []).some((response)=>(response.value ?? 0) > 0);
    const suicideRiskSettlement = input.settlements?.[QUESTIONNAIRE_URLS.C_SSRS];
    const suicideRiskSettled = suicideRiskSettlement !== undefined && isExplicitSettlement(suicideRiskSettlement) && input.reasons[QUESTIONNAIRE_URLS.C_SSRS] !== undefined || responseIsComplete(responses, QUESTIONNAIRE_URLS.C_SSRS);
    return !isScreenerOffLocked({
        selfHarmPositive,
        suicideRiskCompleted: suicideRiskSettled,
        suicideRiskPositive,
        suicideRiskAcknowledged: input.suicideRiskAcknowledged
    });
};
/**
 * Recompute adaptive record eligibility from clinical form state.
 *
 * The persistence boundary must call this instead of trusting the UI's cached
 * `_adaptiveCanRecord` flag. Explicit decline/judgement settlements remain
 * valid user decisions; an `answered` settlement is re-derived from canonical
 * questionnaire completion so it cannot authorize missing answers.
 *
 * @agentic @benchmark/core
 */ const canRecordAdaptiveScreener = (input)=>{
    const responses = input.questionnaireResponses ?? {};
    const settlements = input.settlements ?? {};
    const selfHarmPositive = isSelfHarmPositive(responses[QUESTIONNAIRE_URLS.PHQ9]?.responses ?? []);
    const administeredUrls = new Set([
        ...Object.keys(responses),
        ...Object.keys(settlements)
    ]);
    const sections = composePersistedAdaptiveSession({
        reasons: input.reasons,
        administeredUrls,
        selfHarmPositive
    }).sections;
    const gad2Score = (responses[QUESTIONNAIRE_URLS.GAD2]?.responses ?? []).reduce((total, response)=>total + (response.value ?? 0), 0);
    const hasGad7Responses = (responses[QUESTIONNAIRE_URLS.GAD7]?.responses ?? []).some((response)=>response.value !== undefined);
    const revealAnxietyFull = shouldRevealAnxietyFullSetFromState({
        gad2Score,
        hasGad7Responses
    });
    const administeredForSection = (section)=>{
        if (section.key !== "anxiety") {
            return [
                questionnaireUrl(section.instruments[0])
            ];
        }
        if (section.gating === "full-by-memory") {
            return [
                QUESTIONNAIRE_URLS.GAD7
            ];
        }
        return revealAnxietyFull ? [
            QUESTIONNAIRE_URLS.GAD2,
            QUESTIONNAIRE_URLS.GAD7
        ] : [
            QUESTIONNAIRE_URLS.GAD2
        ];
    };
    const ignoredQuestionnaireUrls = input.ignoredQuestionnaireUrls ?? new Set();
    const blockedQuestionnaireUrls = input.blockedQuestionnaireUrls ?? new Set();
    const statuses = sections.flatMap((section)=>{
        const administeredUrls = administeredForSection(section);
        const requiredUrls = section.key === "suicide-risk" ? administeredUrls : administeredUrls.filter((url)=>!ignoredQuestionnaireUrls.has(url));
        if (requiredUrls.length === 0) return [];
        const hasQuarantinedSuicideRisk = section.key === "suicide-risk" && requiredUrls.some((url)=>ignoredQuestionnaireUrls.has(url));
        const sectionSettled = !hasQuarantinedSuicideRisk && requiredUrls.every((url)=>{
            if (blockedQuestionnaireUrls.has(url)) return false;
            const settlement = settlements[url];
            return settlement !== undefined && isExplicitSettlement(settlement) && input.reasons[url] !== undefined || responseIsComplete(responses, url);
        });
        return [
            sectionSettled ? "answered" : "pending"
        ];
    });
    const suicideRiskPositive = selfHarmPositive && (responses[QUESTIONNAIRE_URLS.C_SSRS]?.responses ?? []).some((response)=>(response.value ?? 0) > 0);
    if (sections.length > 0 && statuses.length === 0) {
        return !suicideRiskPositive || input.suicideRiskAcknowledged;
    }
    return canRecordScreening({
        statuses,
        suicideRiskPositive,
        suicideRiskAcknowledged: input.suicideRiskAcknowledged
    });
};

/**
 * Organization resolvers: pure functions that extract display data from Organization resources.
 * @agentic @benchmark/core
 */ /**
 * Returns organization display name or fallback to "Organization {id}".
 * @agentic @benchmark/core
 */ function getOrganizationDisplayName(organization) {
    return organization.name || `Organization ${organization.id}`;
}

/**
 * Returns true if tag is an AI-generated tag (Aurora system).
 * @agentic @benchmark/core
 */ const isAiGeneratedTag = (tag)=>tag.system === AI_GENERATED_TAG_SYSTEM && !!tag.code && AI_GENERATED_TAG_CODES.has(tag.code);

/** A questionnaire cannot be safely finalized from the supplied form state. */ class QuestionnairePersistenceError extends Error {
    constructor(message){
        super(message);
        this.name = "QuestionnairePersistenceError";
    }
}
const pickSortedRecord = (record, includedUrls)=>Object.fromEntries(Object.entries(record ?? {}).filter(([url])=>includedUrls.has(url)).sort(([left], [right])=>left.localeCompare(right)));
const hasOwn = (value, key)=>Object.prototype.hasOwnProperty.call(value, key);
const ASSESSMENT_STATE_FIELDS = [
    "selectedQuestionnaires",
    "questionnaireResponses",
    "_questionnaireResponseIds",
    "_observationIds",
    "_readOnlyQuestionnaireCount",
    "_adaptiveScreenerSession",
    "_adaptiveSettlements",
    "_suicideRiskAcknowledged",
    "_suicideRiskAcknowledgedAt",
    "_suicideRiskAcknowledgedBy",
    "_adaptiveActive",
    "_adaptiveCanRecord"
];
/**
 * True only when a projection carries assessment state that could drive or
 * describe a questionnaire write. Conflict-marker arrays are excluded because
 * the action injects its freshly reloaded authoritative markers into otherwise
 * assessment-free encounter projections.
 */ const hasAssessmentStatePayload = (formData)=>ASSESSMENT_STATE_FIELDS.some((field)=>hasOwn(formData, field) && formData[field] !== undefined);
const recordTouchesQuestionnaire = (record, questionnaireUrl)=>record !== undefined && hasOwn(record, questionnaireUrl);
const touchesConflictedQuestionnaire = (formData, questionnaireUrl)=>{
    const adaptiveFormData = formData;
    return formData.selectedQuestionnaires?.includes(questionnaireUrl) === true || recordTouchesQuestionnaire(formData.questionnaireResponses, questionnaireUrl) || recordTouchesQuestionnaire(formData._questionnaireResponseIds, questionnaireUrl) || recordTouchesQuestionnaire(formData._observationIds, questionnaireUrl) || recordTouchesQuestionnaire(adaptiveFormData._adaptiveScreenerSession?.reasons, questionnaireUrl) || recordTouchesQuestionnaire(adaptiveFormData._adaptiveSettlements, questionnaireUrl) || questionnaireUrl === QUESTIONNAIRE_URLS$1.C_SSRS && (adaptiveFormData._suicideRiskAcknowledged !== undefined || adaptiveFormData._suicideRiskAcknowledgedAt !== undefined || adaptiveFormData._suicideRiskAcknowledgedBy !== undefined);
};
/**
 * Stable representation of the quarantined instruments' write-driving state.
 * Non-conflicted assessments are intentionally excluded so clinicians can
 * complete them while ambiguous historical artifacts remain untouched.
 */ const getQuarantinedAssessmentStateFingerprint = (formData, quarantinedUrls)=>{
    const session = formData._adaptiveScreenerSession;
    const questionnaireResponses = Object.fromEntries(Object.entries(pickSortedRecord(formData.questionnaireResponses, quarantinedUrls)).map(([url, entry])=>[
            url,
            {
                ...entry,
                responses: [
                    ...entry.responses
                ].sort((left, right)=>left.linkId.localeCompare(right.linkId))
            }
        ]));
    const quarantineIncludesSuicideRisk = quarantinedUrls.has(QUESTIONNAIRE_URLS$1.C_SSRS);
    return JSON.stringify({
        selectedQuestionnaires: [
            ...formData.selectedQuestionnaires ?? []
        ].filter((url)=>quarantinedUrls.has(url)).sort(),
        questionnaireResponses,
        questionnaireResponseIds: pickSortedRecord(formData._questionnaireResponseIds, quarantinedUrls),
        observationIds: pickSortedRecord(formData._observationIds, quarantinedUrls),
        scoreConflictUrls: [
            ...formData._questionnaireScoreConflictUrls ?? []
        ].sort(),
        duplicateScoreUrls: [
            ...formData._questionnaireDuplicateScoreUrls ?? []
        ].sort(),
        adaptiveSession: session ? {
            id: session.id,
            baseline: session.baseline,
            reasons: pickSortedRecord(session.reasons, quarantinedUrls)
        } : null,
        adaptiveSettlements: pickSortedRecord(formData._adaptiveSettlements, quarantinedUrls),
        suicideRiskAcknowledged: quarantineIncludesSuicideRisk ? formData._suicideRiskAcknowledged === true : null,
        suicideRiskAcknowledgedAt: quarantineIncludesSuicideRisk ? formData._suicideRiskAcknowledgedAt ?? null : null,
        suicideRiskAcknowledgedBy: quarantineIncludesSuicideRisk ? formData._suicideRiskAcknowledgedBy ?? null : null
    });
};
/**
 * Validate adaptive finalization before the action performs any write side
 * effect. The bundle builder also invokes this for direct callers.
 */ const assertAdaptiveScreenerFinalization = (formData, isDraft = false, quarantinedUrls = new Set(), scoreConflictUrls = new Set())=>{
    if (isDraft) return;
    if (quarantinedUrls.has(QUESTIONNAIRE_URLS$1.C_SSRS) || scoreConflictUrls.has(QUESTIONNAIRE_URLS$1.C_SSRS)) {
        throw new QuestionnairePersistenceError("A recorded suicide-risk assessment has conflicting scores and requires clinical review before recording.");
    }
    const unsafeConflictUrls = [
        ...scoreConflictUrls
    ].filter((url)=>!quarantinedUrls.has(url) && touchesConflictedQuestionnaire(formData, url));
    if (unsafeConflictUrls.length > 0) {
        throw new QuestionnairePersistenceError("A recorded assessment score requires clinical review before recording.");
    }
    const adaptiveFormData = formData;
    const session = adaptiveFormData._adaptiveScreenerSession;
    const settlements = adaptiveFormData._adaptiveSettlements;
    const safetyAcknowledged = adaptiveFormData._suicideRiskAcknowledged === true && adaptiveFormData._suicideRiskAcknowledgedAt !== undefined;
    if (adaptiveFormData._adaptiveActive === true && (!session || !canRecordAdaptiveScreener({
        reasons: session.reasons,
        settlements,
        questionnaireResponses: formData.questionnaireResponses,
        suicideRiskAcknowledged: safetyAcknowledged,
        ignoredQuestionnaireUrls: quarantinedUrls,
        blockedQuestionnaireUrls: new Set([
            ...scoreConflictUrls
        ].filter((url)=>!quarantinedUrls.has(url)))
    }))) {
        throw new QuestionnairePersistenceError("Complete or settle every adaptive screening section before recording.");
    }
    if (session && adaptiveFormData._adaptiveActive !== true && !isAdaptiveSafetyObligationSatisfied({
        reasons: session.reasons,
        questionnaireResponses: formData.questionnaireResponses,
        settlements,
        suicideRiskAcknowledged: safetyAcknowledged
    })) {
        throw new QuestionnairePersistenceError("Complete and acknowledge the suicide-risk assessment before leaving the adaptive screener.");
    }
};
/**
 * Builds a FHIR Bundle for encounter upsert (Encounter, ClinicalImpression, QuestionnaireResponse, Observation, Flags, Tasks).
 * @agentic @benchmark/core
 */ function buildUpsertEncounterBundle(options) {
    const { formData, patient, practitioner, practitionerRole, organization, existingEncounter, originalFormData, existingQuestionnaireResponses = {}, existingScoreObservations = {}, existingActiveFlags, goals = [], scribeComposition, onWarning, isDraft = false, draftFormState, benchmarkOrganizationId } = options;
    const scoreConflictUrls = new Set([
        ...originalFormData?._questionnaireScoreConflictUrls ?? [],
        ...formData._questionnaireScoreConflictUrls ?? []
    ]);
    const duplicateScoreUrls = new Set([
        ...originalFormData?._questionnaireDuplicateScoreUrls ?? [],
        ...formData._questionnaireDuplicateScoreUrls ?? []
    ]);
    const conflictMarkersMatch = JSON.stringify([
        ...formData._questionnaireScoreConflictUrls ?? []
    ].sort()) === JSON.stringify([
        ...originalFormData?._questionnaireScoreConflictUrls ?? []
    ].sort()) && JSON.stringify([
        ...formData._questionnaireDuplicateScoreUrls ?? []
    ].sort()) === JSON.stringify([
        ...originalFormData?._questionnaireDuplicateScoreUrls ?? []
    ].sort());
    if (duplicateScoreUrls.size > 0 && (!conflictMarkersMatch || hasAssessmentStatePayload(formData) && getQuarantinedAssessmentStateFingerprint(formData, duplicateScoreUrls) !== getQuarantinedAssessmentStateFingerprint(originalFormData ?? {}, duplicateScoreUrls))) {
        throw new QuestionnairePersistenceError("A recorded assessment score requires review before assessments can be changed.");
    }
    assertAdaptiveScreenerFinalization(formData, isDraft, duplicateScoreUrls, scoreConflictUrls);
    // Build the main encounter resource
    let encounter;
    let encounterTempFullUrl;
    const existingTags = existingEncounter?.meta?.tag || [];
    const domainModel = encounterFromForm(formData, existingTags);
    // Determine if this is an update or creation
    if (existingEncounter) {
        encounter = {
            ...existingEncounter
        };
        // Update status: finalize drafts or keep as draft
        encounter.status = isDraft ? "in-progress" : "finished";
        // Strip AI-generated tags from existing encounter
        const tags = encounter.meta?.tag;
        if (tags && tags.length > 0) {
            const filteredTags = tags.filter((tag)=>!isAiGeneratedTag(tag));
            if (filteredTags.length < tags.length) {
                encounter.meta = {
                    ...encounter.meta,
                    tag: filteredTags
                };
            }
        }
    } else if (formData._scribeEncounterId) {
        throw new Error("buildUpsertEncounterBundle: Scribe encounters must be loaded and passed as existingEncounter");
    } else {
        // Create new encounter
        encounterTempFullUrl = `urn:uuid:${crypto.randomUUID()}`;
        let serviceProviderReference;
        if (benchmarkOrganizationId && organization) {
            const organizationDisplay = getOrganizationDisplayName(organization);
            serviceProviderReference = buildReferenceWithDisplay("Organization", benchmarkOrganizationId, organizationDisplay);
        }
        encounter = {
            resourceType: "Encounter",
            status: isDraft ? "in-progress" : "finished",
            class: domainModel.class || {
                system: "http://terminology.hl7.org/CodeSystem/v3-ActCode",
                code: "VR",
                display: "virtual"
            },
            subject: buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient)),
            ...serviceProviderReference && {
                serviceProvider: serviceProviderReference
            },
            participant: [
                {
                    individual: buildPractitionerRoleReference(practitionerRole.id || "", getPractitionerDisplayName(practitioner))
                }
            ]
        };
    }
    // Apply domain model transformations
    if (domainModel.period?.start) {
        encounter.period = domainModel.period;
    }
    // Ensure drafts always have a period.start for timeline ordering
    if (isDraft && !encounter.period?.start) {
        encounter.period = {
            ...encounter.period,
            start: new Date().toISOString()
        };
    }
    // Reconcile the per-section scratch extension unconditionally: encounterFromForm
    // omits it when empty, so clearing all scratch must strip a previously-stored
    // one even when there are no other extensions to apply (otherwise a re-opened
    // intake would restore notes the clinician removed).
    //
    // The general (whole-call) scratch extension is reconciled ONLY when the form
    // explicitly owns the field (`intake-scratch` present, even when empty). An
    // update that omits the field entirely — a non-intake or partial save that never
    // loaded the pad — must NOT delete a value the clinician never touched. Clearing
    // the pad sends `""` (present), which still strips.
    const ownsGeneralScratch = formData["intake-scratch"] !== undefined;
    encounter.extension = (encounter.extension || []).filter((ext)=>ext.url !== ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL && (ext.url !== ENCOUNTER_SCRATCH_EXTENSION_URL || !ownsGeneralScratch));
    // Apply extensions from domain model
    if (domainModel.extensions.length > 0) {
        const filtered = (encounter.extension || []).filter((ext)=>(ext.url !== ENCOUNTER_SCRATCH_EXTENSION_URL || !ownsGeneralScratch) && ext.url !== ENCOUNTER_SECTION_SCRATCH_EXTENSION_URL && ext.url !== "http://benchmarkhealth.com/fhir/StructureDefinition/prefill-source");
        encounter.extension = [
            ...filtered,
            ...domainModel.extensions
        ];
    }
    // Apply tags from domain model
    encounter.meta = {
        ...encounter.meta,
        tag: domainModel.tags
    };
    // Handle scribe composition ID extension (business logic)
    if (domainModel.scribeCompositionId) {
        const extUrl = "http://benchmarkhealth.com/fhir/StructureDefinition/prefill-source";
        encounter.extension = encounter.extension || [];
        const alreadyHas = encounter.extension.some((ex)=>ex.url === extUrl && typeof ex.valueReference?.reference === "string" && ex.valueReference.reference.endsWith(domainModel.scribeCompositionId || ""));
        if (!alreadyHas) {
            encounter.extension.push({
                url: extUrl,
                valueReference: {
                    reference: `Composition/${domainModel.scribeCompositionId}`
                }
            });
        }
    }
    // Handle draft form state extension
    encounter.extension = encounter.extension || [];
    // Strip any existing draft form state extension
    encounter.extension = encounter.extension.filter((ext)=>ext.url !== ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL);
    if (isDraft && draftFormState) {
        encounter.extension.push({
            url: ENCOUNTER_DRAFT_FORM_STATE_EXTENSION_URL,
            valueString: JSON.stringify(draftFormState)
        });
    }
    const clinicalImpressionBuilds = [];
    const clinicalImpressionMapping = {
        comment: CLINICAL_IMPRESSION_CODE_MAPPINGS.comment,
        "chief-complaint": CLINICAL_IMPRESSION_CODE_MAPPINGS.chiefComplaint,
        "hpi-current-symptoms": CLINICAL_IMPRESSION_CODE_MAPPINGS.hpiCurrentSymptoms,
        "mental-health-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.mentalHealthHistory,
        "mental-status-appearance": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-appearance"],
        "mental-status-behavior": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-behavior"],
        "mental-status-speech": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-speech"],
        "mental-status-mood-affect": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-mood-affect"],
        "mental-status-thought-process-content": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-thought-process-content"],
        "mental-status-cognition": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-cognition"],
        "mental-status-perception": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-perception"],
        "mental-status-insight-judgment": CLINICAL_IMPRESSION_CODE_MAPPINGS["mental-status-insight-judgment"],
        "trauma-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.traumaHistory,
        "substance-use-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.substanceUseHistory,
        "medical-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.medicalHistory,
        "medication-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.medicationHistory,
        "social-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.socialHistory,
        "family-history": CLINICAL_IMPRESSION_CODE_MAPPINGS.familyHistory,
        "assessment-plan": CLINICAL_IMPRESSION_CODE_MAPPINGS.assessmentPlan,
        // Does not appear to be used in any forms currently - nov 12 2025
        // "case-management-summary":
        //   CLINICAL_IMPRESSION_CODE_MAPPINGS.caseManagementSummary,
        "current-outpatient-treaters": CLINICAL_IMPRESSION_CODE_MAPPINGS.currentOutpatientTreaters,
        // Universal Intake subsection fields (single-entry overhaul).
        hospitalizations: CLINICAL_IMPRESSION_CODE_MAPPINGS.hospitalizations,
        safety: CLINICAL_IMPRESSION_CODE_MAPPINGS.safety,
        "existing-treaters": CLINICAL_IMPRESSION_CODE_MAPPINGS["existing-treaters"],
        "outpatient-care-history": CLINICAL_IMPRESSION_CODE_MAPPINGS["outpatient-care-history"],
        "substance-current-use": CLINICAL_IMPRESSION_CODE_MAPPINGS["substance-current-use"],
        "substance-treatment-history": CLINICAL_IMPRESSION_CODE_MAPPINGS["substance-treatment-history"],
        "chronic-conditions": CLINICAL_IMPRESSION_CODE_MAPPINGS["chronic-conditions"],
        "other-physical-symptoms": CLINICAL_IMPRESSION_CODE_MAPPINGS["other-physical-symptoms"],
        "living-situation": CLINICAL_IMPRESSION_CODE_MAPPINGS["living-situation"],
        "social-supports": CLINICAL_IMPRESSION_CODE_MAPPINGS["social-supports"],
        "occupation-education": CLINICAL_IMPRESSION_CODE_MAPPINGS["occupation-education"],
        "peds-home-life": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-home-life"],
        "peds-school-education": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-school-education"],
        "peds-activities-strengths": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-activities-strengths"],
        "peds-peers-social": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-peers-social"],
        "peds-birth-complications": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-birth-complications"],
        "peds-developmental-milestones": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-developmental-milestones"],
        "peds-family-history": CLINICAL_IMPRESSION_CODE_MAPPINGS["peds-family-history"]
    };
    for (const [field, code] of Object.entries(clinicalImpressionMapping)){
        const value = formData[field];
        // Skip if no value and no existing impression to delete
        if (!value && !originalFormData?._clinicalImpressionIds?.[field]) {
            continue;
        }
        const existingId = originalFormData?._clinicalImpressionIds?.[field];
        // Build value from summary
        // Use the CodeableConcept directly from CLINICAL_IMPRESSION_CODE_MAPPINGS
        // which contains the proper LOINC codes with display names
        const clinicalImpressionValue = value ? {
            valueString: value
        } : undefined;
        // Business logic: Determine if we should delete.
        // If value is empty and an existing impression exists, delete it — except
        // for a retired field that is merely ABSENT (undefined) from formData while
        // its id is threaded through: that is the partial-seed shape (e.g. the
        // scribe-encounter load writes values/ids into originalFormData only), and
        // deleting on that basis would destroy documentation the clinician can no
        // longer re-enter. An explicit empty string, however, is a deliberate clear
        // on an editable draft, so it falls through to the DELETE path below.
        const isRetired = RETIRED_CLINICAL_IMPRESSION_FIELD_KEYS.includes(field);
        if (value === undefined && isRetired) {
            continue;
        }
        const shouldDelete = !clinicalImpressionValue && !!existingId;
        // If deleting, create minimal resource for DELETE operation
        if (shouldDelete && existingId) {
            clinicalImpressionBuilds.push({
                clinicalImpression: {
                    resourceType: "ClinicalImpression",
                    id: existingId
                },
                shouldDelete: true
            });
            continue;
        }
        // Build clinical impression resource (mechanical transformation)
        // Use the CodeableConcept directly from CLINICAL_IMPRESSION_CODE_MAPPINGS
        // which contains the proper LOINC codes with display names
        const clinicalImpression = buildClinicalImpression({
            patient,
            encounter,
            code: code,
            valueType: "summary",
            value: clinicalImpressionValue,
            existingClinicalImpressionId: existingId,
            tempEncounterFullUrl: encounterTempFullUrl,
            effectiveDate: encounter?.period?.start,
            status: isDraft ? "in-progress" : "completed"
        });
        // Skip if no clinical impression can be built (missing code or value)
        if (!clinicalImpression) {
            continue;
        }
        // Note: Condition creation is not applicable here since we always use
        // valueType: "summary" for encounter form data clinical impressions.
        // Condition creation logic is handled in upsertClinicalImpression.action.ts
        // when valueType is "problem" and value contains a coding.
        clinicalImpressionBuilds.push({
            clinicalImpression,
            shouldDelete: false
        });
    }
    // Build questionnaire responses from form data
    const questionnaireResponses = [];
    const observations = [];
    // Adaptive-screener session metadata (set by the Assessments step when the
    // adaptive screener is active): the shared session id, whether this sitting is
    // the patient's baseline, and the reason each section's instrument was asked.
    const adaptiveFormData = formData;
    const adaptiveScreenerSession = adaptiveFormData._adaptiveScreenerSession;
    const adaptiveSettlements = adaptiveFormData._adaptiveSettlements;
    const adaptiveAcknowledgedAt = adaptiveFormData._suicideRiskAcknowledgedAt;
    // The advocate who acknowledged the positive suicide-risk screen (D13) is the
    // acting practitioner on this encounter.
    const adaptiveAcknowledgedBy = adaptiveFormData._suicideRiskAcknowledgedBy ?? (adaptiveAcknowledgedAt ? practitionerRole?.id ? `PractitionerRole/${practitionerRole.id}` : practitioner?.id ? `Practitioner/${practitioner.id}` : undefined : undefined);
    function buildResponseItems(formResponses, questionItems) {
        const result = [];
        for (const question of questionItems){
            if (question.type === "group" && question.item) {
                // Build nested structure for groups
                const nestedItems = buildResponseItems(formResponses, question.item);
                if (nestedItems.length > 0) {
                    result.push({
                        linkId: question.linkId,
                        text: question.text,
                        item: nestedItems
                    });
                }
            } else {
                // Find response for this item
                const response = formResponses.find((r)=>r.linkId === question.linkId);
                if (response && (response.value !== undefined || response.valueString)) {
                    result.push({
                        linkId: question.linkId,
                        text: question.text,
                        answer: response.valueString !== undefined ? [
                            {
                                valueString: response.valueString
                            }
                        ] : response.value !== undefined ? [
                            {
                                valueInteger: response.value,
                                ...response.coding && {
                                    valueCoding: response.coding
                                }
                            }
                        ] : undefined
                    });
                }
            }
        }
        return result;
    }
    if (formData.questionnaireResponses) {
        for (const [questionnaireUrl, responseData] of Object.entries(formData.questionnaireResponses)){
            if (scoreConflictUrls.has(questionnaireUrl) && !duplicateScoreUrls.has(questionnaireUrl)) {
                throw new QuestionnairePersistenceError(`The recorded score for ${getQuestionnaireDefinition(questionnaireUrl)?.title ?? "this questionnaire"} requires review before it can be changed.`);
            }
            if (duplicateScoreUrls.has(questionnaireUrl)) continue;
            const typedResponseData = responseData;
            // Persistence is NEVER gated on instrument classification or the adaptive
            // session. An answered questionnaire response is saved on whatever path
            // recorded it — the manual picker or the adaptive rail — because a
            // questionnaire is a questionnaire and a draft response is a draft
            // response. The adaptive session only drives additive tagging/grouping
            // below (session id, reason, settlement — D18/F7); it never decides
            // whether data is written. Gating persistence on that transient flag is
            // what silently dropped adaptive answers (PGI-S/GAD-2/WSAS/…) on save.
            const existingId = originalFormData?._questionnaireResponseIds?.[questionnaireUrl];
            const existingResponse = existingQuestionnaireResponses[questionnaireUrl] ?? (existingId ? {
                resourceType: "QuestionnaireResponse",
                id: existingId
            } : undefined);
            // A settled adaptive section is recorded even when not fully completed, so
            // partial answers and declines are captured, not dropped (D8). Only applies
            // with an active adaptive session — never on the classic save path, even if
            // stale settlement state lingers on the form.
            const adaptiveSettlement = adaptiveScreenerSession ? adaptiveSettlements?.[questionnaireUrl] : undefined;
            // A declined / partial / clinician-judged section is recorded but not
            // scored — only fully answered sections get a score observation (D8).
            const isDeclinedAdaptiveSection = adaptiveSettlement !== undefined && adaptiveSettlement !== "answered";
            const completion = evaluateQuestionnaireCompletion(questionnaireUrl, typedResponseData.responses, {
                declaredComplete: typedResponseData.completed
            });
            if (!completion.supported) {
                throw new QuestionnairePersistenceError(`The ${getQuestionnaireDefinition(questionnaireUrl)?.title ?? "questionnaire"} cannot be safely saved because its definition no longer matches the recorded answers.`);
            }
            const actuallyComplete = completion.complete;
            const selected = formData.selectedQuestionnaires?.includes(questionnaireUrl) === true;
            const enhancedConfig = getEnhancedQuestionnaireConfig(questionnaireUrl);
            const existingObservationId = originalFormData?._observationIds?.[questionnaireUrl];
            const existingObservation = existingScoreObservations[questionnaireUrl] ?? (existingObservationId ? {
                resourceType: "Observation",
                id: existingObservationId
            } : undefined);
            const scoreEligible = actuallyComplete && !isDeclinedAdaptiveSection && Boolean(enhancedConfig?.scoring && enhancedConfig.scoring.linkIds.length > 0 && enhancedConfig.totalScoreLoincCode);
            if (!isDraft && !actuallyComplete && !isDeclinedAdaptiveSection && (typedResponseData.responses.length > 0 || selected || adaptiveSettlement === "answered")) {
                throw new QuestionnairePersistenceError(`Complete or explicitly settle ${getQuestionnaireDefinition(questionnaireUrl)?.title ?? "the questionnaire"} before recording.`);
            }
            const shouldUpsertQuestionnaireResponse = isDraft || actuallyComplete || isDeclinedAdaptiveSection;
            if (shouldUpsertQuestionnaireResponse) {
                const questionItems = enhancedConfig?.questions || [];
                // Build nested FHIR QuestionnaireResponse items
                const questionnaireResponseItems = buildResponseItems(typedResponseData.responses, questionItems);
                const questionnaireResponseFormData = {
                    questionnaire: questionnaireUrl,
                    status: isDraft ? "in-progress" : "completed",
                    authored: existingResponse?.authored ?? new Date().toISOString(),
                    item: questionnaireResponseItems
                };
                const response = questionnaireResponseFromForm(questionnaireResponseFormData, patient, practitioner, practitionerRole, encounter, existingResponse, encounterTempFullUrl);
                // Tag adaptive-screener responses with the shared session id + reason so
                // the sitting reads back as one entry (D18/F7) and cross-visit memory can
                // resolve which sections opened (E1–E3).
                const adaptiveReasonCode = adaptiveScreenerSession?.reasons[questionnaireUrl];
                const isSuicideRiskResponse = questionnaireUrl === QUESTIONNAIRE_URLS$1.C_SSRS;
                const existingSuicideRiskMarker = isSuicideRiskResponse && existingEncounter?.status === "finished" && existingResponse ? readAdaptiveScreenerMarker(existingResponse) : null;
                questionnaireResponses.push(adaptiveScreenerSession && adaptiveReasonCode !== undefined ? tagAdaptiveScreenerResponse(response, {
                    sessionId: adaptiveScreenerSession.id,
                    baseline: adaptiveScreenerSession.baseline,
                    reasonCode: adaptiveReasonCode,
                    settlement: adaptiveSettlement,
                    acknowledgedAt: isSuicideRiskResponse ? adaptiveAcknowledgedAt ?? existingSuicideRiskMarker?.acknowledgedAt : undefined,
                    acknowledgedBy: isSuicideRiskResponse ? adaptiveAcknowledgedBy ?? existingSuicideRiskMarker?.acknowledgedBy : undefined
                }) : response);
                // Build observation for the calculated score (only for scored questionnaires)
                // Only build score observation if questionnaire has scoring and totalScoreLoincCode
                if (scoreEligible && enhancedConfig && enhancedConfig.scoring && enhancedConfig.scoring.linkIds.length > 0 && enhancedConfig.totalScoreLoincCode) {
                    // Filter out non-scorable responses (e.g., functional impairment questions)
                    const scorableResponses = filterScorableResponses(typedResponseData.responses.filter((r)=>r.value !== undefined).map((r)=>({
                            linkId: r.linkId,
                            value: r.value
                        })), questionnaireUrl);
                    if (scorableResponses.length > 0) {
                        const scoreResult = calculateQuestionnaireScore(scorableResponses, enhancedConfig.scoring);
                        const observation = buildObservationFromScore({
                            score: scoreResult.score,
                            questionnaireConfig: enhancedConfig,
                            scoring: enhancedConfig.scoring,
                            patient,
                            encounter,
                            practitionerRole,
                            tempEncounterFullUrl: encounterTempFullUrl,
                            existingObservation,
                            status: isDraft ? "preliminary" : "final",
                            interpretation: scoreResult.interpretation,
                            riskLevel: scoreResult.riskLevel
                        });
                        observations.push(observation);
                    }
                } else if (existingObservationId) {
                    const fullExistingObservation = existingScoreObservations[questionnaireUrl];
                    if (!fullExistingObservation) {
                        throw new QuestionnairePersistenceError("The existing assessment score could not be safely reloaded. Refresh before saving.");
                    }
                    observations.push({
                        ...fullExistingObservation,
                        status: "cancelled"
                    });
                }
            }
        }
    }
    // Record wholly-declined adaptive sections (D8): a section the advocate
    // settled as declined / not-administered but the patient never answered has
    // no questionnaireResponses entry, so persist a minimal completed QR carrying
    // the decline, so the screening is fully recorded and reads back.
    if (!isDraft && adaptiveScreenerSession && adaptiveSettlements) {
        for (const [url, settlement] of Object.entries(adaptiveSettlements)){
            if (scoreConflictUrls.has(url) && !duplicateScoreUrls.has(url)) {
                throw new QuestionnairePersistenceError(`The recorded score for ${getQuestionnaireDefinition(url)?.title ?? "this questionnaire"} requires review before it can be changed.`);
            }
            if (duplicateScoreUrls.has(url)) continue;
            if (settlement === "answered") continue;
            if (formData.questionnaireResponses?.[url]) continue;
            const reasonCode = adaptiveScreenerSession.reasons[url];
            if (reasonCode === undefined) continue;
            const existingId = originalFormData?._questionnaireResponseIds?.[url];
            const existingResponse = existingQuestionnaireResponses[url] ?? (existingId ? {
                resourceType: "QuestionnaireResponse",
                id: existingId
            } : undefined);
            const declinedResponse = questionnaireResponseFromForm({
                questionnaire: url,
                status: "completed",
                authored: existingResponse?.authored ?? new Date().toISOString(),
                item: []
            }, patient, practitioner, practitionerRole, encounter, existingResponse, encounterTempFullUrl);
            const isSuicideRisk = url === QUESTIONNAIRE_URLS$1.C_SSRS;
            const existingSuicideRiskMarker = isSuicideRisk && existingEncounter?.status === "finished" && existingResponse ? readAdaptiveScreenerMarker(existingResponse) : null;
            questionnaireResponses.push(tagAdaptiveScreenerResponse(declinedResponse, {
                sessionId: adaptiveScreenerSession.id,
                baseline: adaptiveScreenerSession.baseline,
                reasonCode,
                settlement,
                acknowledgedAt: isSuicideRisk ? adaptiveAcknowledgedAt ?? existingSuicideRiskMarker?.acknowledgedAt : undefined,
                acknowledgedBy: isSuicideRisk ? adaptiveAcknowledgedBy ?? existingSuicideRiskMarker?.acknowledgedBy : undefined
            }));
            const existingObservationId = originalFormData?._observationIds?.[url];
            if (existingObservationId) {
                const fullExistingObservation = existingScoreObservations[url];
                if (!fullExistingObservation) {
                    throw new QuestionnairePersistenceError("The existing assessment score could not be safely reloaded. Refresh before saving.");
                }
                observations.push({
                    ...fullExistingObservation,
                    status: "cancelled"
                });
            }
        }
    }
    // Build goal progress observations from form data (CoCM follow-up)
    if (formData["goal-progress"] && formData["goal-progress"].length > 0) {
        for (const progressData of formData["goal-progress"]){
            // Find the corresponding Goal resource
            const goal = goals.find((g)=>g.id === progressData.goalId);
            if (!goal) {
                onWarning?.(`Goal ${progressData.goalId} not found. Skipping goal progress observation.`);
                continue;
            }
            // Get existing observation ID if editing
            const existingObservationId = originalFormData?._goalProgressObservationIds?.[progressData.goalId];
            // Build Observation resource using goalProgressFromForm adapter
            const goalProgressFormData = {
                goalId: progressData.goalId,
                gasScore: progressData.gasScore,
                effectiveDateTime: progressData.effectiveDateTime,
                note: progressData.note
            };
            const observation = goalProgressFromForm(goalProgressFormData, goal, patient, practitionerRole, existingObservationId ? {
                id: existingObservationId
            } : undefined);
            observations.push(observation);
        }
    }
    // Build care-time observations from form data (intake completion). Created
    // only on finalize so a draft's minutes never count toward billable totals
    // (the monthly sum includes `preliminary` status). The call entry is
    // effective-dated to the encounter period — the canonical call timing the
    // scribe pipeline fills — so it lands in the correct calendar month, appears
    // in the activity log, and counts toward the total. Upsert-by-id mirrors the
    // questionnaire-score pattern so an edit/re-save updates rather than dupes.
    if (!isDraft) {
        const careTimeBase = {
            patientId: patient.id,
            patientDisplay: getPatientDisplayName(patient),
            performerRoleId: practitionerRole.id,
            performerDisplay: getPractitionerDisplayName(practitioner),
            ...formData._careTimeTaskId && {
                taskId: formData._careTimeTaskId,
                taskDisplay: formData._careTimeTaskDisplay ?? "Task"
            },
            ...formData._careTimeCarePlanId && {
                carePlanId: formData._careTimeCarePlanId,
                carePlanDisplay: formData._careTimeCarePlanDisplay ?? "Care Plan"
            },
            ...encounter.id && {
                encounterId: encounter.id,
                encounterDisplay: `Encounter ${encounter.id}`
            }
        };
        // New encounters have no id yet; wire the in-bundle temp full url so the
        // care-time Observation resolves to the encounter created in this txn.
        const attachEncounterRef = (obs)=>{
            if (!encounter.id && encounterTempFullUrl) {
                obs.encounter = {
                    reference: encounterTempFullUrl
                };
            }
            return obs;
        };
        const callMinutes = formData["care-time-call-minutes"];
        if (typeof callMinutes === "number" && callMinutes > 0) {
            const existingId = originalFormData?._careTimeObservationIds?.["intake-call"];
            const hasPeriod = Boolean(encounter.period?.start && encounter.period?.end);
            const callObs = careTimeFromForm({
                ...careTimeBase,
                minutes: callMinutes,
                method: "intake-call",
                notes: formData["care-time-call-notes"]?.trim() || undefined,
                // Link to the call Communication so this entry and the Dialpad
                // webhook tee's auto-created `call` entry collapse via the FHIR
                // conditional-create dedup key (derived-from + category).
                communicationId: formData._careTimeCallCommunicationId,
                effectiveDate: encounter.period?.start ?? new Date().toISOString(),
                ...hasPeriod && {
                    effectivePeriodStart: encounter.period.start,
                    effectivePeriodEnd: encounter.period.end
                }
            }, existingId ? {
                resourceType: "Observation",
                id: existingId
            } : undefined);
            observations.push(attachEncounterRef(callObs));
        }
        const docMinutes = formData["care-time-documentation-minutes"];
        if (typeof docMinutes === "number" && docMinutes > 0) {
            const existingId = originalFormData?._careTimeObservationIds?.["intake-documentation"];
            const docObs = careTimeFromForm({
                ...careTimeBase,
                minutes: docMinutes,
                method: "intake-documentation",
                notes: formData["care-time-documentation-notes"]?.trim() || undefined,
                effectiveDate: new Date().toISOString()
            }, existingId ? {
                resourceType: "Observation",
                id: existingId
            } : undefined);
            observations.push(attachEncounterRef(docObs));
        }
    }
    // Build flags from form data
    const flags = [];
    if (!isDraft && Array.isArray(formData["followup-flags"]) && formData["followup-flags"].length > 0) {
        const flagCodes = formData["followup-flags"];
        for (const code of flagCodes){
            // Use centralized category resolution from constants
            const category = getFlagCategoryByCode(code);
            if (!category) {
                // Log warning if flag code has no category mapping
                onWarning?.(`Flag code "${code}" has no category mapping and will be skipped`);
                continue;
            }
            // Get display values for category and code
            const categoryDisplay = getFlagCategoryDisplay(category);
            const codingInfo = getFlagCodingByCode(code);
            // Check if this flag already exists for the patient
            const targetToken = codingInfo.system ? `${codingInfo.system}|${code}` : code;
            const hasDuplicate = existingActiveFlags.some((f)=>{
                const codings = f.code?.coding || [];
                return codings.some((c)=>{
                    const token = c.system ? `${c.system}|${c.code}` : c.code;
                    return token === targetToken;
                });
            });
            if (hasDuplicate) {
                continue;
            }
            const flag = {
                resourceType: "Flag",
                status: "active",
                category: [
                    {
                        coding: [
                            {
                                system: "http://terminology.hl7.org/CodeSystem/flag-category",
                                code: category,
                                display: categoryDisplay
                            }
                        ],
                        text: categoryDisplay
                    }
                ],
                code: {
                    coding: [
                        {
                            system: codingInfo.system,
                            code,
                            display: codingInfo.display
                        }
                    ],
                    text: codingInfo.display
                },
                subject: buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient)),
                author: buildPractitionerRoleReference(practitionerRole.id, getPractitionerDisplayName(practitioner))
            };
            flags.push(flag);
        }
    }
    // Build tasks from form data
    const tasks = [];
    // Build optional follow-up task if specified
    if (!isDraft && formData["followup-task-type"]) {
        const taskConfig = getTaskTypeConfig(formData["followup-task-type"]);
        const task = {
            resourceType: "Task",
            status: "ready",
            intent: "plan",
            priority: taskConfig?.defaultPriority || "routine",
            code: {
                coding: [
                    {
                        system: taskConfig?.system || "http://www.benchmarkhealth.com/fhir/ValueSet/task-type",
                        code: formData["followup-task-type"],
                        display: taskConfig?.display || formData["followup-task-type"]
                    }
                ]
            },
            description: formData["followup-description"] || taskConfig?.description || "",
            authoredOn: new Date().toISOString(),
            for: buildPatientReference(toPatientId(patient.id), getPatientDisplayName(patient)),
            requester: buildPractitionerRoleReference(practitionerRole.id, getPractitionerDisplayName(practitioner)),
            owner: formData["followup-assigned-to"] ? buildPractitionerRoleReference(formData["followup-assigned-to"], formData["followup-assigned-to-display"] || "") : undefined
        };
        if (formData["followup-due-date"]) {
            task.restriction = {
                period: {
                    end: formData["followup-due-date"]
                }
            };
        }
        tasks.push(task);
    }
    // Collect all resources into transaction entries
    const entries = [];
    // Add encounter (POST for new, PUT for update)
    entries.push({
        resource: encounter,
        method: encounter.id ? "PUT" : "POST",
        url: encounter.id ? `Encounter/${encounter.id}` : "Encounter",
        fullUrl: encounterTempFullUrl
    });
    // Add clinical impressions
    for (const build of clinicalImpressionBuilds){
        if (build.shouldDelete && build.clinicalImpression?.id) {
            // Delete operation
            entries.push({
                resource: build.clinicalImpression,
                method: "DELETE",
                url: `ClinicalImpression/${build.clinicalImpression.id}`
            });
        } else if (build.clinicalImpression) {
            // Create or update
            entries.push({
                resource: build.clinicalImpression,
                method: build.clinicalImpression.id ? "PUT" : "POST",
                url: build.clinicalImpression.id ? `ClinicalImpression/${build.clinicalImpression.id}` : "ClinicalImpression"
            });
        }
    }
    // Add questionnaire responses
    for (const response of questionnaireResponses){
        entries.push({
            resource: response,
            method: response.id ? "PUT" : "POST",
            url: response.id ? `QuestionnaireResponse/${response.id}` : "QuestionnaireResponse"
        });
    }
    // Add observations
    for (const observation of observations){
        entries.push({
            resource: observation,
            method: observation.id ? "PUT" : "POST",
            url: observation.id ? `Observation/${observation.id}` : "Observation"
        });
    }
    // Add flags
    for (const flag of flags){
        entries.push({
            resource: flag,
            method: flag.id ? "PUT" : "POST",
            url: flag.id ? `Flag/${flag.id}` : "Flag"
        });
    }
    // Add tasks
    for (const task of tasks){
        entries.push({
            resource: task,
            method: task.id ? "PUT" : "POST",
            url: task.id ? `Task/${task.id}` : "Task"
        });
    }
    // Add composition status update if scribe composition was used.
    // Also record the FHIR-native Composition.encounter back-reference so the note
    // is linked to the encounter it produced. Existing encounters reference by id;
    // newly created encounters reference the in-bundle temp full url, which the
    // transaction resolves to the created Encounter (mirrors child-resource refs).
    if (scribeComposition?.id) {
        const encounterReference = encounter.id ? `Encounter/${encounter.id}` : encounterTempFullUrl;
        const updatedComposition = {
            ...scribeComposition,
            status: "amended",
            ...encounterReference ? {
                encounter: {
                    reference: encounterReference
                }
            } : {}
        };
        entries.push({
            resource: updatedComposition,
            method: "PUT",
            url: `Composition/${scribeComposition.id}`
        });
    }
    // Convert entries to Bundle format
    return {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                fullUrl: entry.fullUrl,
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
}

export { QuestionnairePersistenceError, assertAdaptiveScreenerFinalization, buildAvailabilityUpdateBundle, buildBatchUpdateHealthcareServicesBundle, buildCancelledTaskEntries, buildCarePlan, buildCarePlanActivityTask, buildCarePlanBundle, buildCareTimeFromCommunication, buildCareTimeObservation, buildClinicalImpression, buildCoCMBillingPacketBundle, buildCoCMBillingSummary, buildCoCMCancellationBundle, buildCoCMCompletionBundle, buildCoCMEnrollmentBundle, buildCreatePatientBundle, buildCreatePractitionerBundle, buildGoalTransactionBundle, buildIntakeCancellationBundle, buildIntakeCarePlan, buildIntakeChargeItem, buildIntakeCompletionBundle, buildLibraryResource, buildNeedServiceRequest, buildNotificationTask, buildObservationFromScore, buildPausePatientBundle, buildProblemListCondition, buildProblemListConditions, buildProgramSelectionObservation, buildReference, buildResumedCarePlan, buildTriageCancellationBundle, buildTriageCompletionBundle, buildUnpausePatientBundle, buildUpdatePatientBundle, buildUpsertCommunicationBundle, buildUpsertEncounterBundle, buildUpsertHealthcareServiceBundle, createCodeableConcept$1 as createCodeableConcept, getQuarantinedAssessmentStateFingerprint, hasAssessmentStatePayload, updateActivitiesForCancellation, updateCarePlanActivityStatus };
